/* planSolver — přiřazování zdrojů k cílům, glide path (Morningstar brzda),
 * budoucí přítoky, hypoteční varianty a cashflow timeline.
 *
 * Metodika: diktát Pana Čunga (docs/plan-engine-spec-2026-07.md) + kniha
 * Osobní finance (Syrový/Tyl). Vše v REÁLNÝCH hodnotách (výnosy nad inflaci).
 *
 * Re-exportováno z planEngine.js — importuj klidně z "@/lib/planEngine".
 * Soubor je záměrně bez závislostí (testovatelný přes node --input-type=module);
 * jediný import je `bezpecneHodnoty.js`, který je sám bez závislostí, takže se
 * modul dál načte i holým node.
 */
import { asArr, asObj, asText, sberProblemu } from "./bezpecneHodnoty";
/* STROP HORIZONTU CÍLE — jedna hodnota pro výpočet i pro zobrazení.
 * Do 25. 8. 2026 měl solver 1000 let a engine 80: karta v Návrhu psala
 * „za 1000 let", zatímco tištěný plán u téhož cíle „za 80 let". */
import { ROKY_STROP } from "./analyzaV2/horizontCile";
/* Vzorec cíle rezervy je JEDEN pro celou appku — analýza ho ukazuje jako
 * doporučení, solver z něj dělá dopočítaný cíl „Povinná likvidní rezerva".
 * Do 24. 9. 2026 ho měl solver opsaný (`zaklad * 6`), takže se dal opravit
 * na jednom místě a na druhém ne. Modul je sám bez závislostí, takže se
 * planSolver dál načte i holým node. */
import { rezervaCastka, REZERVA_MESICU_VYCHOZI } from "./analyzaV2/doporuceniCile";
/* Přebytek bez odsouhlasení — jedno pravidlo s planEngine (Č9, 4. 10. 2026). */
import { prebytekZModelu } from "./prebytekPlanu";
/* Razítko metodiky plánu (pravidlo majitele 3. 10. 2026: staré uložené plány
 * se nepřepočítávají). Modul je bez závislostí. */
import { platiZmena, METODIKA_C20, METODIKA_2026_10_05, METODIKA_2026_10_18, METODIKA_2026_10_28 } from "./metodikaPlanu";
import { alokaceZTabulkyPosun } from "./alokaceTabulky";
import { alokaceRenty, rentaPoLetech } from "./rentaPoLetech";
import { cistyPosun } from "./metodikaVolby";
/* Akontace podle věku V ROCE KOUPĚ a podle ZEMĚ (METODIKA 2026-10-05, body 7
 * a 8 balíku 4. 10. 2026): limit LTV má JEDNO místo v appce — lišta kalkulaček,
 * plán, kino cíle i Cesta života čtou `ltvLimit`. Země tenanta bez volby
 * z globálního storu (týž, ze kterého appka bere měnu). Moduly bez Reactu. */
import { ltvLimit, LTV_PRAVIDLA } from "./nastroje/uverLimity";
import { normZeme, zemeTenanta } from "./tenantMena";
import { typNefin } from "./analyzaV2/propsaniMajetku";
import { NEMOVITOST_BYDLENI, NEMOVITOST_PRONAJEM } from "./analyzaV2/nefinancni";
/* Tab závazku (hypotéky / osobní / firemní) — u SK první bydlení (hypotéka
 * v závazcích = klient nemovitost má). Čistý modul bez importů. */
import { tabZavazku, HYPOTEKY } from "./analyzaV2/zavazkyTaby";
/* Bydlení a rekonstrukce = hypotéka; ostatní cíle na úvěr = spotřebitelský úvěr
 * nebo úvěr ze stavebka (metodika 2026-10-05). Čistý modul bez importů. */
import { jeHypotecniTypu } from "./analyzaV2/cileTyp";
/* Krok 3: uložená volba dofinancování, která nad daty analýzy neplatí (auto
 * jako zástava, smazaná položka), se nepočítá (oprava 5. 10. 2026). */
import { platnostVolbyDofinancovani, vlastniciMajetku } from "./dofinancovaniMajetek";

/* ===== Konfigurovatelné konstanty ===== */

/* REÁLNÉ výnosy p.a. dle tříd aktiv — tedy UŽ NAD INFLACÍ.
 *
 * METODIKA MAJITELE (potvrzeno 21. 8. 2026, jeho slovy):
 *   „akcie 7,7 % · dluhopisy 2,3 % · peněžní trh 0,8 % — vše nad inflaci,
 *    inflace 2,5 %."
 *
 * ⚠️ VÝNOSY JSOU REÁLNÉ, INFLACE SE UŽ NIKDE NEPŘIČÍTÁ. Ověřeno grepem:
 * v `planSolver`, `planEngine` ani `analyzaV2/*` není žádný další inflační
 * člen. Částky jsou tedy v DNEŠNÍCH CENÁCH. Kdyby někdo příště přidal
 * inflaci ještě jednou, plán by klientovi tiše ubral ~2,5 % ročně.
 * Inflace 2,5 % je tu jen jako doložený předpoklad, ze kterého ta reálná
 * čísla vycházejí — do výpočtu NEVSTUPUJE.
 *
 * Varianty drží dosavadní rozestup: optimisticky +1,5 p.b. akcie,
 * pesimisticky −2 p.b. akcie. Dluhopisy a peněžní trh se nevarírují —
 * jejich rozptyl je řádově menší a majitel je zadal jako jedno číslo.
 *
 * ⚠️ Realistická sazba akcií šla 6,17 % → 7,7 %. Plány tím vycházejí
 * PŘÍZNIVĚJI: doporučená měsíční investice klesne. Je to metodické
 * rozhodnutí majitele, ne odhad — proto je tu jeho citace. */
export const INFLACE_PREDPOKLAD = 0.025;   // jen dokumentace, do výpočtu nevstupuje

export const VYNOSY = {
  realisticky: { akcie: 0.077, dluhopisy: 0.023, cash: 0.008 },
  optimisticky: { akcie: 0.092, dluhopisy: 0.023, cash: 0.008 },
  pesimisticky: { akcie: 0.057, dluhopisy: 0.023, cash: 0.008 },
};

/* ===== DVĚ SAZBY U JEDNOHO CÍLE — ČÍM SE LIŠÍ ==============================
 * `SAZBA_HYPO` je úrok HYPOTÉKY. Platí pro hlavní úvěr ve všech variantách
 * akontace, tedy i pro těch 90 % ceny ve variantě „100% financování".
 * `SAZBA_STAVEBKO_UVER` je úrok ÚPLNĚ JINÉHO produktu — úvěru ze stavebního
 * spoření, kterým se ve variantě 100 % dokrývá zbývajících 10 % ceny. Je vyšší
 * (a kratší, 20 let), protože je nezajištěný.
 *
 * ⚠️ PORADCE EDITUJE JEN TU PRVNÍ (`cil.sazbaHypoPct`, viz `sazbaHypoCile`).
 * Není to opomenutí: „sazba u cíle" je nabídka banky na hypotéku, kterou má
 * poradce na stole. Sazba stavebka je metodická konstanta domu a na kartě
 * varianty 100 % se vypisuje ZVLÁŠŤ a z těchhle konstant, aby bylo vidět, že se
 * upravenou sazbou hypotéky nehnula. Kdyby se obě schovaly za jedno pole,
 * poradce by přepsal „5,3 %" a appka by mu tiše přepočítala i stavebko na
 * sazbu, kterou žádná stavební spořitelna nedá. */
export const SAZBA_HYPO = 0.049;            // p.a., anuitní splátka hypotéky
export const SPLATNOST_HYPO_VYCHOZI = 30;   // let — výchozí splatnost hypotéky v plánu
export const SPLATNOST_HYPO_MAX = 40;       // let — víc už banka nedá (a dřív než v 70 musí být splaceno)
export const SAZBA_HYPO_MAX_PCT = 25;       // % p.a. — strop proti překlepu (2,9 → 29)
export const SAZBA_STAVEBKO_UVER = 0.055;   // p.a., úvěr ze stavebního spoření (100% varianta)
export const SPLATNOST_STAVEBKO_UVER = 20;  // let
/* ÚVĚR NA CÍL MIMO BYDLENÍ (auto, podnikání…) — majitel 5. 10. 2026: „u auta se
 * řeší úvěrem spotřebitelským nebo ze stavebka … spotřebitelský je do 12 let
 * max … tam není potřeba ČNB, je to spotřebitelský úvěr“. Žádná akontace podle
 * ČNB, žádné parametry hypotéky — jen kolik klient zaplatí hotově a splátka.
 * ⚠️ SAZBA JE ORIENTAČNÍ, ne nabídka banky (ověřit u banky): ČNB, měnová
 * statistika — nové obchody spotřebitelských úvěrů domácnostem kolem 8,1–8,2 %
 * p.a. (VI–VII/2025, cbamonitor.cz „Bankovní statistika“). Úvěr ze stavebního
 * spoření má vlastní sazbu `SAZBA_STAVEBKO_UVER` výš. Splatnost 7 let je jen
 * výchozí hodnota (poradce ji přepíše), strop 12 let je majitelův. */
export const SAZBA_SPOTREBITELSKY_UVER = 0.08; // p.a., orientačně
export const SPLATNOST_SPOTREBITELSKY_VYCHOZI = 7; // let
export const SPLATNOST_SPOTREBITELSKY_MAX = 12;    // let — majitel 5. 10. 2026
/* Kolik klient u úvěru mimo bydlení zaplatí hotově (procenta ceny). */
export const HOTOVE_UVER_PCT = Object.freeze([0, 10, 20, 30]);
export const SAZBA_RENTA = 0.04;            // bezpečné čerpání renty (Čung 4 %)
export const STAVEBKO_DOSTUPNE_MES = 4;     // zrušení stavebka trvá ~4 měsíce
/* Věk, od kterého jde DPS (III. pilíř) vybrat BEZ sankce — a proto v něm plán
 * penzijko připisuje do renty jako jednorázový vklad.
 * Dřív vybrat LZE: odbytné se vyřídí řádově v týdnech. Jenže klient tím vrací
 * všechny státní příspěvky a dodaňuje dřív uplatněnou daňovou úlevu, takže to
 * plán sám od sebe NEPŘEDPOKLÁDÁ. Dokud si klient zrušení sám neřekne, počítá
 * se s penězi až v tomhle věku — viz `rozdelZdroje` a vklad do renty níž. */
export const VEK_PENZIJKO = 60;
/* SLOVENSKO (nález AP-05, 19. 10. 2026, majitel: „u SK 62 let místo 60“):
 * nárok na doplnkový starobný dôchodok z DDS (III. pilier) vzniká dovŕšením
 * 62 rokov (zák. 650/2004 Z. z.). Bez země = země tenanta (stránka plánu ji
 * přepíná podle měny dat, lib/menaDatStranky.js). */
export const VEK_PENZIJKO_SK = 62;
export function vekPenzijka(zeme) {
  return normZeme(zeme ?? zemeTenanta()) === "SK" ? VEK_PENZIJKO_SK : VEK_PENZIJKO;
}

/* ===== DLOUHÉ ZDROJE: STAVEBKO A PENZIJKO MÍŘÍ NA DLOUHÝ CÍL ==============
 *
 * DIKTÁT MAJITELE (24. 9. 2026), jeho slovy — odpověď na otázku, jestli
 * stavebko a penzijko patří do majetku, se kterým smí plán pracovat:
 *   „ano, může se s tím pracovat, penze nebo DIP lze zrušit .. ale většinou
 *    se to investuje do renty na dlouhý cíl."
 *
 * Co z toho pro solver plyne:
 *  1. NEJSOU to „nezapojený" majetek — plán s nimi pracuje (to už platilo:
 *     penzijko jde do renty, stavebko do vodopádu od 4. měsíce),
 *  2. ve VÝCHOZÍM stavu míří na DLOUHÝ CÍL (rentu), ne na rezervu ani na auto,
 *  3. zrušit je JDE, ale je to ZTRÁTA a ta musí být vidět (viz `ZTRATA_*`).
 *
 * ⚠️ PROČ JE TO VOLBA (`zdrojeDoRenty`) A NE ROVNOU NOVÉ VÝCHOZÍ CHOVÁNÍ.
 * Spočítaný plán se NIKDE NEUKLÁDÁ — každá obrazovka i tisk ho počítají znovu
 * z analýzy (viz `plneniPlanu` → `solverOptionsZNavrhu`). Kdyby se vodopád
 * přehodil natvrdo, KAŽDÝ už uzavřený plán by při dalším otevření ukázal jiná
 * čísla, než jaká poradce s klientem probral — a to bez jediného jeho kliknutí.
 * Volba se proto ukládá do `navrh.solver.zdrojeDoRenty`; analýza bez ní jede
 * přesně jako dosud (regresní zámek: `lib/__tests__/zdrojeDoRenty.test.js`).
 */

/** O co klient přijde, když penzijní produkt zruší dřív. Kódy, ne věty —
 *  planSolver je záměrně bez textů, hlášku skládá UI z překladů. */
export const ZTRATA_STATNI_PRISPEVEK = "statniPrispevek";   // DPS / staré připojištění
export const ZTRATA_DANOVA_VYHODA = "danovaVyhoda";         // DIP

/* Druhy penzijních smluv, kterým náleží STÁTNÍ PŘÍSPĚVEK (klíče z
 * `analyzaV2/produktyPole.DRUHY_PENZIJKA`; DIP státní příspěvek nemá, má
 * daňovou výhodu). Prázdný druh = poradce ho nevybral, ne „žádný z nich". */
const DRUHY_PENZIJKA_STATNI = new Set(["dps", "pripojisteni"]);

// Startovní alokace dle rizikového profilu (akcie/dluhopisy/cash v %).
export const PROFILY = {
  dynamicky: { akcie: 90, dluhopisy: 10, cash: 0 },
  vyvazeny: { akcie: 70, dluhopisy: 25, cash: 5 },
  konzervativni: { akcie: 40, dluhopisy: 50, cash: 10 },
};
// Rentiérské portfolio à la Nobelova nadace — drží se i v čerpání.
export const RENTA_ALOKACE = { akcie: 50, dluhopisy: 35, cash: 15 };

/* ===== Pomocné ===== */

/* `asArr` místo `(arr || [])`: pojistka `|| []` chrání jen proti null/undefined.
 * Když z API doletí objekt, `{length: 3}` nebo pole s dírami, `.reduce` na tom
 * neexistuje (nebo padne uvnitř na `null.hodnota`) → výjimka → bílá obrazovka
 * v prezentaci u klienta. `asArr` propustí jen skutečné položky-objekty. */
const sum = (arr, f = "hodnota") => asArr(arr).reduce((a, r) => a + (+r[f] || 0), 0);

/* ===== DRUH ÚČTU — podle TYPU A ID, NIKDY podle názvu (audit R5) ===========
 *
 * Do 25. 8. 2026 se účty třídily regexem na uživatelský popisek:
 *     sumKde(ucty, /spo[řr]ic/i, "u2")   // NEZÁVISLÉ filtry, ne else-if
 * Poradce ale smí každý řádek přejmenovat (appka ho k tomu vyzývá polem
 * „Vaše označení") a tím se peníze buď ZDVOJILY, nebo ZMIZELY:
 *   · řádek s id `u2` přejmenovaný na „Běžný účet u ČS" (500 000 Kč) prošel
 *     OBĚMA filtry (id u2 → spořicí, název → běžný) → pool 1 000 000 Kč.
 *     Plán rozdělil půl milionu, který klient nemá.
 *   · řádek „Fio konto" neprošel žádným → likvidní 0 Kč, zatímco planEngine
 *     sčítal všechny účty (500 000). Na jedné A4 pak stálo „rezerva naplněna
 *     na 0 % (500 000 Kč)" a vedle „chybí 0 Kč".
 *
 * TEĎ PLATÍ DVĚ RŮZNÁ PRAVIDLA — a v tom rozdílu je celá oprava:
 *
 * 1) JE TO LIKVIDITA (peníze, které plán rozděluje)? Rozhoduje JEN VÝSLOVNÝ
 *    ÚDAJ: select „Typ účtu" (`typUctu`, hodnoty z
 *    `analyzaV2/produktyPole.TYPY_UCTU`), jinak id řádku z výchozí předlohy
 *    (`analyzaV2/model.EMPTY.majetek.ucty` = u1…u5), jinak zaškrtnutí
 *    „Okamžitě dostupné". NÁZEV ŘÁDKU TOHLE ROZHODNUTÍ NEOVLIVNÍ NIKDY.
 *    NEZNÁMÝ ŘÁDEK JE LIKVIDNÍ — „Fio konto" je pořád bankovní účet ze sekce
 *    „4. Bankovní účty a spoření" a peníze z něj nesmí vypadnout z plánu jen
 *    proto, že se nedá zařadit. Mimo likvidní pool jde jen to, co je VÝSLOVNĚ
 *    termínovaný vklad nebo cizí měna.
 *
 * 2) DO KTERÉHO ŠUPLÍKU (spořicí / běžný / hotovost)? To je jen POJMENOVÁNÍ
 *    zdroje v klientském výstupu („z běžného účtu 300 000 Kč"). Tady se název
 *    jako POSLEDNÍ nápověda použít smí — u řádku, který nemá ani typ, ani id
 *    z předlohy. Přesunout peníze DOVNITŘ ani VEN z likvidního poolu ale
 *    nedokáže, takže se jimi nedá ani zdvojit, ani ztratit.
 *
 * ZAŠKRTNUTÍ „Okamžitě dostupné" (`likvidni: true`) klasifikaci PŘEBIJE —
 * poradce, který u termínovaného vkladu potvrdí, že peníze jsou k dispozici
 * hned, má poslední slovo. Umí jen PŘIDAT do likvidity, nikdy ubrat (nezaškrtnuté
 * pole je výchozí stav, ne odpověď „ne").
 */
/* Hodnoty selectu `TYPY_UCTU` (analyzaV2/produktyPole.js) + slovenské tvary,
 * kdyby se do dat dostaly z SK obrazovky. Klíč je vždy lowercase + trim. */
const TYP_NA_DRUH = {
  "běžný účet": "bezny",
  "bežný účet": "bezny",
  "spořicí účet": "sporici",
  "sporiaci účet": "sporici",
  "termínovaný vklad": "termin",
  "terminovany vklad": "termin",
  hotovost: "hotovost",
  "cizí měna": "cizi",
  "cudzia mena": "cizi",
};
const ID_NA_DRUH = { u1: "termin", u2: "sporici", u3: "bezny", u4: "hotovost", u5: "cizi" };
/** Druhy, které NEJSOU okamžitě dostupná likvidita (termíňák, cizí měny). */
const DRUHY_NELIKVIDNI = new Set(["termin", "cizi"]);
/* Nápověda z názvu — POUZE pro pojmenování šuplíku uvnitř likvidního poolu
 * (pravidlo 2 výš). Je to `else if` řetěz, ne nezávislé filtry: první shoda
 * vyhrává a řádek se započte právě jednou. */
const NAZEV_NAPOVEDA = [
  [/spo[řr]ic|sporiac/i, "sporici"],
  [/hotovost/i, "hotovost"],
  [/b[ěe][žz]n/i, "bezny"],
];

/**
 * VÝSLOVNÝ druh řádku účtu: `sporici | bezny | hotovost | termin | cizi | null`.
 * `null` = poradce typ nevybral a řádek není z výchozí předlohy (vlastní řádek).
 * NÁZEV ŘÁDKU SE TADY NEČTE — viz pravidlo 1 v komentáři výš.
 */
export function druhUctu(row) {
  const r = asObj(row);
  const typ = asText(r.typUctu).trim().toLowerCase();
  if (typ && TYP_NA_DRUH[typ]) return TYP_NA_DRUH[typ];
  const id = asText(r.id).trim();
  if (ID_NA_DRUH[id]) return ID_NA_DRUH[id];
  return null;
}

/**
 * Je řádek účtu okamžitě dostupná likvidita?
 * Neznámý druh ANO (pořád je to bankovní účet), termíňák/cizí měna NE —
 * dokud u nich poradce nezaškrtne „Okamžitě dostupné".
 */
export function jeUcetLikvidni(row) {
  if (asObj(row).likvidni === true) return true;
  const druh = druhUctu(row);
  return druh === null || !DRUHY_NELIKVIDNI.has(druh);
}

/** Šuplík uvnitř likvidního poolu — jen pojmenování zdroje, viz pravidlo 2. */
function suplikLikvidniho(row) {
  const druh = druhUctu(row);
  if (druh === "sporici" || druh === "hotovost" || druh === "bezny") return druh;
  const nazev = asText(asObj(row).oznaceni);
  for (const [rx, kam] of NAZEV_NAPOVEDA) if (rx.test(nazev)) return kam;
  return "bezny";
}

/**
 * Rozdělení účtů do kbelíků. KAŽDÝ ŘÁDEK PADNE PŘESNĚ DO JEDNOHO —
 * `sporici + bezny + hotovost + ostatni === součet všech účtů` (hlídá test).
 */
export function kbelikyUctu(ucty) {
  const out = { sporici: 0, bezny: 0, hotovost: 0, ostatni: 0 };
  for (const r of asArr(ucty)) {
    const v = +r.hodnota || 0;
    if (!jeUcetLikvidni(r)) { out.ostatni += v; continue; }
    out[suplikLikvidniho(r)] += v;
  }
  return out;
}

/**
 * Penzijní majetek rozpadlý podle DRUHU smlouvy — jen kvůli tomu, aby se dalo
 * říct, O CO klient přijde, když produkt zruší dřív. Bez druhu by appka musela
 * tvrdit obojí, nebo nic.
 * Platí `statni + dip + neurceno === sum(penzijko)` (hlídá test) — žádná koruna
 * se cestou neztratí.
 */
export function penzijkoPodleDruhu(rows) {
  const out = { statni: 0, dip: 0, neurceno: 0 };
  for (const r of asArr(rows)) {
    const v = +r.hodnota || 0;
    const d = asText(r.druh).trim().toLowerCase();
    if (DRUHY_PENZIJKA_STATNI.has(d)) out.statni += v;
    else if (d === "dip") out.dip += v;
    else out.neurceno += v;   // druh nevybraný → nevíme, nevymýšlíme si
  }
  return out;
}

/**
 * O co klient přijde předčasným zrušením penzijního produktu.
 *
 * ⚠️ ŽÁDNÁ SAZBA ANI ČÁSTKA. Engine neví, kolik státních příspěvků už klient
 * dostal ani jakou úlevu uplatnil — vrací se jen KÓD ztráty. Kdo dopíše číslo,
 * vymyslel si ho.
 *
 * @param {object} druhy výstup `penzijkoPodleDruhu`
 * @returns {{kody: string[], druhNeznamy: boolean}|null} `null` = není o čem
 *   mluvit (klient žádné penzijko nemá)
 */
export function ztrataZeZruseniPenzijka(druhy) {
  const d = asObj(druhy);
  const statni = +d.statni || 0;
  const dip = +d.dip || 0;
  const neurceno = +d.neurceno || 0;
  const kody = [];
  if (statni > 0) kody.push(ZTRATA_STATNI_PRISPEVEK);
  if (dip > 0) kody.push(ZTRATA_DANOVA_VYHODA);
  /* Druh nevybraný: nevíme, jestli je to DPS nebo DIP, takže se řekne obojí.
   * Zamlčet jednu možnost by znamenalo tvrdit o smlouvě něco, co nikdo nezadal. */
  if (neurceno > 0 && kody.length === 0) kody.push(ZTRATA_STATNI_PRISPEVEK, ZTRATA_DANOVA_VYHODA);
  if (!kody.length) return null;
  return { kody, druhNeznamy: neurceno > 0 };
}

/** Anuitní měsíční splátka úvěru. */
export function anuitniSplatka(jistina, sazbaPa, roky) {
  const n = Math.max(1, Math.round(roky * 12));
  const r = sazbaPa / 12;
  if (r <= 0) return jistina / n;
  return (jistina * r) / (1 - Math.pow(1 + r, -n));
}

/** Věk 1. klienta z analýzy (z data narození), jinak null. */
export function vekKlienta(analyza) {
  const nar = analyza?.klienti?.[0]?.narozeni;
  if (!nar) return null;
  const d = new Date(nar);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let v = now.getFullYear() - d.getFullYear();
  if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) v--;
  return v;
}

/* ===== 1. Zdroje & likvidita ===== */

/** Rozdělení zdrojů z AnalyzaV2 podle likvidity (co smí jít jednorázově, co počká). */
export function rozdelZdroje(analyza) {
  const d = asObj(analyza);
  const m = asObj(d.majetek);
  const ucty = asArr(m.ucty);
  /* Třídění podle TYPU a ID, ne podle názvu — viz `kbelikyUctu` výš (audit R5).
   * Každý řádek padne přesně do jednoho kbelíku, takže se peníze nemůžou ani
   * zdvojit, ani ztratit: sporici + bezny + hotovost + ostatniUcty = suma účtů. */
  const kb = kbelikyUctu(ucty);
  const sporici = kb.sporici;
  const bezny = kb.bezny;
  const hotovost = kb.hotovost;
  const ostatniUcty = kb.ostatni; // výslovně termínovaný vklad / cizí měna
  /* TERMÍNOVANÉ VKLADY zvlášť (Č20, majitel 4. 10. 2026). Pořád jsou součástí
   * `ostatniUcty` (okamžitě dostupné nejsou), ale cíl „hned“ a rezerva si z nich
   * smí vzít, když běžný, spořicí a hotovost nestačí — dřív, než sáhnou na
   * stavební spoření. Cizí měny zůstávají mimo. */
  const terminovane = ucty.reduce((a, r) => a + (!jeUcetLikvidni(r) && druhUctu(r) === "termin" ? (+r.hodnota || 0) : 0), 0);

  const p2 = asObj(d.prijmy2);
  const prijmy =
    (+p2.prokaz1 || 0) + (+p2.prokaz2 || 0) + (+p2.ostatni1 || 0) + (+p2.ostatni2 || 0) +
    (+p2.pasivni || 0) + (+p2.bonusyRok || 0) / 12;
  /* VÝDAJE = max(obvyklé, součet rozpisu) — STEJNÉ PRAVIDLO JAKO ANALÝZA
   * (`soucetVydaju` v analyzaV2/stavy.js) A JAKO PLÁN (`planEngine.spocitejPlan`).
   * Do 8/2026 tu stálo jen `+d.vydaje2?.obvykle`, takže poradci, který výdaje
   * rozepsal položkově (analýza takový zápis uznává za úplný, přenos do „obvyklé"
   * je RUČNÍ tlačítko), vycházel z příjmu 45 000 − 0 − splátky přebytek 30 000 Kč
   * — a solver z něj klientovi vypočítal „renta splněna na 100 %". Analýza vedle
   * přitom počítala se 27 000 Kč výdajů a plán s přebytkem 3 000 Kč.
   * Vzorec se NEKOPÍRUJE náhodou — shodu hlídá `jednoCisloJedenPlan.test.js`. */
  const soucetRozpisu = Object.values(asObj(d.vydaje2?.detail)).reduce((a, x) => a + (Number.isFinite(+x) ? Math.max(0, +x) : 0), 0);
  const vydaje = Math.max(Math.max(0, +d.vydaje2?.obvykle || 0), soucetRozpisu);
  const splatky = sum(d.zavazkyB, "splatka") + sum(d.zavazkyO, "splatka");
  /* PŘEBYTEK SE BEZ VÝDAJŮ NETVRDÍ — dvě pasti v jednom řádku (obojí opravené
   * v planEngine 8/2026, tady zůstaly):
   *  1) `+d.prebytek || …` bralo ODSOUHLASENOU NULU jako „nevyplněno" a tiše ji
   *     přepsalo dopočtem. Poradce s klientem uzavřel, že volné peníze nejsou,
   *     a solver stejně rozdělil dopočtených 30 000 Kč měsíčně.
   *  2) Bez výdajů vycházel přebytek = celý příjem mínus splátky. Kapacita, kterou
   *     nemá co doložit, se pak v projekci proměnila na splněný cíl renty.
   * `prebytekNeznamy` = kapacita je 0 jen proto, že chybí podklad; obrazovka to
   * musí říct slovy, ne to ukázat jako údaj. */
  const prebytekZadany = zadanaCastka(d.prebytek);
  /* BEZ PŘÍJMŮ SE PŘEBYTEK NETVRDÍ TAKY (29. 9. 2026). Do té doby hlídal dopočet
   * jen výdaje, takže klient bez zadaných příjmů měl v plánu přebytek „0" —
   * zatímco analýza (`volneCashflow`) poctivě „nezadáno". Nezadané není nula. */
  /* ŘEŠENÍ I BEZ ODSOUHLASENÉHO PŘEBYTKU (Č9, majitel 4. 10. 2026): bez výdajů
   * se dřív nerozdělovalo nic (`prebytekNeznamy`) a plán se zastavil. Teď
   * rozhoduje lib/prebytekPlanu.js — vyplněné číslo (i nula) → volné CF z dat →
   * 20 % čistého příjmu → bez příjmu neznámé. Modelovou částku nese
   * `prebytekZdroj` („dopocet“ / „pravidlo“), obrazovky ji označí. */
  const model = prebytekZModelu({ zadany: prebytekZadany, prijmy, vydaje, splatky });
  const prebytekNeznamy = model.castka === null;
  const prebytekMes = prebytekZadany !== null
    ? prebytekZadany
    : (prebytekNeznamy ? 0 : Math.round(model.castka));
  /* PŘEBYTEK SE ZNAMÉNKEM (kontrola 2. 10. 2026, „fantomové peníze“). `prebytekMes`
   * je ořezaný na nulu — domácnost ve schodku (příjmy 40 000, výdaje 41 000,
   * splátky 2 000 → −3 000) v něm má 0. Když se pak k té nule přičetla uvolněná
   * platba rušené dražší smlouvy (záporný rozdíl pojistného −2 000), dostala
   * rodina v minusu 2 000 Kč/měs na cíle. Správně −3 000 + 2 000 = −1 000 →
   * na cíle 0. Uvolněné peníze proto NEJDŘÍV kryjí schodek (viz
   * `rozdilPojistnehoEfektivni`). Jen pro výpočet uvolnění, nic jiného ho nečte. */
  const prebytekSeZnamenkemMes = prebytekZadany !== null
    ? prebytekZadany
    : (prebytekNeznamy ? 0 : Math.round(model.seZnamenkem));

  return {
    likvidni: { sporici, bezny, hotovost, celkem: sporici + bezny + hotovost },
    ostatniUcty,
    /* Součet VŠECH účtů (likvidní + termíňák + cizí měny). Jde ven proto, aby
     * planEngine nemusel účty sčítat znovu po svém — dvojí součet byl kořen
     * rozporu „naplněno 0 % (500 000 Kč)" vs. „chybí 0 Kč" (audit R5). */
    uctyCelkem: sporici + bezny + hotovost + ostatniUcty,
    /* Klíč jen s termínovaným vkladem — u klienta bez něj je výstup beze změny. */
    ...(terminovane > 0 ? { terminovane } : {}),
    stavebko: { castka: sum(m.stavebko), dostupneZaMes: STAVEBKO_DOSTUPNE_MES },
    /* Penzijko (DPS i DIP) je FINANČNÍ MAJETEK klienta jako každý jiný — patří
     * do poolu, který plán rozděluje, a rozpočet ho musí ukázat.
     * `naRentu: true` = celá částka jde do finanční nezávislosti (diktát majitele:
     * „vždycky se to zahrne a bere se to jako do renty"), ne mezi ostatní cíle.
     * POZOR na starý komentář „nelze zrušit do 60": zrušit LZE (odbytné, řádově
     * týdny), ale za cenu vrácení státních příspěvků a dodanění dřív uplatněné
     * daňové úlevy. Proto plán nepředpokládá, že klient DPS zruší, a připisuje
     * peníze až ve VEK_PENZIJKO. Změna časování = jiná projekce renty.
     * `druhy` je rozpad podle druhu smlouvy — jediný podklad pro větu, O CO
     * klient přijde, kdyby produkt zrušil dřív (viz `ztrataZeZruseniPenzijka`). */
    penzijko: { castka: sum(m.penzijko), naRentu: true, druhy: penzijkoPodleDruhu(m.penzijko) },
    investice: sum(m.opf) + sum(m.kzp),
    /* NEFINANČNÍ MAJETEK DNES — bez dědictví (29. 9. 2026). Analýza vede
     * dědictví jako BUDOUCÍ majetek, který „zatím se nepočítá"
     * (analyzaV2/budouciMajetek.js); plán ho do 29. 9. sčítal do dnešního
     * nefinančního majetku, takže u téhož klienta ukázal o chalupu po rodičích
     * víc než analýza. Jde ven ZVLÁŠŤ (`nefinancniBudouci`), ať ho obrazovka
     * může ukázat s nálepkou „v budoucnu" — ale do žádného součtu „dnes". */
    nefinancni: sum(m.nemovitosti) + sum(m.firma),
    nefinancniBudouci: sum(m.dedictvi),
    /* Výdaje, ze kterých přebytek vyšel. Jde ven proto, aby se dalo ověřit
     * (testem i okem), že solver počítá s týmiž výdaji jako analýza a plán. */
    vydajeMes: vydaje,
    prebytekMes,
    prebytekSeZnamenkemMes,
    prebytekNeznamy,
    /* Odkud přebytek je: „odsouhlaseny“ | „dopocet“ | „pravidlo“ | „chybiPrijmy“.
     * Rozpočet, pruh v Návrhu i snímky prezentace podle toho ukážou štítek
     * „zatím neodsouhlaseno klientem“ (components/plan/StitekPrebytku.js). */
    prebytekZdroj: model.zdroj,
  };
}

/* ===== 3. Glide path (Morningstar brzda) ===== */

/**
 * Roční alokace pro cíl: start dle profilu, postupný odprodej akcií,
 * poslední 2–3 roky přesun do cash, poslední rok 0/0/100.
 * Renta: lineární přechod na trvalé rentiérské portfolio 50/35/15 (drží se i v čerpání).
 * `tabulky: true` (metodika 2026-10-18, Fichtner): rok t dostane sloupec tabulky
 * pro zbývající roky H−t+1 — cíl lib/alokaceTabulky (s. 25), renta lib/rentaPoLetech
 * (s. 27). Bez volby dnešní heuristika beze změny (staré plány, kina analýzy).
 * `posun` (jen s `tabulky`, Nastavení → Metodika plánu „Dynamičtější“, 0–5):
 * sloupec o N let dál = víc akcií dřív (`alokaceZTabulkyPosun`); renta
 * v akumulaci totéž, renta v čerpání (sloupec 0) beze změny.
 * @returns pole [{rok, akcie, dluhopisy, cash}] (procenta, součet 100)
 */
export function alokaceProCil(horizontRoky, profil = "vyvazeny", { renta = false, tabulky = false, posun = 0 } = {}) {
  // strop 1000 let: reálný horizont je ≤ ~70; brání OOM/zacyklení při roky=Infinity
  // nebo obřím finite (for t=1..H by rostl neomezeně). NaN/0/záporné → 1 (beze změny).
  const H = Math.max(1, Math.min(1000, Math.round(+horizontRoky || 0)));
  if (tabulky) {
    /* Renta s čerpáním HNED (0 let do začátku) = sloupec 0 tabulky s. 27
     * (portfolio v čerpání), ne sloupec 1 — horizont se jinak ořízne na 1 rok. */
    if (renta && !(Math.round(+horizontRoky || 0) >= 1)) return [{ rok: 1, ...alokaceRenty(0, profil) }];
    const tab = [];
    const p = cistyPosun(posun);
    for (let t = 1; t <= H; t++) tab.push({ rok: t, ...(renta ? alokaceRenty(H - t + 1 + p, profil) : alokaceZTabulkyPosun(H - t + 1, profil, p)) });
    return tab;
  }
  const start = PROFILY[profil] || PROFILY.vyvazeny;
  const roky = [];
  for (let t = 1; t <= H; t++) {
    let akcie, cash;
    if (renta) {
      const frac = t / H;
      akcie = Math.round(start.akcie + (RENTA_ALOKACE.akcie - start.akcie) * frac);
      cash = Math.round(start.cash + (RENTA_ALOKACE.cash - start.cash) * frac);
    } else {
      const zbyva = H - t + 1; // let do cíle (včetně letošního)
      if (zbyva <= 1) {
        akcie = 0; cash = 100; // poslední rok = žádné tržní riziko
      } else {
        // akcie: lineárně ze startu k nule 2 roky před koncem; krátký horizont akcie stropuje
        const strop = Math.max(0, (zbyva - 2) * 10);
        akcie = Math.min(strop, Math.round((start.akcie * Math.max(0, zbyva - 2)) / Math.max(1, H - 2)));
        // cash: poslední 3 roky náběh z profilového základu na 100 %
        cash = zbyva >= 4 ? start.cash : Math.round(start.cash + (100 - start.cash) * ((4 - zbyva) / 3));
      }
    }
    akcie = Math.max(0, Math.min(100, akcie));
    cash = Math.max(0, Math.min(100 - akcie, cash));
    roky.push({ rok: t, akcie, dluhopisy: 100 - akcie - cash, cash });
  }
  return roky;
}

/** Vážený reálný výnos portfolia p.a. pro alokaci {akcie,dluhopisy,cash} v %. */
export function vynosPortfolia(alok, varianta = "realisticky") {
  const v = VYNOSY[varianta] || VYNOSY.realisticky;
  return ((alok.akcie || 0) * v.akcie + (alok.dluhopisy || 0) * v.dluhopisy + (alok.cash || 0) * v.cash) / 100;
}

/** Kolik let čerpání sčítá kapitál „po letech“ — prakticky nekonečná renta
 * (strop `rentaPoLetech`; další roky přidají u vyváženého profilu < 0,1 %). */
export const ROKY_CERPANI_PO_LETECH = 100;

/**
 * KAPITÁL RENTY „PO LETECH ČERPÁNÍ“ (Fichtner s. 26; Nastavení → Metodika plánu,
 * majitel 19. 10. 2026). Volba poradce místo pravidla 4 %: na rentu každého
 * roku čerpání n leží samostatné portfolio s alokací cíle za n let (s. 25)
 * a dnes na ně stačí renta / (1 + výnos)^n — výnos z našich sazeb `VYNOSY`.
 * Součet přes 0…100 let. Vyvážený 200 000 Kč/měs ≈ 40,7 mil. místo 60 mil.
 * (docs/metodika-alokace-fichtner.md §5).
 * @param {number} rentaRocne roční renta (Kč)
 * @param {string} profil
 * @param {string} varianta
 */
export function kapitalRentyPoLetech(rentaRocne, profil = "vyvazeny", varianta = "realisticky") {
  /* Nekonečná / rozbitá renta se nesmí potichu změnit na 0 — stejně jako
   * u pravidla 4 % ji vrátíme dál a ošetří ji kód pro nekonečný cíl (QA Q3). */
  if (+rentaRocne === Infinity) return Infinity;
  return rentaPoLetech({ rentaRocne, roky: ROKY_CERPANI_PO_LETECH, profil, vynos: (a) => vynosPortfolia(a, varianta) }).celkem;
}

/**
 * Projekce cíle po měsících s glide path.
 * @param {number} horizontRoky   délka projekce
 * @param {Array}  alokace        výstup alokaceProCil (po posledním roce drží poslední)
 * @param {string} varianta       realisticky | optimisticky | pesimisticky
 * @param {Array}  vklady         jednorázové [{castka, mesic}] (mesic 0 = hned)
 * @param {number} pravidelne     Kč/měs po celý horizont
 * @param {Array}  kroky          navýšení pravidelné investice [{odMes, castkaMes}]
 * @param {null|{spravaPa?: number, vstupniPct?: number}} naklady  POPLATKY (Návrh řešení
 *        15. 10. 2026, rozhodnutí 3 — čára „po poplatcích“): `spravaPa` = správa jako
 *        ZLOMEK ročně (0.01 = 1 %), odečte se od ročního výnosu portfolia; `vstupniPct` =
 *        vstupní poplatek v PROCENTECH (3 = 3 %), strhne se z každého vkladu. `vlozeno`
 *        zůstává hrubé (co klient zaplatil). Bez `naklady` je řada beze změny.
 * @returns {rada: [{rok, hodnota, vlozeno}], konec, vlozeno}
 */
export function projekceCile({ horizontRoky, alokace, varianta = "realisticky", vklady = [], pravidelne = 0, kroky = [], naklady = null }) {
  const months = Math.max(1, Math.min(1000, Math.round(+horizontRoky || 0))) * 12; // strop 1000 let → brání OOM při Infinity/obřím horizontu
  /* Bez poplatků jsou obě hodnoty neutrální (− 0 a × 1), takže výpočet dává bit po
   * bitu totéž co dřív — hlídá lib/navrh/__tests__/zaklad.test.js (3 profily × 3 varianty).
   * Správa je stropovaná na 50 % ročně: překlep „1“ místo „0.01“ by jinak dal NaN. */
  const sprava = naklady ? Math.min(0.5, Math.max(0, Number.isFinite(+naklady.spravaPa) ? +naklady.spravaPa : 0)) : 0;
  const zVkladu = naklady ? 1 - Math.min(100, Math.max(0, Number.isFinite(+naklady.vstupniPct) ? +naklady.vstupniPct : 0)) / 100 : 1;
  let value = 0, vlozeno = 0;
  const rada = [{ rok: 0, hodnota: 0, vlozeno: 0 }];
  for (let mIdx = 0; mIdx < months; mIdx++) {
    const alok = alokace[Math.min(Math.floor(mIdx / 12), alokace.length - 1)];
    const mr = Math.pow(1 + vynosPortfolia(alok, varianta) - sprava, 1 / 12) - 1;
    let vklad = pravidelne;
    for (const k of kroky) if (mIdx >= k.odMes) vklad += k.castkaMes;
    for (const v of vklady) if (v.mesic === mIdx) vklad += v.castka;
    value = (value + vklad * zVkladu) * (1 + mr);
    vlozeno += vklad;
    if ((mIdx + 1) % 12 === 0) rada.push({ rok: (mIdx + 1) / 12, hodnota: Math.round(value), vlozeno: Math.round(vlozeno) });
  }
  return { rada, konec: Math.round(value), vlozeno: Math.round(vlozeno) };
}

/**
 * Kolik jednorázově dnes, aby to při glide path dorostlo na cíl (bez pravidelné investice).
 * ⚠️ Uzavřený vzorec: násobí roční výnosy a IGNORUJE pravidelné vklady i existující
 * jednorázovky. Pro režim obojí je NEPOUŽITELNÁ — tam jde bisekce nad projekceCile
 * (viz dopocitejCil). Zůstává kvůli původnímu rozvrhu likvidních zdrojů v solveCile.
 */
export function potrebnaJednorazovka(cilova, alokace, varianta) {
  let faktor = 1;
  for (const a of alokace) faktor *= 1 + vynosPortfolia(a, varianta);
  return cilova / faktor;
}

/** Binární hledání pravidelné investice Kč/měs, aby projekce dosáhla cíle. */
export function potrebnaPravidelna({ cilova, horizontRoky, alokace, varianta, vklady, kroky = [] }) {
  const konec = (M) => projekceCile({ horizontRoky, alokace, varianta, vklady, pravidelne: M, kroky }).konec;
  if (konec(0) >= cilova) return 0;
  let lo = 0, hi = Math.max(500, cilova / (horizontRoky * 12)) * 2, guard = 0;
  while (konec(hi) < cilova && guard++ < 40) hi *= 2;
  for (let i = 0; i < 50; i++) {
    const mid = (lo + hi) / 2;
    if (konec(mid) < cilova) lo = mid; else hi = mid;
  }
  return Math.ceil(hi);
}

/* ===== 3b. Dopočet cíle — režimy řešení (jednorázově / měsíčně / obojí) =====
 *
 * ⚠️ METODICKÉ PRAVIDLO: dopočet běží VŽDY bisekcí nad projekceCile(), nikdy
 * uzavřeným vzorcem anuity. Projekce má glide path (alokaceProCil) — alokace se
 * každý rok mění, výnos NENÍ konstantní a uzavřený vzorec pro něj neexistuje.
 * Kdyby se platba počítala anuitou, graf ze stejné obrazovky by kreslil jiné číslo
 * než tabulka. Přesně tuhle chybu má konkurenční nástroj; neopakovat.
 */

/** Povolené režimy řešení cíle v plánu (pro UI přepínač). */
export const REZIMY_CILE = ["jednorazove", "mesicne", "oboji"];
const REZIM_SET = new Set(REZIMY_CILE);

const MAX_BISEKCE = 1e12;     // strop hledání; nad ním je cíl reálně nedosažitelný
const TOL_SPLNENO = 0.995;    // stejná tolerance splnění jako v solveCile
/* Label ručního/dopočteného vkladu. Záměrně holé podstatné jméno bez interního
 * žargonu („poradce"/„dopočet") — tenhle řádek se dostane až na plátno klientovi.
 * Kdo ho zadal, se pozná z příznaku `rucni` a z pole `dopocitano` na cíli.
 * Renderovací místa ho na SK překládají větvením podle `rucni` (viz PlanPrehled). */
const LABEL_DOPOCET = "jednorázový vklad";

/** Bezpečné číslo: NaN/Infinity/undefined → def. */
const cislo = (x, def = 0) => (Number.isFinite(+x) ? +x : def);

/** Nezáporná částka zadaná poradcem, nebo null když nezadal nic použitelného. */
function zadanaCastka(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = +v;
  if (!Number.isFinite(n)) return null;
  return Math.max(0, n);
}

/**
 * Horizont zadaný poradcem. `0` je PLATNÝ údaj („hned"), prázdno / text /
 * záporno není. Nemůže to dělat `zadanaCastka`: ta sráží záporná čísla na nulu
 * — u částky správně, u horizontu by z „−5 let" udělala „hned".
 */
function zadanyHorizont(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = +v;
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/**
 * Bisekce nad projekcí: najdi nejmenší X ≥ 0, pro které konec(X) ≥ cilova.
 * konec() musí být neklesající funkce X (platí pro jednorázovku i pravidelnou).
 * @returns {{hodnota:number, nedosazitelne:boolean}} hodnota je VŽDY konečné číslo ≥ 0
 */
export function bisekceNadProjekci(konec, cilova, hiStart) {
  if (!(cilova > 0)) return { hodnota: 0, nedosazitelne: false };
  if (cislo(konec(0), 0) >= cilova) return { hodnota: 0, nedosazitelne: false }; // splněno ze zdrojů
  let lo = 0;
  let hi = Math.min(MAX_BISEKCE, Math.max(1000, cislo(hiStart, 1000)));
  let guard = 0;
  while (hi < MAX_BISEKCE && guard++ < 40 && cislo(konec(hi), 0) < cilova) {
    hi = Math.min(MAX_BISEKCE, hi * 2);
  }
  if (cislo(konec(hi), 0) < cilova) return { hodnota: Math.ceil(hi), nedosazitelne: true };
  for (let i = 0; i < 50; i++) {
    const mid = (lo + hi) / 2;
    if (cislo(konec(mid), 0) < cilova) lo = mid; else hi = mid;
  }
  return { hodnota: Math.ceil(hi), nedosazitelne: false };
}

/**
 * Jednotný dopočet řešení jednoho cíle. Bisekce nad projekceCile → číslo, které
 * dostaneš, nakreslí stejná projekce (vrací se v poli `projekce`).
 *
 * @param {number} cilova        cílová ČÁSTKA (u renty už kapitál, ne měsíční renta)
 * @param {number} horizontRoky  horizont v letech (0/záporný/NaN → 1, Infinity → strop 1000)
 * @param {Array}  alokace       glide path z alokaceProCil(horizontRoky, profil);
 *                               když chybí, dopočítá se z horizontu (vyvážený profil)
 * @param {string} varianta      realisticky | optimisticky | pesimisticky
 * @param {Array}  vklady        jednorázovky z ENGINE zdrojů [{castka, mesic}] (spořák, penzijko, přítoky)
 * @param {Array}  kroky         stupňovité navýšení pravidelné [{odMes, castkaMes}]
 * @param {string} rezim         jednorazove | mesicne | oboji (neznámé → mesicne)
 * @param {object} zadano        { jednorazove?, pravidelne? } — co poradce PŘIBIL ručně
 *
 * Režimy:
 *  - jednorazove: pravidelná = 0, dopočítá se jednorázovka (nebo se vezme zadano.jednorazove)
 *  - mesicne:     jednorázovka navíc = 0 (jen `vklady`), dopočítá se pravidelná;
 *                 pokud poradce přesto zadá zadano.jednorazove, respektuje se
 *  - oboji:       přibitá je JEDNA z nich → dopočítá se ta druhá; obě přibité → jen vyhodnotí
 *
 * @returns {{rezim, cilova, horizontRoky, jednorazove, pravidelne, dosazeno, splneno,
 *            chybi, nedosazitelne, dopocitano, alokace, vklady, projekce}}
 *          Žádné pole není nikdy NaN ani Infinity. `nedosazitelne` = bisekce narazila
 *          na strop (cíl nelze dorovnat) — hodnoty jsou pak jen orientační strop.
 */
export function dopocitejCil({
  cilova,
  horizontRoky,
  alokace,
  varianta = "realisticky",
  vklady = [],
  kroky = [],
  rezim = "mesicne",
  zadano = {},
} = {}) {
  const cil = Math.max(0, cislo(cilova, 0));
  // stejný guard jako alokaceProCil/projekceCile → Infinity/obří horizont končí na 1000 letech
  const H = Math.max(1, Math.min(1000, Math.round(+horizontRoky || 0)));
  const alok = Array.isArray(alokace) && alokace.length > 0 ? alokace : alokaceProCil(H);

  const vkl = (Array.isArray(vklady) ? vklady : [])
    .map((v) => ({ ...v, castka: Math.max(0, cislo(v?.castka, 0)), mesic: Math.round(cislo(v?.mesic, -1)) }))
    .filter((v) => v.castka > 0 && v.mesic >= 0);
  const krk = (Array.isArray(kroky) ? kroky : [])
    .map((k) => ({ ...k, odMes: Math.round(cislo(k?.odMes, -1)), castkaMes: Math.max(0, cislo(k?.castkaMes, 0)) }))
    .filter((k) => k.odMes >= 0 && k.castkaMes > 0);

  const R = REZIM_SET.has(rezim) ? rezim : "mesicne";
  const zadJ = zadanaCastka(zadano?.jednorazove);
  const zadP = zadanaCastka(zadano?.pravidelne);

  const spocti = (J, M) =>
    projekceCile({
      horizontRoky: H,
      alokace: alok,
      varianta,
      vklady: J > 0 ? [...vkl, { zdroj: LABEL_DOPOCET, castka: J, mesic: 0 }] : vkl,
      pravidelne: M,
      kroky: krk,
    });
  const konecPri = (J, M) => cislo(spocti(J, M).konec, 0);

  let jednorazove = 0;
  let pravidelne = 0;
  let dopocitano = null;      // co engine dopočítal: "jednorazove" | "pravidelne" | null
  let nedosazitelne = false;

  if (cil <= 0) {
    // nulová/záporná cílovka → není co řešit; nikdy nevracíme záporné číslo
    jednorazove = zadJ ?? 0;
    pravidelne = R === "jednorazove" ? 0 : zadP ?? 0;
  } else if (R === "jednorazove") {
    pravidelne = 0;
    if (zadJ != null) {
      jednorazove = zadJ;
    } else {
      const b = bisekceNadProjekci((J) => konecPri(J, 0), cil, cil);
      jednorazove = b.hodnota;
      nedosazitelne = b.nedosazitelne;
      dopocitano = "jednorazove";
    }
  } else if (zadP != null && zadJ != null) {
    // obojí přibité → nic nedopočítáváme, jen poctivě vyhodnotíme
    jednorazove = zadJ;
    pravidelne = zadP;
  } else if (zadP != null) {
    // přibitá pravidelná → hledáme jednorázovku BISEKCÍ nad projekcí (ne uzavřeným vzorcem)
    pravidelne = zadP;
    const b = bisekceNadProjekci((J) => konecPri(J, pravidelne), cil, cil);
    jednorazove = b.hodnota;
    nedosazitelne = b.nedosazitelne;
    dopocitano = "jednorazove";
  } else {
    // mesicne (a obojí s přibitou jednorázovkou / bez zadání) → hledáme pravidelnou
    jednorazove = zadJ ?? 0;
    const b = bisekceNadProjekci(
      (M) => konecPri(jednorazove, M),
      cil,
      Math.max(500, cil / (H * 12)) * 2,
    );
    pravidelne = b.hodnota;
    nedosazitelne = b.nedosazitelne;
    dopocitano = "pravidelne";
  }

  // zaokrouhli PŘED projekcí, ať graf kreslí přesně to číslo, které ukazujeme
  jednorazove = Math.round(Math.max(0, cislo(jednorazove, 0)));
  pravidelne = Math.round(Math.max(0, cislo(pravidelne, 0)));

  const projekce = spocti(jednorazove, pravidelne);
  const dosazeno = Math.max(0, cislo(projekce.konec, 0));
  const splneno = cil <= 0 ? true : dosazeno >= cil * TOL_SPLNENO;
  const chybi = splneno ? 0 : Math.max(0, Math.round(cil - dosazeno));

  return {
    rezim: R,
    cilova: cil,
    horizontRoky: H,
    jednorazove,
    pravidelne,
    dosazeno,
    splneno,
    chybi,
    nedosazitelne,
    dopocitano,
    alokace: alok,
    vklady: jednorazove > 0 ? [...vkl, { zdroj: LABEL_DOPOCET, castka: jednorazove, mesic: 0 }] : vkl,
    projekce,
  };
}

/* ===== 4. Budoucí přítoky ===== */

/* RŮST VKLADU — PARAMETRY PATŘÍ DO DAT, NE NATVRDO DO KÓDU (diktát majitele, 9/2026).
 * ---------------------------------------------------------------------------
 * Majitel: „příjmy rostou tři čtyři procenta ročně… budeme to tam moct zadávat
 * ručně v nastavení… ohledně inflace můžeme nad i nenad inflaci."
 *
 * Do 9/2026 byla šablona natvrdo +5 % à 5 let šestkrát a nedala se nikde přepsat.
 * Teď se procento, krok i počet kroků zadávají v kartě řešení a UKLÁDAJÍ SE DO
 * ANALÝZY jako jeden záznam `pritoky[typ === PRITOK_NASTAVENI_RUST]`.
 * PROČ TAM: `pritoky` je jediné pole analýzy, do kterého karta plánu umí zapsat,
 * a ukládá se spolu s analýzou. Záznam NENÍ přítok — solver, cashflow ani graf
 * toku si ho nevšimnou, protože všichni testují `typ === "mzda"` / `"jednorazovy"`.
 *
 * ⚠️ BÁZE — TADY SE DÁ TICHO ZKREJT INFLACE. Engine počítá REÁLNĚ, nad inflací
 * (viz hlavička souboru). Když poradce napíše „4 % ročně" a myslí NOMINÁLNĚ,
 * musí se inflace ODEČÍST; jinak by plán klientovi přidal inflaci navrch a renta
 * by vyšla krásněji, než je pravda. Proto `baze`:
 *   · "realny"    — zadané % jde do enginu, jak je (dosavadní chování)
 *   · "nominalni" — reálné tempo = (1+n)/(1+i) − 1 (Fisher), teprve to jde do enginu
 * `inflacePct` MUSÍ BÝT VIDĚT na obrazovce a jít přepsat. Výchozí je doložený
 * předpoklad `INFLACE_PREDPOKLAD`, ze kterého vycházejí i reálné výnosy — ale
 * do samotné projekce dál NEVSTUPUJE, jen do tohoto přepočtu zadaného tempa.
 */
export const PRITOK_NASTAVENI_RUST = "nastaveni-rust-mzdy";

export const RUST_MZDY_BAZE = Object.freeze(["realny", "nominalni"]);

/* VÝCHOZÍ TEMPO RŮSTU = 3 % REÁLNĚ (majitel, 24. 9. 2026, jeho slova:
 * „jo, růst příjmu 3 procent je ok, nad inflaci.").
 *
 * ⚠️ CO TO DĚLÁ S ULOŽENÝMI PLÁNY: ANI JEDNO ČÍSLO. `solveCile` bere růst mzdy
 * VÝHRADNĚ z materializovaných přítoků `pritoky[typ="mzda"]` — ty vzniknou
 * teprve kliknutím na „Navrhnout růst mzdy" a leží v analýze. Tahle konstanta
 * říká jen, co tlačítko PŘEDVYPLNÍ příště. Plán, ve kterém už kroky jsou, jede
 * dál z nich.
 * ⚠️ CO SE ZMĚNÍ NA OBRAZOVCE: plán s kroky předvyplněnými po 5 % a BEZ
 * uloženého záznamu nastavení hlásí `stavRustuMzdy` → `LISI` („engine počítá
 * z těchhle kroků, nastavení předepisuje jiné"). Dřív u něj svítilo `SEDI`.
 * Je to pravda, ne regrese: plán opravdu počítá s 5 %, zatímco metodika říká 3.
 * Čísla plánu se nehnou, dokud poradce kroky sám nesrovná.
 *
 * Krok (5 let), počet kroků (6) ani báze (reálně) se NEMĚNÍ — majitel mluvil
 * jen o procentu. */
export const RUST_MZDY_VYCHOZI = Object.freeze({
  rustPct: 3,
  krokLet: 5,
  pocet: 6,
  baze: "realny",
  inflacePct: INFLACE_PREDPOKLAD * 100,
});

/** Číslo v rozsahu, nebo `null` když poradce nezadal nic použitelného.
 * NEZNÁMÉ ≠ NULA: prázdno propadne na výchozí hodnotu, ne na nulu. */
function vRozsahu(v, min, max) {
  if (v === null || v === undefined || v === "") return null;
  const n = +v;
  if (!Number.isFinite(n)) return null;
  return Math.min(max, Math.max(min, n));
}

const KLICE_RUSTU = ["rustPct", "krokLet", "pocet", "baze", "inflacePct"];

/** První vrstva, která daný klíč opravdu vyplnila (opts > uložené > výchozí). */
function slozRust(...vrstvy) {
  const out = {};
  for (const k of KLICE_RUSTU) {
    for (const v of vrstvy) {
      if (v && v[k] !== null && v[k] !== undefined && v[k] !== "") { out[k] = v[k]; break; }
    }
  }
  return out;
}

/**
 * Normalizované parametry růstu vkladu + tempo, se kterým engine SKUTEČNĚ počítá.
 * @param {object} nastaveni { rustPct, krokLet, pocet, baze, inflacePct }
 * @returns {{rustPct:number, krokLet:number, pocet:number, baze:string,
 *            inflacePct:number, realnyPct:number, rostouci:boolean, duvod:string|null}}
 *   `realnyPct` — reálné tempo p.a. v procentech; TO jde do enginu.
 *   `rostouci`  — false, když z parametrů nevyjde žádný reálný růst (nominální
 *                 zadání pod inflací nebo nula). Šablona pak nemá co předvyplnit
 *                 a karta to musí říct nahlas, ne mlčet.
 *   `duvod`     — `null` | "nulovy-rust" | "pod-inflaci" (kód pro hlášku v UI)
 */
export function rustMzdyParametry(nastaveni = {}) {
  const n = nastaveni || {};
  const rustPct = vRozsahu(n.rustPct, 0, 100) ?? RUST_MZDY_VYCHOZI.rustPct;
  /* krok nesmí být 0 — všechny kroky by dopadly do stejného roku a šablona by
   * místo postupného růstu poslala jednu skokovou částku. */
  const krokLet = Math.round(vRozsahu(n.krokLet, 1, 50) ?? RUST_MZDY_VYCHOZI.krokLet);
  const pocet = Math.round(vRozsahu(n.pocet, 0, 40) ?? RUST_MZDY_VYCHOZI.pocet);
  const baze = RUST_MZDY_BAZE.includes(n.baze) ? n.baze : RUST_MZDY_VYCHOZI.baze;
  const inflacePct = vRozsahu(n.inflacePct, 0, 100) ?? RUST_MZDY_VYCHOZI.inflacePct;
  /* Fisher, ne odečtení procent: při 4 % a inflaci 2,5 % je reálné tempo 1,46 %,
   * ne 1,5 %. U malých čísel je rozdíl drobný, u 10 % a 8 % už ne. */
  const realnyPct = baze === "nominalni"
    ? ((1 + rustPct / 100) / (1 + inflacePct / 100) - 1) * 100
    : rustPct;
  const rostouci = realnyPct > 0 && pocet > 0;
  const duvod = rostouci ? null : (baze === "nominalni" && rustPct > 0 && realnyPct <= 0 ? "pod-inflaci" : "nulovy-rust");
  return { rustPct, krokLet, pocet, baze, inflacePct, realnyPct, rostouci, duvod };
}

/**
 * Uložené nastavení růstu z analýzy (záznam v `pritoky`), už normalizované.
 * `ulozeno: false` = karta ukazuje VÝCHOZÍ čísla a nesmí je vydávat za volbu
 * poradce. `ids` jsou všechny nalezené záznamy — karta je při zápisu smaže
 * všechny, aby se jich v datech nenasbírala řada.
 */
export function rustMzdyNastaveni(analyza) {
  const zapisy = asArr(analyza?.pritoky).filter((p) => p?.typ === PRITOK_NASTAVENI_RUST);
  const posledni = zapisy.length ? zapisy[zapisy.length - 1] : null;
  return {
    ...rustMzdyParametry(posledni || {}),
    ulozeno: !!posledni,
    ids: zapisy.map((p) => p?.id).filter(Boolean),
  };
}

/** Poznámka k přítoku — musí z ní být poznat, jestli se inflace odečítala. */
function poznamkaRustu(P) {
  const pct = (x) => String(Math.round(x * 100) / 100).replace(".", ",");
  if (P.baze === "nominalni") {
    return `šablona: růst mzdy +${pct(P.rustPct)} % nominálně − inflace ${pct(P.inflacePct)} % = +${pct(P.realnyPct)} % reálně à ${P.krokLet} let — upravte dle klienta`;
  }
  return `šablona: růst mzdy +${pct(P.rustPct)} % à ${P.krokLet} let — upravte dle klienta`;
}

/**
 * Šablona růstu mzdy — složeně, `pocet` kroků po `krokLet` letech.
 * Parametry: `opts` (co poradce právě zadal) > nastavení uložené v analýze >
 * `RUST_MZDY_VYCHOZI`. Bez obojího vyjde +3 % REÁLNĚ à 5 let ×6 (majitel,
 * 24. 9. 2026 — do té doby +5 %; viz `RUST_MZDY_VYCHOZI`).
 * Jde o návrh, žádná garance — poradce si čísla upraví.
 * @param {object} analyza analýza v2 (bere se `prijmy2` jako základ a `pritoky` kvůli nastavení)
 * @param {object} [opts] { rustPct, krokLet, pocet, baze, inflacePct }
 * @returns pole přítoků [{typ: "mzda", odRoku, castkaMes, poznamka}] — prázdné,
 *   když není z čeho růst (nulový příjem) nebo když z parametrů reálný růst nevyjde.
 */
export function navrhniRustMzdy(analyza, opts = {}) {
  const P = rustMzdyParametry(slozRust(opts, rustMzdyNastaveni(analyza)));
  const p2 = analyza?.prijmy2 || {};
  const zaklad = (+p2.prokaz1 || 0) + (+p2.prokaz2 || 0) + (+p2.ostatni1 || 0) + (+p2.ostatni2 || 0);
  if (zaklad <= 0 || !P.rostouci) return [];
  const r = P.realnyPct / 100;
  const poznamka = poznamkaRustu(P);
  const out = [];
  for (let k = 1; k <= P.pocet; k++) {
    const prirustek = zaklad * (Math.pow(1 + r, k) - Math.pow(1 + r, k - 1));
    const castkaMes = Math.round(prirustek / 100) * 100;
    /* ZÁMĚRNÁ ZMĚNA CHOVÁNÍ proti kódu do 9/2026: krok zaokrouhlený na 0 Kč se
     * NEVYDÁ. „+0 Kč/měs od r. 2031" není údaj o klientovi, jen šum po
     * zaokrouhlení na stokoruny.
     * MATEMATIKA PLÁNU SE TÍM NEMĚNÍ: kapacita si přítoky sčítá jako
     * `c += +p.castkaMes || 0` (níž v solveCile), takže nulový krok do ní
     * nikdy nic nepřidal. Mizí jen šum na obrazovce — chip „+0 Kč", řádek
     * „+0 Kč/měs — šablona…" v `cashflowTimeline` a započtený krok v hlášce
     * „předvyplněno N kroků".
     * Kdy to vůbec nastane: u prvního kroku při 5 % až pod ~1 000 Kč/měs
     * základu (5 % z 999 = 50 → zaokrouhleno na 0). Přírůstky složeně ROSTOU,
     * takže `continue` zahodí nejvýš vedoucí nuly, nikdy krok mezi dvěma
     * nenulovými. */
    if (castkaMes <= 0) continue;
    out.push({ typ: "mzda", odRoku: k * P.krokLet, castkaMes, poznamka });
  }
  return out;
}

/* ===== SEDÍ OBRAZOVKA S ENGINEM? =====
 *
 * ⚠️ NÁLEZ 9/2026 (vysoká): panel „Růst vkladu — nastavení" psal „S tímhle
 * tempem engine počítá." hned při přepsání procenta. NEPLATILO TO. `solveCile`
 * bere růst mzdy VÝHRADNĚ z materializovaných přítoků `pritoky[typ="mzda"]`
 * a ty vzniknou teprve kliknutím na „Navrhnout růst mzdy". Poradce mohl
 * předvyplnit kroky při 5 % reálně, pak přepnout na 3 % nominálně (panel
 * ukázal 0,49 % reálně a tvrdil, že tak se počítá) — a renta i graf dál jely
 * z pětiprocentních kroků. Na obrazovce jedno číslo, v plánu druhé.
 *
 * PROČ SE KROKY NEPŘEPOČÍTÁVAJÍ SAMY (cesta „a" ze zadání) — a proč tahle
 * funkce místo toho jen HLÁSÍ ROZDÍL (cesta „b"):
 *   1. kroky jsou NÁVRH, který poradce ručně upravuje i maže (chipy u renty);
 *      automatický přepočet by mu je při každém úhozu do procenta smazal,
 *   2. procento se komituje živě při každém platném znaku — automatika by za
 *      jedno psané „3,5" vyrobila a zahodila desítky záznamů v datech,
 *   3. v panenském stavu (nikdo nic nepředvyplnil) by změna procenta tiše
 *      zavedla do plánu růst, o který poradce nepožádal — a renta by vyskočila.
 * Rozdíl proto musí být VIDĚT a jít jedním kliknutím srovnat; stav, kdy panel
 * tvrdí jedno a engine počítá druhé, zůstat nesmí.
 */
export const RUST_STAV = Object.freeze({
  SEDI: "sedi",    // engine počítá přesně to, co je nastavené
  CHYBI: "chybi",  // nastavení předepisuje růst, ale kroky nejsou předvyplněné → engine počítá BEZ růstu
  LISI: "lisi",    // předvyplněné kroky jsou jiné, než co by z nastavení vyšlo → engine počítá Z NICH
});

/**
 * Porovná, co engine SKUTEČNĚ počítá (materializované přítoky mzdy), s tím,
 * co by z nastavení vyšlo. Jediný zdroj pravdy pro hlášku v kartě i pro testy.
 * @param {object} analyza analýza v2
 * @param {object} [nastaveni] parametry z panelu (jinak se vezmou z analýzy)
 * @returns {{stav:string, sedi:boolean, kroky:object[], nahled:object[]}}
 *   `kroky`  — přítoky mzdy, ze kterých engine počítá (i ruční),
 *   `nahled` — kroky, které by z nastavení vyšly.
 * Prázdné obojí = SEDI: engine nepočítá s růstem a nastavení žádný nepředepisuje.
 */
export function stavRustuMzdy(analyza, nastaveni) {
  const nahled = navrhniRustMzdy(analyza, nastaveni || {});
  /* Srovnává se jen ŠABLONA (kroky z tlačítka). Ruční přítok mzdy (známé
   * zvýšení platu zapsané v analýze nebo v plánu) je samostatná položka —
   * engine s ním počítá vedle šablony a šablona ho už nepřepisuje
   * (majitel 5. 10. 2026: „dal jsem navrhnout růst mzdy … nic se nemění“ —
   * šablona tiše nahradila ruční +5 000 od 3. roku vlastními kroky od 5. roku
   * a renta skončila na stejném procentu). */
  const kroky = asArr(analyza?.pritoky).filter(jeSablonovyKrokMzdy);
  const rucni = asArr(analyza?.pritoky).filter((p) => p?.typ === "mzda" && !jeSablonovyKrokMzdy(p));
  /* Srovnává se ROK a ČÁSTKA, ne poznámka ani id: kroky ze dvou různých cest
   * (tlačítko × ruční přidání) se stejnými čísly znamenají pro engine totéž. */
  const klic = (p) => `${Math.round(cislo(p?.odRoku, 0))}|${Math.round(cislo(p?.castkaMes, 0))}`;
  const a = kroky.map(klic).sort();
  const b = nahled.map(klic).sort();
  const sedi = a.length === b.length && a.every((x, i) => x === b[i]);
  return {
    stav: sedi ? RUST_STAV.SEDI : (kroky.length === 0 ? RUST_STAV.CHYBI : RUST_STAV.LISI),
    sedi, kroky, nahled, rucni,
  };
}

/**
 * Je přítok krok ŠABLONY růstu mzdy (vznikl tlačítkem „Navrhnout růst mzdy“)?
 * Šablona razí poznámku „šablona: …“ (`poznamkaRustu`) — platí i pro kroky
 * uložené dřív. Ruční přítok mzdy (poznámka od poradce) šablonou NENÍ.
 */
export function jeSablonovyKrokMzdy(p) {
  return p?.typ === "mzda" && /^\s*šablona/i.test(String(p?.poznamka || ""));
}

/* ===== 5. Hypotéka jako cíl ===== */

/* ⚠️ JEDNA AKONTACE PRO CELOU APPKU (audit R1, 25. 8. 2026).
 * ---------------------------------------------------------------------------
 * Do 25. 8. 2026 se akontace počítala na ŠESTI místech a dvěma pravidly:
 *   · solver     → 10 % do 36 let (výjimka ČNB), jinak 20 %
 *   · planEngine → `castka(hodnota) * 0.2` natvrdo
 *   · cestaLayout, AnalyzaV2Page, CilCinema (2×) → taky `* 0.2`
 * Klient s cílem „byt za 3 000 000" tak viděl na jedné A4 vlastní zdroje
 * 600 000 a hned pod tím rozvrh spočítaný na 300 000 (a procento plnění proti
 * těm 300 000, takže 100 %).
 *
 * ZDROJEM PRAVDY JE SOLVER, protože jediný zná:
 *   1. VOLBU PORADCE — `cil.akontacePct` vybraný v kartě Financování,
 *   2. VĚKOVÉ PRAVIDLO ČNB — do 36 let stačí 10 % (LTV 90 %),
 *   3. celou nabídku variant včetně 100% financování s úvěrem ze stavebka.
 * Když věk neznáme, drží se konzervativnějších 20 % (LTV 80 %) — dřívější
 * chování analytické vrstvy, takže se jí nic nezhorší.
 */
export const AKONTACE_VYCHOZI_PCT = 20;   // standardní limit ČNB (LTV 80 %)
export const AKONTACE_MLADI_PCT = 10;     // výjimka ČNB do 36 let (LTV 90 %)
export const VEK_MLADI = 36;

/** Výchozí akontace podle věku klienta (ČNB). Neznámý věk → 20 %.
 * NEZNÁMÝ ≠ 0 LET (2. 10. 2026): `+null` i `+""` je v JS 0, a dokud tu nebyla
 * první podmínka, dostal klient bez data narození výjimku pro mladé (10 %).
 * Tak to ukazovalo kino analýzy (`ctx.vekK1 = null`) i Cesta života, zatímco
 * plán po opravě `?? 35` počítá s 20 % — jeden cíl, dvě čísla. */
export function akontacePctVychozi(vek) {
  if (vek == null || vek === "") return AKONTACE_VYCHOZI_PCT;
  const v = +vek;
  return Number.isFinite(v) && v < VEK_MLADI ? AKONTACE_MLADI_PCT : AKONTACE_VYCHOZI_PCT;
}

/** Věk klienta neznámý? (`null`, prázdno, text, NaN — ne nula, ta je zadaná.) */
const vekNeznamy = (vek) => vek == null || vek === "" || !Number.isFinite(+vek);

/**
 * Akontace platná pro daný cíl: volba poradce má přednost před věkovým
 * pravidlem. `akontacePct === 0` je legitimní volba (100% financování), proto
 * se testuje TVAR, ne pravdivost.
 *
 * ⚠️ ULOŽENÝCH 10 % BEZ VĚKU = 20 % (2. 10. 2026, jeden cíl = jedno číslo).
 * 10 % je výjimka ČNB pro mladé — bez známého věku ji solver nenabízí
 * (`hypoVarianty`) a cíl spadne na 20 %. Dřív tu volba poradce vyhrála
 * i bez věku, takže kino analýzy a Cesta života ukazovaly u bytu za 5 mil.
 * 500 000, plán 1 000 000. Takový cíl vznikal snadno: do opravy solver
 * klientovi bez data narození dosadil 35 let a variantu 10 % mu nabídl.
 * U ZNÁMÉHO věku se nic nemění (volba poradce platí jako dřív).
 */
export function akontacePctCile(cil, vek, kontext = null) {
  /* S KONTEXTEM (metodika 2026-10-05): věk V ROCE KOUPĚ a pravidlo země.
   * Uložených 10 % u klienta, který na výjimku v roce koupě nárok mít nebude,
   * = 20 % — jinak by kino a Cesta života slibovaly 10 %, které plán (varianta
   * 10 % se mu nenabízí) stejně nepočítá. Jeden cíl = jedno číslo. */
  if (kontext) {
    const pr = pravidloAkontace(vek, { ...kontext, roky: kontext.roky ?? cil?.roky });
    const zadanoK = cil?.akontacePct;
    if (zadanoK != null && zadanoK !== "" && Number.isFinite(+zadanoK)) {
      const pct = Math.min(100, Math.max(0, +zadanoK));
      if (pct === AKONTACE_MLADI_PCT && !pr.mladi) return AKONTACE_VYCHOZI_PCT;
      return pct;
    }
    return pr.pct;
  }
  const zadano = cil?.akontacePct;
  if (zadano != null && zadano !== "" && Number.isFinite(+zadano)) {
    const pct = Math.min(100, Math.max(0, +zadano));
    if (pct === AKONTACE_MLADI_PCT && vekNeznamy(vek)) return AKONTACE_VYCHOZI_PCT;
    return pct;
  }
  return akontacePctVychozi(vek);
}

/* ── AKONTACE PODLE VĚKU V ROCE KOUPĚ A ZEMĚ (METODIKA 2026-10-05) ───────────
 * Rozhodnutí majitele 4. 10. 2026 („ok vse dle doporučení“, body 7 a 8):
 *
 * 7) VĚK V ROCE KOUPĚ, ne dnešní. Banka posuzuje výjimku pro mladé k datu
 *    žádosti. Do téhle změny dostal 33letý klient s bytem za 3 roky akontaci
 *    10 %, i když mu v době koupě bude 36 a banka mu dá jen 80 % LTV — vlastní
 *    zdroje 500 000 místo 1 000 000 (čísla: beplan-pracovni/
 *    2026-10-04-analyza-rozhodnuti/vek-v-roce-koupe-pred-po.md).
 *    Věk v roce koupě = dnešní věk + roky do cíle (nezadaný horizont = 0).
 *
 * 8) SLOVENSKO PODLE NBS (lib/nastroje/uverLimity.js, LTV_PRAVIDLA.SK):
 *    koupě v roce ≤ 2026 → jistých 80 % LTV (90 % jen v kvótě banky —
 *    neslibuje se); od 2027 → 90 % jen PRVNÍ BYDLENÍ do 35 let bez rezidenční
 *    nemovitosti (každý žadatel), jinak 80 %.
 *
 * Starý plán (razítko < 2026-10-05) tohle nevolá — počítá dnešním věkem
 * a českým pravidlem jako dřív (pravidlo majitele 3. 10.: nepřepočítávat). */

/** Věk v roce koupě: dnešní věk + roky do cíle. Neznámý věk → `null`. */
export function vekVRoceKoupe(vekDnes, roky) {
  if (vekNeznamy(vekDnes)) return null;
  const r = Number.isFinite(+roky) && +roky > 0 ? Math.round(+roky) : 0;
  return +vekDnes + r;
}

/* Rezidenční nemovitost = byt / dům k bydlení i k pronájmu (NBS § 7 ods. 5:
 * domy, byty, apartmány; ne pozemky ani komerční prostory). Počítá se řádek,
 * do kterého poradce něco zadal (hodnotu, částku v cizí měně nebo adresu) —
 * přednastavený prázdný řádek „Byt / rodinný dům“ nemovitost není. */
const TYPY_REZIDENCNI = new Set([NEMOVITOST_BYDLENI, NEMOVITOST_PRONAJEM]);

/** Vlastní klient podle analýzy rezidenční nemovitost (k bydlení / k pronájmu)? */
export function maRezidencniNemovitost(analyza) {
  for (const r of asArr(asObj(asObj(analyza).majetek).nemovitosti)) {
    if (!TYPY_REZIDENCNI.has(typNefin(r))) continue;
    if ((+r.hodnota || 0) > 0 || (+r.original_amount || 0) > 0 || asText(r.adresa).trim()) return true;
  }
  return false;
}

/* HYPOTÉKA V ZÁVAZCÍCH = KLIENT NEMOVITOST MÁ (dočištění 4. 10. 2026, bod 8).
 * Hypotéka (i americká) je úvěr zajištěný nemovitostí — když ji klient splácí,
 * první bydlení to podle NBS není, i když poradce řádek nemovitosti v majetku
 * nevyplnil. Neznámé ≠ nárok: bez téhle kontroly appka slíbila 10 %.
 * Počítá se řádek tabu „Hypotéky a bydlení“ s jistinou nebo splátkou, jehož
 * druh je hypotéka, nebo starý řádek bez druhu s textem „hypot…“. Úvěr ze
 * stavebka a „jiný úvěr na bydlení“ zajištění nemovitostí mít nemusí. */
const DRUHY_HYPOTEKY = new Set(["hypoteka", "americka_hypoteka"]);
function maHypotekuVZavazcich(analyza) {
  const a = asObj(analyza);
  for (const pole of ["zavazkyB", "zavazkyO"]) {
    for (const r of asArr(a[pole])) {
      if (!((+r.jistina || 0) > 0 || (+r.splatka || 0) > 0)) continue;
      if (tabZavazku(r, pole) !== HYPOTEKY) continue;
      const druh = asText(r.druh).trim();
      if (druh ? DRUHY_HYPOTEKY.has(druh) : /hypot/i.test(asText(r.oznaceni))) return true;
    }
  }
  return false;
}

/** Věk druhého klienta dnes (z data narození), jinak `null`. */
function vekKlienta2(analyza) {
  const nar = asObj(asArr(asObj(analyza).klienti)[1]).narozeni;
  if (!nar) return null;
  return vekKlienta({ klienti: [{ narozeni: nar }] });
}

/* DRUHÝ KLIENT = DALŠÍ ŽADATEL i bez data narození (dočištění 4. 10. 2026).
 * Osobu dělá identita (jméno / příjmení / narození — `osobyZAnalyzy.maPartnera`)
 * a u úvěru i příjem: příjem druhého klienta bez jména je nejčastěji nevyplněný
 * partner, který bude žádat s klientem. Prázdný řádek žadatel není. */
function maKlienta2(analyza) {
  const a = asObj(analyza);
  const k = asObj(asArr(a.klienti)[1]);
  if (asText(k.jmeno).trim() || asText(k.prijmeni).trim() || asText(k.narozeni).trim()) return true;
  const p2 = asObj(a.prijmy2);
  return (+p2.prokaz2 || 0) + (+p2.ostatni2 || 0) > 0;
}

/**
 * Kontext pravidla akontace z analýzy — jednou na obrazovku / běh solveru.
 * @param {object} analyza `data.v2`
 * @param {{zeme?: string, dnes?: Date|string}} [o] země tenanta (bez ní globální
 *   store appky), dnešek (testy)
 * @returns {{zeme: string, rokDnes: number, prvniBydleni: boolean, vekDnesK2: number|null, maK2: boolean}}
 *   `prvniBydleni` = žádná rezidenční nemovitost v majetku ANI hypotéka
 *   v závazcích; `maK2` = druhý klient je další žadatel (i bez data narození)
 */
export function kontextAkontace(analyza, { zeme, dnes } = {}) {
  const d = dnes ? new Date(dnes) : new Date();
  const rokDnes = Number.isFinite(d.getTime()) ? d.getFullYear() : new Date().getFullYear();
  return {
    zeme: normZeme(zeme ?? zemeTenanta()),
    rokDnes,
    prvniBydleni: !maRezidencniNemovitost(analyza) && !maHypotekuVZavazcich(analyza),
    vekDnesK2: vekKlienta2(analyza),
    maK2: maKlienta2(analyza),
  };
}

/**
 * JEDNO PRAVIDLO AKONTACE pro plán, kino cíle i Cestu života.
 * @param {number|null} vekDnes dnešní věk 1. klienta
 * @param {object} kontext `kontextAkontace(…)` + `roky` (roky do koupě)
 * @returns {{pct: number, mladi: boolean, vekPriKoupi: number|null, rokKoupe: number,
 *   zeme: string, vekZnamy: boolean, rezim?: string, zdroj: string, overeno: string|null}}
 */
export function pravidloAkontace(vekDnes, kontext = {}) {
  const k = asObj(kontext);
  const roky = Number.isFinite(+k.roky) && +k.roky > 0 ? Math.round(+k.roky) : 0;
  const rokDnes = Number.isFinite(+k.rokDnes) && +k.rokDnes > 0 ? +k.rokDnes : new Date().getFullYear();
  const zeme = normZeme(k.zeme ?? zemeTenanta());
  const vekPriKoupi = vekVRoceKoupe(vekDnes, roky);
  const rokKoupe = rokDnes + roky;
  /* Druhý klient bez data narození je pořád další žadatel → `[null]` =
   * podmínka nesplněna (uverLimity: neznámý věk = přísnější limit). Dřív
   * šlo `[]` a SK od 2027 slíbilo 10 %, jako by žádný nebyl. */
  const k2BezVeku = k.maK2 === true && vekNeznamy(k.vekDnesK2);
  const vekPriKoupiK2 = vekNeznamy(k.vekDnesK2) ? null : vekVRoceKoupe(k.vekDnesK2, roky);
  const prvniBydleni = k.prvniBydleni === true;
  const lim = ltvLimit({
    vek: vekPriKoupi,
    zeme,
    rokKoupe,
    prvniBydleni,
    vekyDalsich: vekPriKoupiK2 !== null ? [vekPriKoupiK2] : (k2BezVeku ? [null] : []),
  });
  const P = LTV_PRAVIDLA[zeme] || LTV_PRAVIDLA.CZ;
  /* PODLE ČEHO SE 10 % ROZHODLO — pro větu na kartě hypotéky. Dřív karta
   * psala „podle věku v roce koupě“ i tam, kde věk nerozhoduje (SK koupě do
   * 2026 = pevných 80 %, SK s vlastním bytem = rozhoduje byt). */
  const duvod = lim.rezim === "do2026" ? "do2026"
    : lim.rezim === "od2027" && !prvniBydleni ? "nemovitost"
      : lim.rezim === "od2027" && k2BezVeku ? "k2BezVeku"
        : "vek";
  return {
    pct: 100 - lim.limitPct,
    mladi: lim.mladi === true,
    vekPriKoupi,
    rokKoupe,
    zeme,
    vekZnamy: vekPriKoupi !== null,
    ...(lim.rezim ? { rezim: lim.rezim } : {}),
    duvod,
    ...(lim.rezim === "od2027" && vekPriKoupiK2 !== null ? { vekPriKoupiK2 } : {}),
    zdroj: P.zdroj,
    overeno: P.overeno || null,
  };
}

/* ── ÚROK A SPLATNOST HYPOTÉKY U KONKRÉTNÍHO CÍLE ─────────────────────────────
 * Majitel 25. 9. 2026: „tam má být tabulka hypotečního úvěru, aby se dalo
 * posouvat, upravovat ty hypotéky." Do té doby byla splatnost jedno číslo pro
 * celý plán (30 let) a sazba konstanta v kódu — poradce s nabídkou banky
 * na 5,3 % / 25 let neměl kam ji zadat.
 *
 * Obojí se ukládá K CÍLI (`cil.sazbaHypoPct`, `cil.splatnostHypoLet`), stejně
 * jako už dřív `akontacePct`. Dva cíle na úvěr (byt a rekonstrukce) mají každý
 * svou nabídku, takže jedno číslo na celý plán by jednomu z nich lhalo.
 *
 * ⚠️ JEDNOTKA JE V NÁZVU POLE. Sazba se ukládá v PROCENTECH (5.3), protože
 * přesně to poradce píše do inputu; engine počítá s desetinným číslem (0.053).
 * Dokud se to pole jmenovalo jen `sazbaHypo`, nešlo od pohledu poznat, které
 * z těch dvou čísel v datech leží — a 5,3 místo 0,053 je splátka o dva řády
 * jinde. `Pct` a `Let` v názvu je ta nejlevnější pojistka.
 *
 * NEZADÁNO ≠ NULA: prázdno, text i záporná hodnota znamenají „poradce nic
 * nevyplnil" a vrací se výchozí hodnota plánu. Nula by u sazby byla úvěr
 * zdarma, u splatnosti dělení nulou. */

/** Úrok hypotéky u cíle jako DESETINNÉ číslo (0,049). Bez volby poradce výchozí. */
export function sazbaHypoCile(cil, vychozi = SAZBA_HYPO) {
  const z = cil?.sazbaHypoPct;
  if (z == null || z === "" || !Number.isFinite(+z) || +z <= 0) return vychozi;
  return Math.min(SAZBA_HYPO_MAX_PCT, +z) / 100;
}

/** Splatnost hypotéky u cíle v LETECH. Bez volby poradce výchozí plánu. */
export function splatnostHypoCile(cil, vychozi = SPLATNOST_HYPO_VYCHOZI) {
  const z = cil?.splatnostHypoLet;
  const v = Number.isFinite(+vychozi) && +vychozi > 0 ? +vychozi : SPLATNOST_HYPO_VYCHOZI;
  if (z == null || z === "" || !Number.isFinite(+z) || +z <= 0) return v;
  return Math.min(SPLATNOST_HYPO_MAX, Math.max(1, Math.round(+z)));
}

/** Zadal poradce úrok / splatnost sám? Karta podle toho píše „zadáno" vs „výchozí". */
export const sazbaHypoZadana = (cil) => {
  const z = cil?.sazbaHypoPct;
  return z != null && z !== "" && Number.isFinite(+z) && +z > 0;
};
export const splatnostHypoZadana = (cil) => {
  const z = cil?.splatnostHypoLet;
  return z != null && z !== "" && Number.isFinite(+z) && +z > 0;
};

/**
 * Vlastní zdroje, které si klient musí na cíl financovaný úvěrem naspořit.
 * Cíl BEZ úvěru se spoří celý — pravidlo je tu schválně jedno pro obojí, aby
 * si ho volající nemuseli psát znovu (a pokaždé jinak).
 */
export function vlastniZdrojeCile(cil, vek, kontext = null) {
  const cena = +cil?.hodnota || 0;
  if (!(cena > 0)) return 0;
  if (!cil?.uverem) return cena;
  return Math.round((cena * akontacePctCile(cil, vek, kontext)) / 100);
}

/**
 * Varianty financování hypotečního cíle.
 * 10 % akontace jen do 36 let (výjimka ČNB); 100% financování = rozhodnutí
 * poradce (10 % ceny kryje úvěr ze stavebního spoření 5,5 % / 20 let).
 * `mladi` (metodika 2026-10-05) = nárok na 10 % podle `pravidloAkontace`
 * (věk v roce koupě, země); bez něj se rozhoduje dnešním věkem jako dřív.
 * @returns pole variant {id, nazev, akontacePct, vlastniZdroje, hypoteka,
 *   splatkaHypo, uverStavebko|null, celkemMesicne, pozn}
 */
export function hypoVarianty(cil, klientVek, { splatnostHypo = SPLATNOST_HYPO_VYCHOZI, sazbaHypo = SAZBA_HYPO, mladi = undefined, zeme = undefined } = {}) {
  const cena = +cil?.hodnota || 0;
  const vek = klientVek == null ? 99 : +klientVek;
  const mkVar = (id, nazev, pct, pozn) => {
    const vlastniZdroje = Math.round((cena * pct) / 100);
    const hypoteka = cena - vlastniZdroje;
    const splatkaHypo = Math.round(anuitniSplatka(hypoteka, sazbaHypo, splatnostHypo));
    return { id, nazev, akontacePct: pct, vlastniZdroje, hypoteka, splatkaHypo, uverStavebko: null, celkemMesicne: splatkaHypo, pozn };
  };
  const varianty = [];
  const narokMladi = typeof mladi === "boolean" ? mladi : vek < VEK_MLADI;
  /* Slovensko: regulátor NBS, ne ČNB (poznámky jsou interní, na obrazovku nejdou). */
  const sk = zeme !== undefined && normZeme(zeme) === "SK";
  if (narokMladi) varianty.push(mkVar("a10", "Akontace 10 %", AKONTACE_MLADI_PCT, sk ? "NBS od 2027: první bydlení do 35 let (LTV 90 %)." : "Jen do 36 let — výjimka ČNB pro mladé (LTV 90 %)."));
  varianty.push(mkVar("a20", "Akontace 20 %", AKONTACE_VYCHOZI_PCT, sk ? "Jistý limit NBS (LTV 80 %)." : "Standardní limit ČNB (LTV 80 %)."));
  varianty.push(mkVar("a30", "Akontace 30 %", 30, "Nižší splátka, ale váže víc vlastních zdrojů."));
  // 100% financování: 90 % hypotéka + 10 % úvěr ze stavebního spoření
  {
    const hypoteka = Math.round(cena * 0.9);
    const stavebkoJistina = cena - hypoteka;
    const splatkaHypo = Math.round(anuitniSplatka(hypoteka, sazbaHypo, splatnostHypo));
    const splatkaStavebko = Math.round(anuitniSplatka(stavebkoJistina, SAZBA_STAVEBKO_UVER, SPLATNOST_STAVEBKO_UVER));
    varianty.push({
      id: "a100", nazev: "100% financování", akontacePct: 0,
      vlastniZdroje: 0, hypoteka, splatkaHypo,
      uverStavebko: { castka: stavebkoJistina, sazba: SAZBA_STAVEBKO_UVER, roky: SPLATNOST_STAVEBKO_UVER, splatka: splatkaStavebko },
      celkemMesicne: splatkaHypo + splatkaStavebko,
      pozn: "Rozhodnutí poradce: 10 % ceny kryje úvěr ze stavebního spoření 5,5 % p.a. na 20 let, zbytek hypotéka.",
    });
  }
  return varianty;
}

/* ── ÚVĚR NA CÍL MIMO BYDLENÍ (metodika 2026-10-05, majitel 5. 10. 2026) ─────
 * Auto, podnikání a jiné cíle mimo bydlení se nefinancují hypotékou: buď
 * SPOTŘEBITELSKÝ úvěr, nebo ÚVĚR ZE STAVEBNÍHO SPOŘENÍ, splatnost nejvýš 12 let.
 * Akontace podle ČNB tu neplatí — „vlastní zdroje“ jsou jen to, co klient
 * zaplatí HOTOVĚ (0 / 10 / 20 / 30 % ceny). Tvar variant je TÝŽ jako
 * u `hypoVarianty` (pole `hypoteka` a `splatkaHypo` = výše úvěru a jeho splátka),
 * aby cashflow, tisk i karta četly jedno a totéž. */

/** Produkt úvěru u cíle mimo bydlení: „spotrebitelsky“ (výchozí) | „stavebko“. */
export const produktUveruCile = (cil) => (cil?.uverProdukt === "stavebko" ? "stavebko" : "spotrebitelsky");

/** Úrok úvěru mimo bydlení (desetinně). Zadaný úrok poradce má přednost. */
export function sazbaUveruCile(cil) {
  const vychozi = produktUveruCile(cil) === "stavebko" ? SAZBA_STAVEBKO_UVER : SAZBA_SPOTREBITELSKY_UVER;
  return sazbaHypoCile(cil, vychozi);
}

/** Splatnost úvěru mimo bydlení v letech — strop 12 let (majitel 5. 10. 2026). */
export function splatnostUveruCile(cil) {
  const z = cil?.splatnostHypoLet;
  if (z == null || z === "" || !Number.isFinite(+z) || +z <= 0) return SPLATNOST_SPOTREBITELSKY_VYCHOZI;
  return Math.min(SPLATNOST_SPOTREBITELSKY_MAX, Math.max(1, Math.round(+z)));
}

/**
 * Varianty „kolik hotově“ u úvěru mimo bydlení.
 * @returns pole {id, nazev, akontacePct, vlastniZdroje, hypoteka (= výše úvěru),
 *   splatkaHypo (= splátka úvěru), uverStavebko: null, celkemMesicne, produkt}
 */
export function uverVarianty(cil, { sazba = SAZBA_SPOTREBITELSKY_UVER, splatnost = SPLATNOST_SPOTREBITELSKY_VYCHOZI, produkt = "spotrebitelsky" } = {}) {
  const cena = +cil?.hodnota || 0;
  return HOTOVE_UVER_PCT.map((pct) => {
    const vlastniZdroje = Math.round((cena * pct) / 100);
    const uver = cena - vlastniZdroje;
    const splatka = Math.round(anuitniSplatka(uver, sazba, splatnost));
    return {
      id: `h${pct}`, nazev: `Hotově ${pct} %`, akontacePct: pct,
      vlastniZdroje, hypoteka: uver, splatkaHypo: splatka, uverStavebko: null,
      celkemMesicne: splatka, produkt,
    };
  });
}

/* ── DOFINANCOVÁNÍ Z MAJETKU (krok 3 karty cíle, majitel 5. 10. 2026) ─────────
 * „rikal jsem zt je tam i možnost vybrat financovat dofinancovat z majetku
 * z dědictví od rodičů“. Volba poradce u cíle na úvěr (`navrh.solver.
 * dofinancovani[idCile]`), jen v metodice 2026-10-05; bez volby výpočet beze
 * změny:
 *   · „prodej“ — prodej stávající nemovitosti (nebo jiného majetku, „jiny“):
 *     výnos z prodeje sníží vlastní zdroje, které se musí naspořit,
 *   · „zastava“ — zástava další nemovitosti (typicky rodičů): banka posuzuje
 *     LTV k OBĚMA nemovitostem, takže úvěr smí být vyšší. Limit LTV je TÝŽ
 *     jako u akontace (`ltvLimit` / pravidlo země). Je to modelace — souhlas
 *     zástavce, odhad i bonitu posoudí banka („ověřit u banky“).
 * Částku (hodnotu majetku) poradce vidí a smí přepsat; výchozí je hodnota
 * z analýzy. */
export const DOFINANCOVANI_ZPUSOBY = Object.freeze(["prodej", "zastava", "jiny"]);

/** Normalizovaná volba dofinancování u cíle, nebo `null`. */
/* S `analyza` (solver ji posílá vždy) se volba ověří nad daty analýzy
 * (lib/dofinancovaniMajetek.js, oprava 5. 10. 2026 — „Škoda Rapid nejde
 * zastavit přece“): zástava jen nemovitost, prodej nemovitosti jen nemovitost,
 * „jiný majetek“ jen movitý majetek / dědictví v penězích; položka, která
 * v analýze už není, a zástava u spotřebitelského úvěru taky neplatí.
 * Neplatná volba se NEPOČÍTÁ (karta řekne proč) — žádné tiché započtení.
 * `vlastnici` (`vlastniciMajetku`, dočištění 5. 10. 2026): položku, kterou už
 * započítal dřívější cíl v pořadí plánu, druhý cíl nepočítá. */
export function volbaDofinancovani(mapa, cilId, analyza, { spotr = false, vlastnici = null } = {}) {
  const v = asObj(asObj(mapa)[cilId]);
  if (!DOFINANCOVANI_ZPUSOBY.includes(v.zpusob)) return null;
  const castka = Number.isFinite(+v.castka) && +v.castka > 0 ? Math.round(+v.castka) : 0;
  if (!(castka > 0)) return null;
  if (analyza !== undefined && !platnostVolbyDofinancovani(v, analyza, { spotr, vlastnici, cilId }).platna) return null;
  return { zpusob: v.zpusob, castka, nazev: asText(v.nazev), majetekId: v.majetekId != null ? String(v.majetekId) : null };
}

/* ── ÚVĚR NA CÍL V ČASE — JEDNA FUNKCE PRO PLÁN I GRAF (5. 10. 2026) ─────────
 * Majitel 5. 10. 2026 u karty Bydlení: „a pak zbytek kolik bude splátka“.
 * STARÁ CHYBA: graf „Cashflow v čase“ splátku znal (přebytek po čerpání
 * klesl), solver ne. Kapacita pro další cíle a rentu se od roku koupě vrátila
 * celá — u Čunga renta +18 000 Kč/měs od 2031, přitom splátka 29 721 Kč/měs
 * ty peníze spolkne. Teď jdou plán i graf přes TYTÉŽ funkce:
 *   · `uverCileVCase`      — úvěr cíle jako událost: od kdy, do kdy, kolik,
 *   · `najemPredKoupi` + `priradNajemKUverum` — nájem, který koupě bydlení
 *                            ukončí (jednou, u prvního úvěru na bydlení),
 *   · `zatizeniUvery`      — o kolik úvěry v daném měsíci zmenší volné peníze.
 * Solver (metodika 2026-10-05) je odečte z kapacity od měsíce čerpání,
 * `cashflowTimeline` (graf) z ročního přebytku. Starší plán splátku do
 * kapacity nepočítá (staré plány se nepřepočítávají), graf ji ukáže jako dřív. */

/** Cíl, jehož koupě ukončí nájem: bydlení (ne rekonstrukce, auto, podnikání). */
const jeCilBydleni = (cil) => asText(cil?.typ) === "Bydlení";

/**
 * Nájem, který po koupi bydlení odpadne (Kč/měs) — JEN položka „Nájem“
 * z detailního rozpisu výdajů (`vydaje2.rozpis.najem.o`). Fond oprav a údržba
 * po koupi zůstávají.
 *
 * NEZJISTITELNÝ NÁJEM = 0 (bod 9 dráhy splátky, 5. 10. 2026). Do té doby se
 * bez rozpisu brala celá souhrnná kategorie `vydaje2.detail.bydleni`. Ta je
 * u starších analýz z rychlého rozpisu „Bydlení (nájem / SVJ / fond)“
 * (AnalyzaV2Page `VYDAJE_KAT`) a u rozpisu po „Použít“ součet nájmu, údržby,
 * fondu oprav a ostatního bydlení (`KATEGORIE_POLOZKY`) — kolik z ní je nájem,
 * se zjistit nedá. U vlastníka bytu by koupě „ukončila“ fond oprav a plán by
 * od koupě slíbil peníze, které rodina dál platí. Nevymýšlí se: bez položky
 * Nájem nic neodpadá. Platí pro plán metodiky 2026-10-05 (solver, graf
 * a karta úvěru); starý plán kreslí graf postaru (`cashflowHypotekaPostaru`).
 */
export function najemPredKoupi(analyza) {
  const rozpis = asObj(asObj(analyza?.vydaje2).rozpis);
  return Math.max(0, Math.round(cislo(asObj(rozpis.najem).o, 0)));
}

/**
 * Úvěr cíle jako událost v čase.
 * @param {object} cil  cíl ve tvaru výstupu solveru (`id`, `typ`, `hypoVybrana`, `hypoParametry`)
 * @param {{odMes?: number}} o  měsíc čerpání od dneška (0 = hned)
 * @returns {{cilId, bydleni, odMes, splatkaMes, doMes, splatkaStavebkoMes, doMesStavebko, najemMes}|null}
 *   `doMes` = konec splatnosti úvěru (splátka do té doby), `najemMes` doplní
 *   `priradNajemKUverum`.
 */
export function uverCileVCase(cil, { odMes = 0 } = {}) {
  const v = cil?.hypoVybrana;
  if (!v || typeof v !== "object") return null;
  const od = Math.max(0, Math.round(cislo(odMes, 0)));
  const splatnost = cislo(cil?.hypoParametry?.splatnostLet, 0) > 0 ? +cil.hypoParametry.splatnostLet : SPLATNOST_HYPO_VYCHOZI;
  const st = v.uverStavebko && typeof v.uverStavebko === "object" ? v.uverStavebko : null;
  const rokyStavebko = st && cislo(st.roky, 0) > 0 ? +st.roky : SPLATNOST_STAVEBKO_UVER;
  return {
    cilId: cil?.id ?? null,
    bydleni: jeCilBydleni(cil),
    odMes: od,
    splatkaMes: Math.max(0, Math.round(cislo(v.splatkaHypo, 0))),
    doMes: od + Math.round(splatnost * 12),
    splatkaStavebkoMes: st ? Math.max(0, Math.round(cislo(st.splatka, 0))) : 0,
    doMesStavebko: od + Math.round(rokyStavebko * 12),
    najemMes: 0,
  };
}

/**
 * Nájem odpadne JEDNOU — u nejdřívějšího úvěru na bydlení (druhá nemovitost
 * nájem podruhé neukončí). Vrací nové pole, vstup nemění.
 */
export function priradNajemKUverum(uvery, najemMes) {
  const pole = asArr(uvery).filter((u) => u && typeof u === "object").map((u) => ({ ...u, najemMes: 0 }));
  const n = Math.max(0, Math.round(cislo(najemMes, 0)));
  if (!(n > 0)) return pole;
  let prvni = null;
  for (const u of pole) if (u.bydleni && (prvni == null || u.odMes < prvni.odMes)) prvni = u;
  if (prvni) prvni.najemMes = n;
  return pole;
}

/**
 * O kolik úvěry v měsíci `mIdx` ZMENŠÍ volné peníze: splátky běžících úvěrů
 * (od čerpání do konce splatnosti) minus nájem, který koupě ukončila (ten
 * odpadá natrvalo). Záporné číslo = nájem byl vyšší než splátka.
 */
export function zatizeniUvery(uvery, mIdx) {
  let z = 0;
  for (const u of asArr(uvery)) {
    if (!u || !(mIdx >= cislo(u.odMes, Infinity))) continue;
    if (mIdx < cislo(u.doMes, Infinity)) z += cislo(u.splatkaMes, 0);
    if (mIdx < cislo(u.doMesStavebko, Infinity)) z += cislo(u.splatkaStavebkoMes, 0);
    z -= cislo(u.najemMes, 0);
  }
  return z;
}

/** Měsíc čerpání úvěru podle výstupu solveru: cíl „hned“ = 0, jinak konec horizontu. */
const mesicCerpani = (g) => (g?.hned ? 0 : Math.max(1, Math.min(ROKY_STROP, Math.round(cislo(g?.roky, 0)))) * 12);

/**
 * Úvěry plánu jako události v čase (graf, karta cíle).
 * Plán metodiky 2026-10-05 nese u cíle PŘESNĚ tu událost, se kterou solver
 * krátil kapacitu (`uverVCase`). Starší plán ji nemá (splátku do kapacity
 * nepočítá) — složí se z karty cíle týmiž funkcemi, jak ji graf ukazoval dřív.
 */
export function uveryZeSolveru(sol, analyza) {
  const cile = asArr(sol?.cile);
  const zeSolveru = cile.map((g) => g?.uverVCase).filter((u) => u && typeof u === "object");
  if (zeSolveru.length) return zeSolveru;
  const slozene = cile.filter((g) => g?.hypo && g?.hypoVybrana).map((g) => uverCileVCase(g, { odMes: mesicCerpani(g) }));
  return priradNajemKUverum(slozene, najemPredKoupi(analyza));
}

/**
 * Pravidelné investice plánu v čase — Kč/měs na začátku každého roku 0..N.
 * Každý cíl jen do svého termínu, se stupňovitým navýšením. Dřív graf odečítal
 * SOUČET dnešních investic po celou dobu — i akontaci bytu po koupi, kdy už
 * se na ni nespoří (u Čunga −35 724 místo −15 127 Kč/měs od 2031).
 * @param {object} sol      výstup solveCile
 * @param {object} [efPrav] efektivní měsíční částky podle id cíle (plneniCilu.efektivniPravidelne)
 */
export function investiceVCase(sol, efPrav = null, { horizontRoky = 30 } = {}) {
  const N = Math.floor(cislo(horizontRoky, 0));
  const out = [];
  for (let rok = 0; rok <= N; rok++) out.push(0);
  const ef = asObj(efPrav);
  for (const g of asArr(sol?.cile)) {
    if (!g || typeof g !== "object") continue;
    const zaklad = cislo(ef[g.id] ?? g.pravidelne, 0);
    const H = Math.max(1, Math.min(ROKY_STROP, Math.round(cislo(g.roky, 0))));
    /* Konec vkladů jako v solveru: „hned“ = dospoření, rezerva aspoň rok. */
    const doMes = g.hned ? cislo(g.dosporit?.mesicu, 0) : g.rezerva ? Math.max(12, H * 12) : H * 12;
    for (let rok = 0; rok <= N; rok++) {
      const m = rok * 12;
      if (m >= doMes) break;
      let v = zaklad;
      for (const k of asArr(g.pravidelneKroky)) if (m >= cislo(k?.odMes, Infinity)) v += cislo(k?.castkaMes, 0);
      out[rok] += v;
    }
  }
  return out.map((v) => Math.round(v));
}

/**
 * GRAF STARÉHO PLÁNU POSTARU (bod 7 dráhy splátky, 5. 10. 2026).
 * Plán s razítkem starším než metodika 2026-10-05 drží svá čísla, dokud
 * poradce neklikne „Přepočítat podle nové metodiky“ — i v grafu „Cashflow
 * v čase“, který klient vidí. Tohle je přesně podklad grafu před tou metodikou
 * (HEAD 8f7f92e3, Návrh i karta cíle): jeden úvěr, splátka bez konce
 * splatnosti, nájem = celá kategorie `vydaje2.detail.bydleni` (i u auta),
 * investice stejné po celou dobu (bez `investiceRoky`).
 * @param {object} g      cíl z výstupu solveru (`hypo`, `hypoVybrana`)
 * @param {object} analyza data v2
 * @param {number} odRoku rok čerpání — Návrh `round(g.roky)`, karta svůj `H`
 */
export function cashflowHypotekaPostaru(g, analyza, odRoku) {
  if (!g || !g.hypo || !g.hypoVybrana) return null;
  const najemMes = +analyza?.vydaje2?.detail?.bydleni || 0;
  return {
    odRoku,
    splatkaMes: +g.hypoVybrana.splatkaHypo || 0,
    splatkaStavebkoMes: +g.hypoVybrana.uverStavebko?.splatka || 0,
    najemMes,
    odpadaNajem: najemMes > 0,
  };
}

/**
 * Podklad grafu „Cashflow v čase“ z výstupu solveru: celý plán (Návrh), nebo
 * jen úvěr jednoho cíle (`cilId`, karta cíle). Bez úvěru `null` — graf se
 * neukáže. `hypoteka` = první úvěr ve starém tvaru (tečka „nástup hypotéky“),
 * `uvery` = všechny, se kterými řada počítá.
 * `metodika` = verze plánu (`verzePlanu(navrh)`): starší než 2026-10-05 →
 * graf postaru (`cashflowHypotekaPostaru`, výsledek nese `postaru: true`).
 * Graf plánu nové metodiky nese `seZnamenkem: true` — řada pak počítá
 * volné peníze TÝŽ funkcí jako kapacita solveru (`volnePredCili`).
 */
export function reseniCashflowZeSolveru(sol, analyza, { cilId = null, metodika } = {}) {
  if (metodika !== undefined && !platiZmena(metodika, METODIKA_2026_10_05)) {
    const cile = asArr(sol?.cile);
    const hg = cile.find((g) => g?.hypo && g?.hypoVybrana && (cilId == null || String(g.id) === String(cilId)));
    if (!hg) return null;
    const odRoku = Math.round(+hg.roky || 0);
    return { horizontRoky: Math.max(15, odRoku + 10), postaru: true, reseni: { hypoteka: cashflowHypotekaPostaru(hg, analyza, odRoku) } };
  }
  const vse = uveryZeSolveru(sol, analyza);
  const uvery = cilId == null ? vse : vse.filter((u) => String(u.cilId) === String(cilId));
  if (!uvery.length) return null;
  const prvni = uvery.reduce((a, u) => (u.odMes < a.odMes ? u : a), uvery[0]);
  const odRoku = Math.floor(prvni.odMes / 12);
  return {
    horizontRoky: Math.max(15, odRoku + 10),
    reseni: {
      hypoteka: {
        odRoku, splatkaMes: prvni.splatkaMes, splatkaStavebkoMes: prvni.splatkaStavebkoMes,
        najemMes: prvni.najemMes, odpadaNajem: prvni.najemMes > 0,
      },
      uvery,
      seZnamenkem: true,
    },
  };
}

/**
 * Podklad grafu „Cashflow v čase“ CELÉHO PLÁNU — jedno místo pro Návrh
 * (graf a nápis nad ním) i pro prezentaci (nápis o schodku). Dřív to Návrh
 * skládal ve vlastním `useMemo`; prezentace by ho musela opsat a dvě kopie
 * = dvě čísla v den, kdy se jedna opraví.
 * @param {object} sol     výstup solveCile (týmiž volbami jako obrazovka)
 * @param {object} analyza data v2
 * @param {object} [efPrav] efektivní měsíční částky (plneniCilu.efektivniPravidelne)
 * @param {{metodika?: string}} [o] verze plánu (`verzePlanu(navrh)`)
 * @returns {{horizontRoky: number, reseni: object}|null} null = plán bez úvěru
 */
export function grafCashflowPlanu(sol, analyza, efPrav = null, { metodika } = {}) {
  const cf = sol ? reseniCashflowZeSolveru(sol, analyza, { metodika }) : null;
  if (!cf) return null;
  const investiceMes = Object.values(asObj(efPrav)).reduce((a, v) => a + (+v || 0), 0);
  return {
    horizontRoky: cf.horizontRoky,
    reseni: {
      ...cf.reseni,
      investiceMes,
      /* Investice po letech (každý cíl jen do svého termínu) — jen nová
       * metodika; starý plán odečítá dnešní součet po celou dobu (postaru). */
      ...(cf.postaru ? {} : { investiceRoky: investiceVCase(sol, efPrav, { horizontRoky: cf.horizontRoky }) }),
      /* Cena ochrany — graf ukazuje, co zbude PO plánu, a ochranu plán platí
       * z přebytku první (solver ji odečetl dřív než investice). */
      pojistneMes: sol?.pojistne?.znama ? sol.pojistne.mes : null,
    },
  };
}

/**
 * NÁPIS NAD GRAFEM = GRAF (bod 6 dráhy splátky, 5. 10. 2026).
 * `sol.schodekUveru` počítá jen splátku nad rozpočet. Když jsou cíle zadané
 * ručně nad rozpočet, graf je hlouběji (Čung v DB: nápis „chybí 10 721“, graf
 * −15 127 = splátka 10 721 + ruční cíle 4 406). Nápis teď ukáže CELKOVÉ číslo
 * jako graf; rozpad (splátka × ruční cíle) jde do ⓘ.
 * Celkem v měsíci = max(splátka nad rozpočet, −graf v roce, který ten měsíc
 * začíná). Graf je po letech, takže jen měsíce na začátku roku berou graf;
 * jinak (a za koncem grafu) platí samotná splátka.
 * OCHRANA NAD PŘEBYTEK (průchod Návrhem 5. 10. 2026): co na pojistné chybí
 * (`ochranaVMesici` → `chybiNaOchranu`), je v grafu i v nápisu — v rozpadu
 * jako vlastní část (`ochranaMes`), ne jako „cíle zadané ručně“. Klíče
 * `ochranaMes` / `maxOchranaMes` jen když na ochranu něco chybí.
 * @param {object} schodek `sol.schodekUveru`
 * @param {Array} rada `cashflowTimeline` grafu plánu (`grafCashflowPlanu`)
 * @param {function} [ochranaVMesici] (měsíc) → Kč/měs chybějící na ochranu
 * @returns {null|{odMes, chybiMes, uverMes, rucniMes, maxOdMes, maxChybiMes, maxUverMes, maxRucniMes, ochranaMes?, maxOchranaMes?}}
 */
export function schodekSGrafem(schodek, rada, ochranaVMesici = null) {
  const s = asObj(schodek);
  if (!(cislo(s.chybiMes, 0) > 0)) return null;
  const odMes = Math.max(0, Math.round(cislo(s.odMes, 0)));
  const kroky = asArr(s.kroky).filter((k) => k && Number.isFinite(+k.odMes));
  const uverVMesici = (m) => {
    if (m === odMes) return Math.round(+s.chybiMes);
    let v = 0;
    for (const k of kroky) if (+k.odMes <= m) v = Math.round(cislo(k.chybiMes, 0));
    return v;
  };
  const roky = new Map(asArr(rada).filter((r) => r && Number.isFinite(+r.rok)).map((r) => [+r.rok, r]));
  const vMesici = (m) => {
    const uver = uverVMesici(m);
    const r = m % 12 === 0 ? roky.get(m / 12) : null;
    const graf = r && cislo(r.prebytekMes, 0) < 0 ? -Math.round(+r.prebytekMes) : 0;
    const ochrana = typeof ochranaVMesici === "function" ? Math.max(0, Math.round(cislo(ochranaVMesici(m), 0))) : 0;
    const celkem = Math.max(uver + ochrana, graf);
    return { celkem, uver, ochrana, rucni: celkem - uver - ochrana };
  };
  const mesice = new Set([odMes, ...kroky.map((k) => Math.round(+k.odMes)), ...[...roky.keys()].map((rok) => rok * 12)]);
  const prvni = vMesici(odMes);
  let max = { m: odMes, ...prvni };
  for (const m of [...mesice].filter((x) => x > odMes).sort((a, b) => a - b)) {
    const v = vMesici(m);
    if (v.celkem > max.celkem) max = { m, ...v };
  }
  return {
    odMes, chybiMes: prvni.celkem, uverMes: prvni.uver, rucniMes: prvni.rucni,
    maxOdMes: max.m, maxChybiMes: max.celkem, maxUverMes: max.uver, maxRucniMes: max.rucni,
    ...(prvni.ochrana > 0 || max.ochrana > 0 ? { ochranaMes: prvni.ochrana, maxOchranaMes: max.ochrana } : {}),
  };
}

/**
 * Nápis o schodku pro Návrh i prezentaci — TÝŽ graf, TÝŽ výpočet.
 * @returns viz `schodekSGrafem`; null = plán splátku utáhne (nebo bez úvěru)
 */
export function schodekNadGrafem(sol, analyza, efPrav = null, { metodika } = {}) {
  if (!sol?.schodekUveru) return null;
  const g = grafCashflowPlanu(sol, analyza, efPrav, { metodika });
  const rada = g ? cashflowTimeline(analyza, g.reseni, { horizontRoky: g.horizontRoky }) : [];
  /* Co v měsíci chybí na ochranu — TÁŽ funkce jako graf (`chybiNaOchranu`). */
  const pritoky = asArr(analyza?.pritoky);
  const ochranaVMesici = g && !g.postaru && g.reseni?.pojistneMes != null
    ? (m) => {
      let mzda = 0;
      for (const p of pritoky) if (p?.typ === "mzda" && m >= (+p.odRoku || 0) * 12) mzda += +p.castkaMes || 0;
      return chybiNaOchranu(sol.zdroje, mzda, g.reseni.pojistneMes);
    }
    : null;
  return schodekSGrafem(sol.schodekUveru, rada, ochranaVMesici);
}

/**
 * Roční řada přebytku domácnosti — časově správné cashflow.
 * Zohledňuje: splátku hypotéky AŽ od roku čerpání, od téhož roku odpadající
 * nájem (výdajová kategorie bydlení, lze přebít najemMes) a budoucí přítoky mzdy.
 * @param {object} reseni { hypoteka: {odRoku, splatkaMes, splatkaStavebkoMes?, najemMes?, odpadaNajem?},
 *                          uvery? (úvěry v čase, `uveryZeSolveru` — mají přednost před `hypoteka`),
 *                          investiceMes? (pravidelné investice z plánu, stejné po celou dobu),
 *                          investiceRoky? (investice po letech, `investiceVCase` — mají přednost) }
 * @returns pole [{rok, prebytekMes, zmeny: [text]}]
 */
export function cashflowTimeline(analyza, reseni = {}, { horizontRoky = 30 } = {}) {
  const zdroje = rozdelZdroje(analyza);
  const pritoky = asArr(analyza?.pritoky);
  const hypo = reseni?.hypoteka || null;
  /* Starý tvar bez `najemMes` = graf postaru: celá kategorie bydlení (jako
   * před metodikou 2026-10-05). Nová metodika nájem posílá vždy (`uvery`). */
  const najemMes = hypo?.najemMes != null ? +hypo.najemMes : +analyza?.vydaje2?.detail?.bydleni || 0;
  /* Graf plánu metodiky 2026-10-05 (`reseniCashflowZeSolveru` → `seZnamenkem`):
   * volné peníze roku TÝŽ funkcí jako kapacita solveru (`volnePredCili`) —
   * záporný přebytek se ořízne až po růstu mzdy a ceně ochrany, ne před nimi
   * (bod 8 dráhy splátky). Neznámý přebytek a starý plán počítají postaru. */
  const seZnamenkem = reseni?.seZnamenkem === true && !zdroje.prebytekNeznamy;
  const odpadaNajem = hypo ? hypo.odpadaNajem !== false : false;
  /* Úvěry jako události v čase — TÁŽ funkce `zatizeniUvery` jako kapacita
   * solveru. Starý tvar `hypoteka` (jeden úvěr bez konce splatnosti) se převede. */
  const uvery = Array.isArray(reseni?.uvery)
    ? reseni.uvery
    : hypo
      ? [{
        cilId: null, bydleni: true, odMes: Math.ceil(cislo(hypo.odRoku, 0)) * 12,
        splatkaMes: +hypo.splatkaMes || 0, doMes: Infinity,
        splatkaStavebkoMes: +hypo.splatkaStavebkoMes || 0, doMesStavebko: Infinity,
        najemMes: odpadaNajem ? +najemMes || 0 : 0,
      }]
      : [];
  const investiceMes = +reseni?.investiceMes || 0;
  const investiceRoky = Array.isArray(reseni?.investiceRoky) && reseni.investiceRoky.length ? reseni.investiceRoky : null;
  const investiceVRoce = (rok) => (investiceRoky ? cislo(investiceRoky[Math.min(rok, investiceRoky.length - 1)], 0) : investiceMes);
  /* CENA OCHRANY (rozhodnutí majitele 29. 9. 2026): plán ji platí z přebytku
   * DŘÍV než cíle, takže v řadě „kolik zbude" musí být odečtená stejně jako
   * pravidelné investice. Neznámá cena (`null`) se neodečítá — viz
   * `souhrnPojistneho`. Bez klíče se řada nemění ani o korunu.
   * Odečítá se jako v solveru (`cap = max(0, přebytek + přítoky − cena)`):
   * nejvýš tolik, kolik přebytek v tom roce je — dražší pojištění nevyrobí
   * záporný přebytek, řekne se to varováním u rozpočtu.
   * ROZHODNUTÍ 30. 9. 2026 bod 2: `pojistneMes` je ROZDÍL (nová − rušená stará
   * smlouva) a smí být ZÁPORNÝ — levnější nová smlouva přebytek zvětší. Neznámý
   * přebytek ale uvolněním nevyroste (číslo, které nemáme, se nezvětšuje). */
  /* Záporný rozdíl nejdřív kryje schodek (`rozdilPojistnehoEfektivni`) — týž
   * výpočet jako solver, ať řada „kolik zbude“ nevyrábí fantomové peníze. */
  const pojistneMes = rozdilPojistnehoEfektivni(zdroje, reseni?.pojistneMes);

  const rows = [];
  for (let rok = 0; rok <= horizontRoky; rok++) {
    const investiceRok = investiceVRoce(rok);
    let prebytek = zdroje.prebytekMes - investiceRok;
    const zmeny = [];
    for (const p of pritoky) {
      if (p.typ === "mzda" && rok >= +p.odRoku) {
        prebytek += +p.castkaMes || 0;
        if (rok === +p.odRoku) zmeny.push(`+${Math.round(+p.castkaMes || 0)} Kč/měs — ${p.poznamka || "růst mzdy"}`);
      }
      if (p.typ === "jednorazovy" && rok === +p.odRoku) {
        zmeny.push(`jednorázový přítok ${Math.round(+p.castka || 0)} Kč — ${p.poznamka || "mimořádný příjem"}`);
      }
    }
    if (seZnamenkem) {
      /* Totéž jako solver: kapacita = max(0, volné), splátka nad ni je schodek
       * (graf jde pod nulu přesně o něj), nájem vyšší než splátka nejdřív kryje
       * schodek domácnosti. Pak investice plánu (ruční nad rozpočet = níž). */
      let mzda = 0;
      for (const p of pritoky) if (p?.typ === "mzda" && rok >= (+p.odRoku || 0)) mzda += +p.castkaMes || 0;
      const volne = volnePredCili(zdroje, mzda, reseni?.pojistneMes);
      const z = zatizeniUvery(uvery, rok * 12);
      /* OCHRANA NAD PŘEBYTEK SE NEZTRATÍ (průchod Návrhem 5. 10. 2026): ořez
       * `max(0, volné)` schovával i pojistné, na které přebytek nestačí — graf
       * ukázal −9 000 místo −10 150. Co na ochranu chybí (`chybiNaOchranu`,
       * TÁŽ funkce jako souhrn a rozpočet), jde pod nulu; nájem vyšší než
       * splátka ho nejdřív pokryje (v ořezu `max(0, volné − z)` už je). */
      const chybiOchrana = Math.max(0, chybiNaOchranu(zdroje, mzda, reseni?.pojistneMes) - Math.max(0, -z));
      prebytek = (z >= 0 ? Math.max(0, volne) - z : Math.max(0, volne - z)) - chybiOchrana - investiceRok;
    } else {
      if (pojistneMes > 0) prebytek -= Math.min(pojistneMes, Math.max(0, prebytek + investiceRok));
      else if (pojistneMes < 0) prebytek -= pojistneMes;
      prebytek -= zatizeniUvery(uvery, rok * 12);
    }
    for (const u of uvery) {
      if (!u) continue;
      const splatka = cislo(u.splatkaMes, 0) + cislo(u.splatkaStavebkoMes, 0);
      if (Math.floor(cislo(u.odMes, -1) / 12) === rok) {
        zmeny.push(`−${Math.round(splatka)} Kč/měs ${u.bydleni ? "splátka hypotéky" : "splátka úvěru"} (od čerpání)`);
        if (cislo(u.najemMes, 0) > 0) zmeny.push(`+${Math.round(u.najemMes)} Kč/měs — odpadá nájem`);
      }
      if (Number.isFinite(+u.doMes) && +u.doMes > +u.odMes && Math.floor(+u.doMes / 12) === rok && cislo(u.splatkaMes, 0) > 0) {
        zmeny.push(`+${Math.round(u.splatkaMes)} Kč/měs — úvěr splacen`);
      }
    }
    rows.push({ rok, prebytekMes: Math.round(prebytek), zmeny });
  }
  return rows;
}

/* ===== 2. Přiřazování zdrojů k cílům ===== */

/* `asText`: popis/typ uložený jako objekt se dřív testoval jako "[object Object]".
 * Exportované, protože podle NICH musí cíle třídit i planEngine. Dřív měl vlastní
 * pravidlo (`g.mesicne` pro rentu, jen `popis` pro rezervu), takže cíl typu
 * „Finanční nezávislost" bez příznaku `mesicne` byl pro solver renta a pro engine
 * běžný cíl — a plán ho ukázal dvakrát, pokaždé s jinou cílovou částkou. */
export const jeRezerva = (g) => /rezerv/i.test(asText(g?.popis)) || /rezerv/i.test(asText(g?.typ));
export const jeRenta = (g) => !!g?.mesicne || /nez[áa]vislost|renta/i.test(asText(g?.typ));

/**
 * CÍLOVÁ ČÁSTKA DOPOČÍTANÉ REZERVY — jedno místo pro solver i planEngine.
 *
 * STARÁ CHYBA (dráha F3, 4. 10. 2026): analýza ukazovala rezervu přesně podle
 * vzorce („(40 000 nezbytné + 22 000 splátky) × 6 = 372 000 Kč“), plán ji
 * zaokrouhlil po 10 000 na 370 000 — dvě čísla k jedné rezervě a vzorec, který
 * poradce čte klientovi nahlas, neseděl s plánem. U eurového tenanta (kurz 1)
 * to bylo horší: 6 × (1 000 + 120) € = 6 720 € se v plánu zaokrouhlilo na
 * 10 000 €.
 *
 * METODIKA 2026-10-05: cíl rezervy = vzorec analýzy na celou korunu (euro),
 * BEZ zaokrouhlení po krocích. Plán se starším razítkem zaokrouhluje dál po
 * `krok` (10 000 / kurz) — staré plány se nepřepočítávají.
 *
 * @param {{surova: number, zaklad: number, krok?: number, metodika?: string}} v
 *   `surova` = `rezervaCastka(...)`, `zaklad` = nezbytné výdaje + splátky
 * @returns {number} 0 = cíl rezervy neexistuje (bez výdajů a splátek)
 */
export function cilRezervyPlanu({ surova, zaklad, krok = 10000, metodika } = {}) {
  if (!(+zaklad > 0) || !Number.isFinite(+surova)) return 0;
  if (platiZmena(metodika, METODIKA_2026_10_05)) return Math.max(0, Math.round(+surova));
  const k = Number.isFinite(+krok) && +krok > 0 ? +krok : 10000;
  return Math.max(k, Math.round(+surova / k) * k);
}

/* ===== 2b. Přepis přidělení zdroje na cíle (`prideleniZdroje`) =====
 *
 * Diktát majitele: „Vždycky to jde do renty, ale aby byla možnost to upravovat."
 * Tohle je ta možnost — poradce určí, na KTERÝ cíl jde konkrétní zdroj.
 *
 * ⚠️ PROČ TO MUSÍ EXISTOVAT (skutečná vada, ne teorie): klient se dvěma cíli
 * typu renta dostal do OBOU celé penzijko — tatáž koruna se slíbila dvakrát
 * (800 000 v DPS → plán tvrdil, že rozdělil 1 600 000). Rozpočet to od té doby
 * hlásí jako přečerpání, ale opravit to ručně nešlo. S přepisem jde penzijko
 * rozdělit (`{ penzijko: { c2: 300000, c3: 500000 } }`) nebo přiřadit jednomu
 * (`{ penzijko: "c3" }`).
 *
 * BEZ PŘEPISU SE CHOVÁNÍ NEMĚNÍ — ani o bajt. Prázdná/chybějící mapa nesmí
 * sáhnout na jediné číslo (hlídá fixture-guard v testech).
 */

/** Zdroje, které smí poradce ručně přidělit na konkrétní cíle.
 * `termin` (termínovaný vklad) od Č20 — čerpá ho jen cíl „hned“ a rezerva.
 * `fondy` (podílové fondy + životko s hodnotou) od metodiky 2026-10-05 — bez
 * přepisu jdou do první renty (viz „FONDY A INVESTIČNÍ ŽIVOTKO“ v solveCile);
 * starší plán je nečerpá vůbec, takže přepis u něj nic nezmění. */
export const ZDROJE_PRIDELENI = ["sporici", "bezny", "hotovost", "termin", "stavebko", "penzijko", "fondy"];

/** Label vkladu z fondů a životka s hodnotou (planSolver je bez jazyka —
 * obrazovky ho překládají klíčem `zdrojNazev.fondy`, lib/nazevZdroje.js). */
export const LABEL_FONDY = "fondy a investiční životko";

/* ── FONDY NEJDŘÍV DO CHYBĚJÍCÍ REZERVY (metodika 2026-10-05) ───────────────
 * Majitel 5. 10. 2026 (dotazník): „Když klientovi chybí rezerva a zároveň má
 * fondy/ETF: co s nimi má plán udělat nejdřív?“ → „Nejdřív doplnit rezervu“ —
 * „Z fondů se doplní chybějící rezerva (pevný základ), zbytek jde do renty.“
 *  · JEN CHYBĚJÍCÍ ČÁST: rezerva si nejdřív bere své dosavadní zdroje (účty,
 *    u rezervy „hned“ i termínovaný vklad a stavebko) a z fondů jen to, co
 *    pořád chybí — nikdy víc. Zbytek fondů jde dál jako dosud (první renta).
 *  · JEN LIKVIDNÍ ČÁST: podílové fondy, ETF a akcie (`majetek.opf`) se prodají
 *    během dní. Investiční životko NE — appka ho vede jako dlouhodobé
 *    (`souhrny.pasmaMajetku`: penzijko + KŽP, Návrh: „zamčené v produktech,
 *    nutno rušit“) a pole v analýze je „Odkupné“: peníze až po výpovědi
 *    smlouvy, se ztrátou daňové výhody. Do rezervy proto nejde nikdy.
 *  · Penzijko ani stavebko se tímhle pravidlem neřídí (vázané produkty).
 *  · Přepis poradce (`prideleniZdroje.fondy`) má přednost i tady.
 * Řádek nese `fondy: true` (peníze z poolu fondů — rozpočet i tok peněz je
 * počítají jako fondy) a `doRezervy: true`; obrazovky ho překládají klíčem
 * `zdrojNazev.fondyRezerva` („doplní se z fondů“). */
export const LABEL_FONDY_REZERVA = "doplní se z fondů";
/** Řádek „doplní se z fondů“ u rezervy (metodika 2026-10-05). */
export const jeFondyDoRezervy = (j) => j?.doRezervy === true && j?.fondy === true;

/* ── CÍL „HNED“ (0 let) — Č20, majitel 4. 10. 2026 ──────────────────────────
 * Doslova: „co je to za blbost cil chci splnit hned.. a musi se investovat 10 k
 * kdyz to chce mit ted hned.. to musí se investovat vybrat treba stavebko nebo
 * brat ze stavebko bude rezerva“.
 *
 * DO 4. 10. 2026 se rezerva „hned“ dorovnávala z přebytku DO ROKA: 300 000 −
 * 170 000 ze zdrojů = 130 000 / 12 = 10 834 Kč/měs, karta pak ukazovala
 * „hned (realisticky) 301 931 Kč“ (projekce na rok s výnosem hotovosti) a
 * ostatní cíle dostaly o 10 834 Kč menší kapacitu — u auta za 5 let po celých
 * 5 let (minCap bere minimum přes celý horizont). Čung to smazal a ono se to
 * vrátilo, protože solver ruční čísla u rezervy ignoroval.
 *
 * TEĎ: cíl s horizontem 0 se kryje JEN JEDNORÁZOVĚ ze zdrojů v pořadí
 * `PORADI_OKAMZITE` (běžný → spořicí → hotovost → termínovaný vklad →
 * stavební spoření; nic nelikvidního — penzijko, investice ani nemovitost
 * pro „hned“ nejsou). Totéž pořadí platí pro povinnou rezervu „hned“.
 * Rezerva s horizontem 1 rok a víc bere zdroje JAKO DŘÍV (jen účty: spořicí →
 * hotovost → běžný) a zbytek dospoří z přebytku do horizontu — zúžení
 * 4. 10. 2026: Čung mluvil o cíli „hned“, stavebko a termínovaný vklad pro
 * rezervu s horizontem nechtěl (kontrola: auto přišlo o 130 000 ze stavebka).
 *
 * PLÁN S METODIKOU PŘED 4. 10. 2026 (volba `metodika`, lib/metodikaPlanu.js)
 * počítá všechno tohle PŘESNĚ jako dřív: „hned“ nezná (cíl s horizontem 0 je
 * cíl na rok), rezerva jen z účtů a z přebytku do roka, ruční nula ani
 * dospoření do výpočtu nejdou.
 * Když zdroje nestačí, solver vrátí `chybi` a NÁVRH dospoření
 * (`dosporitNavrh`) — ale měsíční částku sám NEPŘIDĚLÍ. Dospoří se jen tehdy,
 * když to poradce zvolí (`options.dosporitHned[idCile] = počet měsíců`).
 * Výsledek „hned“ je hotovost bez výnosu: kolik má klient k dispozici teď
 * (+ dospoření), žádných 101 % z projekce na rok. */
export const PORADI_OKAMZITE = ["bezny", "sporici", "hotovost", "termin", "stavebko"];
/** Nejdelší dospoření, které solver u cíle „hned“ přijme / navrhne (10 let). */
export const DOSPORIT_MAX_MES = 120;

/** Počet měsíců dospoření zvolený poradcem, nebo `null` (nezvoleno / nesmysl). */
export function dosporitMesicu(v) {
  if (v === null || v === undefined || v === "" || typeof v === "boolean") return null;
  const n = Math.round(+v);
  return Number.isFinite(n) && n >= 1 ? Math.min(DOSPORIT_MAX_MES, n) : null;
}

/** Hotovost bez výnosu: `zaklad` hned + `mesicne` po `mesicu` měsíců. */
function projekceHotovosti(zaklad, mesicne, mesicu) {
  const M = mesicne > 0 ? Math.max(1, Math.round(mesicu || 0)) : 0;
  const roky = Math.max(1, Math.ceil(M / 12));
  const rada = [{ rok: 0, hodnota: Math.round(zaklad), vlozeno: Math.round(zaklad) }];
  for (let r = 1; r <= roky; r++) {
    const v = Math.round(zaklad + mesicne * Math.min(M, r * 12));
    rada.push({ rok: r, hodnota: v, vlozeno: v });
  }
  const konec = Math.round(zaklad + mesicne * M);
  return { rada, konec, vlozeno: konec };
}

/**
 * Normalizace `prideleniZdroje` na mapu klíč → pravidlo.
 * Přijímané tvary hodnoty (pro každý klíč zvlášť):
 *   "c3"                     → celý zdroj smí čerpat jen cíl c3
 *   ["c1","c3"]              → čerpat smí jen tyhle cíle, jinak vodopád beze změny
 *                              (prázdné pole = zdroj se nepoužije nikde)
 *   { c2: 300000, c3: 500000 } → strop v Kč pro každý cíl zvlášť
 * @returns {Map<string, {rezim:"vse"|"castky", cile:Set<string>, castky:Map<string,number>|null}>}
 */
function normalizujPrideleni(prid) {
  const out = new Map();
  if (!prid || typeof prid !== "object") return out;
  for (const klic of ZDROJE_PRIDELENI) {
    const v = prid[klic];
    if (v === undefined || v === null || v === "") continue;
    if (typeof v === "string") {
      out.set(klic, { rezim: "vse", cile: new Set([v]), castky: null });
    } else if (Array.isArray(v)) {
      out.set(klic, { rezim: "vse", cile: new Set(v.map(String)), castky: null });
    } else if (typeof v === "object") {
      const castky = new Map();
      for (const [cid, c] of Object.entries(v)) {
        const n = zadanaCastka(c); // NaN/nesmysl → null → klíč se zahodí, ne 0 „naslepo"
        if (n != null) castky.set(String(cid), n);
      }
      out.set(klic, { rezim: "castky", cile: new Set(castky.keys()), castky });
    }
  }
  return out;
}

/**
 * Normalizace `bezZdroju` (Č20 oprava) na Map(idCile → Set(klíč zdroje)).
 * Neznámé klíče, prázdná pole i rozbitý tvar se zahodí — bez platného záznamu
 * je mapa prázdná a solver jede bajt po bajtu jako dřív.
 */
function normalizujBezZdroju(bez) {
  const out = new Map();
  if (!bez || typeof bez !== "object" || Array.isArray(bez)) return out;
  for (const [cilId, klice] of Object.entries(bez)) {
    if (!Array.isArray(klice)) continue;
    const s = new Set(klice.map(String).filter((k) => ZDROJE_PRIDELENI.includes(k)));
    if (s.size > 0) out.set(String(cilId), s);
  }
  return out;
}

/**
 * Generátor řešení cílů: rezerva VŽDY první; jednorázové vklady jen z likvidních
 * (spořák → hotovost → běžný; běžný pro ostatní cíle až po naplnění rezervy);
 * REZERVA I CÍL S HORIZONTEM 0 („hned“) čerpají v pořadí `PORADI_OKAMZITE` (běžný →
 * spořicí → hotovost → termínovaný vklad → stavebko) a cíl „hned“ se kryje JEN
 * jednorázově — dospoření z přebytku jen na volbu poradce (`dosporitHned`, Č20);
 * rezerva s horizontem ≥ 1 rok bere jen účty (spořicí → hotovost → běžný) jako dřív;
 * plán s metodikou před 4. 10. 2026 (`metodika`) počítá celé tohle postaru;
 * stavebko použitelné od 4. měsíce (mimo rezervu); penzijko (DPS/DIP) celé do renty,
 * ale připsané až v roce dosažení VEK_PENZIJKO; co likvidní nepokryjí, řeší pravidelná
 * investice z přebytku (vč. budoucích přítoků mzdy).
 *
 * PŘEPISY PORADCE (plán si smí hrát s čísly, bez skoku zpět do analýzy):
 *  - cilOverride:      { [idCile]: { cilova?, roky? } } — přepíše cílovou ČÁSTKU a horizont.
 *                      Horizont se propíše i do alokaceProCil → glide path patří novému horizontu.
 *                      `cilova` je vždy finální kapitál (u renty tedy kapitál, ne měsíční renta).
 *  - rezim:            { [idCile]: "jednorazove"|"mesicne"|"oboji" } — režim řešení
 *  - jednorazoveRucni: { [idCile]: castka } — ručně přibitá jednorázovka (měsíc 0)
 *  - pravidelneRucni:  { [idCile]: castka } — ručně přibitá pravidelná investice Kč/měs
 *  - prideleniZdroje:  { [klicZdroje]: "idCile" | ["idCile"] | { [idCile]: castka } }
 *                      — na KTERÝ cíl jde konkrétní zdroj (viz `normalizujPrideleni`).
 *                      Klíče: sporici | bezny | hotovost | termin | stavebko | penzijko
 *                      | fondy (fondy a životko s hodnotou, jen metodika ≥ 2026-10-05).
 *                      Jediné místo, kde se penzijko smí poslat i mimo rentu, a jediné,
 *                      kde se čerpá z POOLU — takže dva cíle renty už si ho nerozdají
 *                      dvakrát. Časování (peníze dorazí ve VEK_PENZIJKO) se NEMĚNÍ.
 *  - dosporitHned:     { [idCile]: mesicu } — cíl „hned“, který zdroje nepokryjí,
 *                      se dospoří z přebytku za tolik měsíců (volba poradce, Č20).
 *  - bezMesicni:       { [idCile]: true } — poradce u cíle bez režimu nechce měsíční
 *                      investici (nula); kapacita zůstane dalším cílům (Č20 oprava).
 *  - bezZdroju:        { [idCile]: [klicZdroje] } — zdroje odebrané u cíle (Č20 oprava).
 *  - pravidelneZadane: { [idCile]: Kč/měs } — ruční kladná měsíční částka u cíle bez
 *                      režimu; platí ve výpočtu i tisku (balík 4. 10. 2026 bod 10,
 *                      jen metodika ≥ 2026-10-05).
 *  - zeme, dnes:       země tenanta (bez volby globální store) a dnešek — pravidlo
 *                      akontace podle věku v roce koupě a NBS (body 7 a 8, ≥ 2026-10-05).
 *  - metodika:         razítko verze plánu (lib/metodikaPlanu.js). Bez volby = aktuální;
 *                      „puvodni“ (uložený plán bez razítka) = výpočet před 4. 10. 2026:
 *                      bez „hned“, rezerva z účtů + z přebytku do roka, `bezMesicni`
 *                      a `dosporitHned` se ignorují. `bezZdroju` platí v obou (je to
 *                      výslovný klik poradce, uložené plány ho nemají).
 * Bez přepisu se cíl počítá PŘESNĚ jako dosud (zpětná kompatibilita).
 * U povinné likvidní rezervy platí `cilOverride` (poradce smí chtít 3× místo 6×),
 * ale NE režim/ruční čísla — rezerva je cash bez glide path, dopočet nemá co řešit.
 *
 * VOLBY METODIKY (obojí VYPNUTÉ do dosavadního chování, aby uložené plány držely
 * svá čísla — přepnutí je „zásah poradce" a ukládá se do `navrh.solver`):
 *  - zdrojeDoRenty:        stavebko i penzijko míří na DLOUHÝ CÍL (renta, jinak
 *                          nejdelší cíl) místo do vodopádu; penzijko se čerpá
 *                          z poolu, takže dva cíle renty si ho nerozdají dvakrát.
 *  - rezervaMesicu:        na kolik měsíců je dopočítaná rezerva (3 / 6 / 12;
 *                          6 = dosavadní vzorec, tedy výchozí stav).
 *
 * @param {object} options { varianta, profil, vek, splatnostHypo, horizontKapacityLet,
 *   cilOverride, rezim, jednorazoveRucni, pravidelneRucni, zdrojeDoRenty,
 *   rezervaMesicu }
 * @returns { cile: [...], zdroje, zbyva, rezervaOK, celkemJednorazove, celkemPravidelne }
 *   `celkemJednorazove` = VŠE, co jde do plánu hned (zdroje klienta + ručně přibitý vklad).
 *   `celkemJednorazoveRucni` = kolik z toho NENÍ z dnešních zdrojů (undefined, když 0).
 *   `celkemPenzijko` = penzijko (DPS/DIP) přidělené do renty; do `celkemJednorazove`
 *     se nepočítá, protože dorazí až ve VEK_PENZIJKO (undefined, když 0).
 *   `celkemFondy` = fondy a životko s hodnotou přidělené cílům (metodika
 *     2026-10-05, výchozí = první renta); do `celkemJednorazove` se nepočítají,
 *     protože už pracují (klíč chybí, když 0).
 * Per cíl: { id, popis, typ, roky, cilova, jednorazove: [{zdroj, castka, mesic}],
 *   pravidelne, pravidelneKroky, alokace, projekce, splneno, chybi, stavPo, pozn }
 *   `pravidelneKroky[]` = { odMes, castkaMes, duvody: [{duvod:"cil"|"mzda", popis, cilId}] }
 *     — stupňovité navýšení pravidelné investice. `duvody` říká, PROČ kapacita
 *     v tom měsíci stoupla (dospořený cíl / budoucí přítok mzdy); může být
 *     prázdné, pak se o příčině nic netvrdí.
 * Při přepisu navíc: { rezim, dopocitano, nedosazitelne, nadKapacitu, kapacitaMes }
 */
/* Peníze, které jdou do plánu HNED, versus peníze, které teprve doletí.
 * Predikát je exportovaný, aby ho `lib/plneniCilu.js` (a přes něj planEngine)
 * použil pro tentýž součet, jaký ukazuje prezentace — jinak by věta „jednorázově
 * vložíme X" měla na dvou obrazovkách dvě čísla. */
export const jePenzijko = (j) => j?.penzijko === true || /penzijko/.test(j?.zdroj || "");
/* Fondy a životko s hodnotou (metodika 2026-10-05): peníze klienta, které UŽ
 * pracují — nic se „nevkládá“ ani „nekupuje“, jen se přiřadí k rentě. Proto
 * stojí mimo `jeVkladDnes` stejně jako penzijko (věta „jednorázově vložíme X“
 * a řádek „koupit za X“ by jinak slibovaly nový nákup za peníze, které už
 * investované jsou) a solver je vrací zvlášť jako `celkemFondy`. */
export const jeFondy = (j) => j?.fondy === true || /^fondy a investi/.test(j?.zdroj || "");
export const jeVkladDnes = (j) => !!j && !jePenzijko(j) && !jeFondy(j) && !/přítok/.test(j.zdroj || "");

/* ── CENA OCHRANY SE PLATÍ Z PŘEBYTKU DŘÍV NEŽ CÍLE ──────────────────────────
 * Rozhodnutí majitele 29. 9. 2026: „jo, musí se odečíst".
 *
 * DO TÉHLE CHVÍLE solver rozdělil na cíle CELÝ volný přebytek a měsíční cenu
 * pojištění ukazovala appka jen vedle, pro informaci. U vzorové rodiny
 * (přebytek 8 000 Kč, ochrana ≈ 8 720 Kč) tak plán poslal 8 000 Kč do cílů
 * A ZÁROVEŇ doporučil pojištění za 8 720 Kč — klient by měsíčně platil
 * o 8 720 Kč víc, než má.
 *
 * CENU SOLVER NEPOČÍTÁ. Dostane ji hotovou (`options.pojistneMes`) z jediného
 * zdroje, který používá karta v Návrhu i papír: `lib/cenaOchrany.js`
 * (skládá ji `plneniCilu.pojistneProPlan`). Druhý výpočet ceny tady by byl
 * přesně ta vada, kterou `cenaOchrany.js` zavírá (karta 8 720 × papír 3 000).
 *
 * TŘI STAVY, ŽÁDNÝ NEVYMÝŠLÍ:
 *   · klíč vůbec nepřišel (`undefined`) → volající o ceně nic neví, plán se
 *     počítá jako dřív a výstup NENESE `pojistne` (bajt po bajtu stejný),
 *   · cena NEZNÁMÁ (`null`, 0, nesmysl) → NEODEČÍTÁ SE, ale výstup to řekne
 *     (`znama: false`) — obrazovka pak napíše, že plán s pojištěním nepočítá.
 *     Nezadané není nula a nula není cena (týž pravidlo jako OnePagePlanPage),
 *   · cena známá → odečte se z každého měsíce kapacity; když je vyšší než
 *     přebytek, na cíle jde 0 (žádná záporná kapacita) a `nadPrebytek` to
 *     obrazovce řekne nahlas.
 *
 * ROZHODNUTÍ MAJITELE 30. 9. 2026 bod 2 (METODIKA 2026-10): do solveru chodí
 * ROZDÍL POJISTNÉHO = nová cena od poradce − platba rušené staré smlouvy
 * (ta je už ve výdajích klienta). Rozdíl smí být 0 (nová stojí tolik co stará)
 * i ZÁPORNÝ (nová je levnější → přebytek NAROSTE). Neznámá cena (`null`, „“,
 * nesmysl) se dál neodečítá; ruční nula od poradce chodí jako `duvod: "nula"`
 * (viz `plneniCilu.pojistneDoSolveru`), ne jako rozdíl 0.
 * Šablonový odhad sem nechodí vůbec — plán odečítá JEN cenu od poradce. */
function cenaOchranyMes(v) {
  if (v === null || v === undefined || v === "" || typeof v === "boolean" || Array.isArray(v)) return null;
  const n = +v;
  return Number.isFinite(n) ? n : null;
}

/**
 * O KOLIK SE PŘEBYTEK OPRAVDU ZMĚNÍ — jediné místo pro solver, souhrn i řadu
 * cash flow (kontrola 2. 10. 2026, „fantomové peníze“).
 *   · kladný rozdíl (nová dražší) → beze změny; ořez na přebytek dělá volající,
 *   · ZÁPORNÝ rozdíl (uvolněná dražší stará smlouva) NEJDŘÍV KRYJE SCHODEK:
 *     přebytek se znaménkem −3 000 + uvolněno 2 000 = −1 000 → na cíle 0
 *     (dřív 0 + 2 000 = 2 000 na cíle u rodiny, která je v minusu),
 *   · neznámý přebytek se uvolněním nezvětšuje (číslo, které nemáme, neroste).
 * @param {object} zdroje výstup `rozdelZdroje`
 * @param {number} rozdil rozdíl pojistného Kč/měs (nová − rušená stará)
 * @returns {number} efektivní rozdíl (≤ 0 = kolik přibude na cíle)
 */
export function rozdilPojistnehoEfektivni(zdroje, rozdil) {
  const r = cenaOchranyMes(rozdil);
  if (r == null) return 0;
  if (r >= 0) return r;
  const z = asObj(zdroje);
  if (z.prebytekNeznamy) return 0;
  const orez = Math.max(0, cislo(z.prebytekMes, 0));
  const znamenko = cenaOchranyMes(z.prebytekSeZnamenkemMes);
  const zaklad = znamenko == null ? orez : znamenko;
  const navic = Math.max(0, zaklad - r) - Math.max(0, zaklad);
  return navic > 0 ? -navic : 0;
}

/**
 * VOLNÉ PENÍZE MĚSÍCE PŘED CÍLI A ÚVĚRY — metodika 2026-10-05 (bod 8 dráhy
 * splátky, 5. 10. 2026). Jedna funkce pro kapacitu solveru i graf „Cashflow
 * v čase“ (`cashflowTimeline` s `seZnamenkem`).
 *
 * STARÁ CHYBA: záporný přebytek se ořízl na nulu DŘÍV, než se přičetl růst
 * mzdy. Rodina v minusu 5 000 Kč/měs s růstem mzdy +5 000 dostala na cíle
 * 5 000, přitom jí zbývá 0 — a schodek na splátku hypotéky vyšel o 5 000
 * menší (24 721 místo 29 721). Teď: přebytek SE ZNAMÉNKEM + růst mzdy −
 * rozdíl pojistného, ořez na nulu dělá až volající. Záporný rozdíl pojistného
 * (levnější nová smlouva) tak nejdřív kryje schodek sám od sebe.
 * Bez růstu mzdy vyjde max(0, …) na korunu jako dosud (`rozdilPojistnehoEfektivni`
 * dělá pro dnešek totéž).
 * @param {object} zdroje výstup `rozdelZdroje`
 * @param {number} mzdaMes součet přítoků mzdy platných v tom měsíci
 * @param {number|null} pojistneMes rozdíl pojistného (nová − rušená), null = neznámý
 * @returns {number|null} volné Kč/měs (smí být záporné); null = přebytek
 *   neznámý → volající počítá postaru (neznámé číslo se uvolněním nezvětšuje)
 */
export function volnePredCili(zdroje, mzdaMes, pojistneMes) {
  const z = asObj(zdroje);
  if (z.prebytekNeznamy) return null;
  const znamenko = cenaOchranyMes(z.prebytekSeZnamenkemMes);
  const zaklad = znamenko == null ? Math.max(0, cislo(z.prebytekMes, 0)) : znamenko;
  return zaklad + cislo(mzdaMes, 0) - (cenaOchranyMes(pojistneMes) ?? 0);
}

/**
 * KOLIK NA OCHRANU V MĚSÍCI CHYBÍ — jedna funkce pro řadu „Cashflow v čase“,
 * nápis o schodku (rozpad v ⓘ), souhrn pojistného i běžící rozpočet plánu
 * (průchod Návrhem 5. 10. 2026, metodika 2026-10-05).
 *
 * STARÁ CHYBA: ochrana dražší než přebytek se ořízla na přebytek. Souhrn znal
 * `chybiMes` (rodina z průchodu: ochrana 15 000, přebytek 13 850 → chybí
 * 1 150), ale graf ukázal −9 000 místo −10 150 a Průběžný přehled, rozpočet
 * v prezentaci i Shrnutí „zbývá −9 000“ — chybějící peníze se tiše ztratily.
 * Teď je to číslo, které se na obrazovkách s měsíčním rozpočtem odečte navíc.
 *
 * Vlastní schodek domácnosti (výdaje nad příjmy) tu NENÍ — ten hlásí rozpočet
 * jinde; tady jen pojistné, které plán přidal a na které přebytek nestačí.
 * Kladný rozdíl (nová dražší než rušená) se porovná s volnými penězi měsíce
 * PŘED pojistným (`volnePredCili` bez ceny, ořez na nulu); záporný rozdíl
 * (uvolnění) nikdy nic nedluží. Neznámý přebytek → 0 (nic se netvrdí).
 * @param {object} zdroje výstup `rozdelZdroje`
 * @param {number} mzdaMes součet přítoků mzdy platných v tom měsíci
 * @param {number|null} pojistneMes rozdíl pojistného (nová − rušená)
 * @returns {number} Kč/měs ≥ 0
 */
export function chybiNaOchranu(zdroje, mzdaMes, pojistneMes) {
  const p = cenaOchranyMes(pojistneMes);
  if (p == null || !(p > 0)) return 0;
  const zaklad = volnePredCili(zdroje, mzdaMes, null);
  if (zaklad == null) return 0;
  return Math.max(0, p - Math.max(0, zaklad));
}

/* PROČ SE NEODEČÍTÁ — jen u NEZNÁMÉ ceny, ať obrazovka řekne pravý důvod
 * (do 29. 9. 2026 měly všechny tři stavy jednu větu „Cena pojištění nezadaná"):
 *   · `odmitnuto` — klient v analýze odpověděl „Ne“ na zajištění příjmu
 *                   (`d.souhlas.rizika`). Plán cenu NEODEČÍTÁ; „Později“ je
 *                   jiná odpověď a odečítá se (návrh, o kterém se ještě mluví).
 *                   Od 5. 10. 2026 jen STARÝ plán (otázka z analýzy odešla,
 *                   plneniCilu.ochranaOdmitnuta hlídá razítko metodiky),
 *   · `nula`      — poradce zapsal ruční cenu 0 (smlouvy nebo všech osob).
 *                   Cena JE zadaná, jen se z ní nic neodečte,
 *   · bez důvodu  — cenu opravdu neznáme (není koho ocenit). */
export const POJISTNE_DUVOD = Object.freeze({ odmitnuto: "odmitnuto", nula: "nula" });
const jeDuvodPojistneho = (v) => v === POJISTNE_DUVOD.odmitnuto || v === POJISTNE_DUVOD.nula;

/**
 * Souhrn „přebytek → pojištění → cíle" pro obrazovku (měsíc 0, bez přítoků).
 * Jediné místo, kde vzniká věta „z toho pojištění X · na cíle Y".
 * @param {object} zdroje výstup `rozdelZdroje`
 * @param {*} pojistneMes ROZDÍL pojistného Kč/měs (nová − rušená stará; null =
 *   neznámý; smí být 0 i záporný — rozhodnutí 30. 9. 2026 bod 2)
 * @param {boolean|null} odhad je cena (aspoň zčásti) šablonový odhad? Od
 *   30. 9. 2026 se odhad neodečítá, takže z plánu chodí vždy `false`; `true`
 *   jen u výslovného příznaku (starší volání).
 * @param {string|null} duvod proč se neodečítá (`POJISTNE_DUVOD`), jen u neznámé ceny
 * @param {object} [extra] `{ novaMes, uvolnenoMes }` — nová cena od poradce a platba
 *   rušené staré smlouvy, ať obrazovka umí „dnes 3 000 → nově 6 000 (+3 000)“
 */
export function souhrnPojistneho(zdroje, pojistneMes, odhad = null, duvod = null, extra = null) {
  const z = asObj(zdroje);
  const prebytek = Math.max(0, cislo(z.prebytekMes, 0));
  const cena = duvod === POJISTNE_DUVOD.odmitnuto ? null : cenaOchranyMes(pojistneMes);
  if (cena == null) {
    return {
      znama: false, mes: null, odhad: null, prebytekMes: Math.round(prebytek),
      odectenoMes: 0, naCileMes: Math.round(prebytek),
      nadPrebytek: false, chybiMes: 0, prebytekNeznamy: !!z.prebytekNeznamy,
      /* Klíč jen se známým důvodem — bez něj je výstup bajt po bajtu jako dřív. */
      ...(jeDuvodPojistneho(duvod) ? { duvod } : {}),
    };
  }
  /* Záporný rozdíl (rušená stará smlouva dražší než nová) PŘIDÁ na cíle —
   * ale jen ke ZNÁMÉMU přebytku a nejdřív zaplatí schodek
   * (`rozdilPojistnehoEfektivni`, kontrola 2. 10. 2026). */
  const efektivni = rozdilPojistnehoEfektivni(z, cena);
  const odecteno = efektivni > 0 ? Math.min(efektivni, prebytek) : efektivni;
  const ex = asObj(extra);
  const cisloNebo = (v) => (v !== undefined && v !== null && v !== "" && Number.isFinite(+v) ? Math.round(+v) : null);
  const novaMes = cisloNebo(ex.novaMes);
  const uvolnenoMes = cisloNebo(ex.uvolnenoMes);
  /* Chybějící peníze na ochranu (`chybiNaOchranu`, TÁŽ funkce jako graf). */
  const chybi = chybiNaOchranu(z, 0, cena);
  /* VÝMĚNA STARÉ MAJETKOVÉ SMLOUVY ZA NOVOU s cenou od poradce (průchod Návrhem
   * 5. 10. 2026, `lib/nahradaMajetku.js`): je už v rozdílu `cena`; tady jen
   * obě strany, ať věta „Pojistné: dnes → nově“ sedí (`parPojistneho`). */
  const maj = asObj(ex.majetek);
  const majStara = cisloNebo(maj.staraMes);
  const majNova = cisloNebo(maj.novaMes);
  return {
    znama: true,
    /* `mes` = ROZDÍL (to, o co se přebytek mění) — `prebytekMes − mes = naCileMes`
     * platí dál. Nová cena a rušená platba jsou zvlášť (`novaMes`, `uvolnenoMes`). */
    mes: Math.round(cena),
    rozdilMes: Math.round(cena),
    ...(novaMes != null ? { novaMes } : {}),
    ...(uvolnenoMes != null ? { uvolnenoMes } : {}),
    ...(majStara != null && majNova != null ? { majetekStaraMes: majStara, majetekNovaMes: majNova } : {}),
    /* KOLIK POJISTNÉ UBERE Z MĚSÍČNÍHO ROZPOČTU (metodika 2026-10-05): celý
     * rozdíl i s tím, na co přebytek nestačí — běžící rozpočet (Průběžný
     * přehled, Rozpočet plánu, prezentace, Shrnutí) pak ukáže „zbývá −10 150“,
     * ne ořezaných −9 000. Klíč jen když se liší od `odectenoMes` (chybí
     * peníze) a jen u nové metodiky — jinak výstup bajt po bajtu jako dřív. */
    ...(chybi > 0 && platiZmena(ex.metodika, METODIKA_2026_10_05) ? { vRozpoctuMes: Math.round(odecteno + chybi) } : {}),
    /* Zadaná jen ČÁST ceny (u části osob / bez dětské smlouvy). Klientské
     * výstupy ji pak nesmí tisknout jako cenu celé ochrany (kontrola 2. 10. 2026). */
    ...(ex.cast === true ? { cast: true } : {}),
    odhad: odhad === true,
    prebytekMes: Math.round(prebytek),
    odectenoMes: Math.round(odecteno),
    naCileMes: Math.round(Math.max(0, prebytek - odecteno)),
    /* „Dražší než přebytek" se tvrdí jen o ZNÁMÉM přebytku. U neznámého
     * (chybí výdaje) je přebytek 0 jen proto, že ho neznáme — a věta
     * „pojištění je dražší, než na co máte" by byla údaj o klientovi,
     * který appka nemá. */
    nadPrebytek: !z.prebytekNeznamy && cena > prebytek + 0.5,
    chybiMes: Math.round(chybi),
    prebytekNeznamy: !!z.prebytekNeznamy,
  };
}

export function solveCile(analyza, options = {}) {
  /* Sběrač polí, která dorazila v jiném TVARU — solver z nich pak nic nespočítá
   * a jeho nuly by na plátně vypadaly jako údaj. Vylezou ven v `dataProblemy`. */
  const sber = sberProblemu();
  const {
    varianta = "realisticky",
    profil = "vyvazeny",
    /* NASTAVENÍ → METODIKA PLÁNU (19. 10. 2026, `navrh.metodikaVolby` přes
     * metodikaPlanu.metodikaDoSolveru). Platí jen pod razítkem 2026-10-18:
     * `posunLet` 0–5 = „Dynamičtější“ alokace (sloupec tabulky o N let dál),
     * `kapitalRenty` „poLetech“ = kapitál renty portfoliem po letech čerpání
     * (`kapitalRentyPoLetech`) místo pravidla 4 %. Bez volby beze změny. */
    posunLet = 0,
    kapitalRenty = "pravidlo4",
    /* Výchozí úrok hypotéky plánu (desetinně). Nový plán nese aktuální tržní
     * sazbu v `navrh.metodikaVolby.sazbaHypoPct` (28. 10. 2026); bez ní 4,9 %
     * jako dřív. Sazba zadaná u cíle (`cil.sazbaHypoPct`) má přednost. */
    sazbaHypo: sazbaHypoVolba = SAZBA_HYPO,
    /* Věk klienta. NEPOSLANÝ (`undefined`) se bere z data narození v analýze.
     * Neznámý věk se pro HYPOTÉKU nevymýšlí — viz `vekProHypo` níž. */
    vek: vekVolba,
    /* Splatnost hypotéky pro celý plán. Cíl, u kterého poradce vyplnil vlastní
     * splatnost (`splatnostHypoLet`), si ji přebíjí — viz `splatnostHypoCile`. */
    splatnostHypo = SPLATNOST_HYPO_VYCHOZI,
    horizontKapacityLet = 50,
    cilOverride = {},
    rezim = {},
    jednorazoveRucni = {},
    pravidelneRucni = {},
    prideleniZdroje = {},
    /* DOSPOŘENÍ CÍLE „HNED“ — volba poradce, ne automatika (Č20):
     * `{ [idCile]: počet měsíců }`. Bez klíče se cíl „hned“ kryje jen ze zdrojů. */
    dosporitHned = {},
    /* BEZ MĚSÍČNÍ INVESTICE — rozhodnutí poradce u cíle bez režimu (Č20 oprava,
     * 4. 10. 2026): `{ [idCile]: true }`. Skládá ho `plneniCilu.solverOptionsZeSolveru`
     * z měkkého přepisu `prav[id] = 0` (křížek / smazané pole v kartě cíle).
     * Do téhle opravy šla nula jen do karty a rozpočtu Návrhu — solver dál
     * přiděloval svůj návrh, a Plný plán, 1 strana i Shrnutí tiskly „Řešení:
     * 6 390 Kč měsíčně … 100 %“ u cíle, kde to poradce smazal. Teď cíl dostane
     * 0 Kč/měs (jen zdroje), kapacita zůstane volná pro další cíle a ve výstupu
     * je `pravidelneNavrh` = co by solver navrhl (tlačítko „návrh X“ v kartě). */
    bezMesicni = {},
    /* ZDROJE, KTERÉ PORADCE U CÍLE ODEBRAL (Č20 oprava): `{ [idCile]: [klic…] }`.
     * Vyloučení, ne seznam povolených cílů — cíl přidaný později má k zdroji
     * přístup jako dřív (dřív se ukládal snímek ostatních cílů do
     * `prideleniZdroje` a nový cíl zdroj tiše neviděl). */
    bezZdroju = {},
    /* RUČNĚ PŘEPSANÁ MĚSÍČNÍ ČÁSTKA u cíle bez režimu (bod 10 balíku 4. 10.
     * 2026, metodika 2026-10-05): `{ [idCile]: Kč/měs }` (kladné číslo; nulu
     * nese `bezMesicni`). Skládá ho `plneniCilu.solverOptionsZeSolveru`
     * z měkkého přepisu `prav[id]`. Do téhle změny platilo číslo jen v kartě
     * a rozpočtu Návrhu — Plný plán, 1 strana, Shrnutí, prezentace i odkaz
     * /m/ tiskly návrh systému. Teď ho cíl dostane ve výpočtu (a kapacitu
     * bere/uvolní pro další cíle); starší plán ho ignoruje. */
    pravidelneZadane = {},
    /* VLASTNÍ RIZIKOVÝ PROFIL CÍLE (dráha F3, 4. 10. 2026, metodika
     * 2026-10-05): `{ [idCile]: "dynamicky" | "vyvazeny" | "konzervativni" }`
     * z `navrh.solver.profilCil` (přepínač Profil v kartě cíle). Do téhle změny
     * ho znala jen karta v Návrhu — počítala si s ním vlastní projekci, a tisk,
     * Schod po schodu i prezentace jely s profilem plánu: u téhož cíle dvě
     * procenta. Teď ho dostane cíl ve výpočtu (glide path i potřebná částka);
     * starší plán ho ignoruje. Rezerva a cíl „hned“ se neinvestují — bez vlivu. */
    profilCil = {},
    /* ZEMĚ TENANTA pro pravidlo akontace (SK = NBS). Bez volby globální store
     * appky (`zemeTenanta`), ze kterého se bere i měna. */
    zeme = undefined,
    /* Dnešek (testy, ověřovací skripty) — jen pro rok koupě u akontace. */
    dnes = undefined,
    /* METODIKA PLÁNU (razítko verze, lib/metodikaPlanu.js). Bez volby =
     * aktuální metodika. Uložený plán bez razítka sem posílá „puvodni“ a
     * solver pak počítá jako před 4. 10. 2026 — pravidlo majitele 3. 10.:
     * staré plány se nepřepočítávají, dokud poradce neklikne „Přepočítat“. */
    metodika = undefined,
    /* Granularita zaokrouhlení automatického cíle rezervy. Je to JEDINÁ měnová
     * konstanta v solveru — planEngine ji posílá jako `10000 / rate`, aby cíl
     * rezervy vyšel v obou modulech stejně i mimo koruny (v appce je rate vždy 1,
     * viz lib/tenantMena.js). Bez toho parametru měl EUR tenant cíl rezervy
     * zaokrouhlený po 10 000 €. */
    rezervaKrok = 10000,
    /* VOLBA MAJITELE: dlouhé zdroje (stavebko, penzijko) míří na dlouhý cíl.
     * `false` = dosavadní vodopád, aby uložené plány držely svá čísla.
     * Viz hlavička „DLOUHÉ ZDROJE" u `VEK_PENZIJKO`. */
    zdrojeDoRenty = false,
    /* Na kolik měsíců je dopočítaná rezerva (majitel 25. 9. 2026: „rezerva buď
     * 3 - 6 - 12 měsíců"). Počet platí na CELÝ základ, tedy nezbytné výdaje
     * i splátky. Výchozí `6` je TÁŽ hodnota, jakou doporučuje analýza
     * (`analyzaV2/doporuceniCile.REZERVA_MESICU_VYCHOZI`) — a zároveň dosavadní
     * vzorec (nezbytné + splátky) × 6, takže uzavřené plány se nehnou.
     * Volba poradce jde z `navrh.solver.rezervaMesicu`, ne odsud. */
    rezervaMesicu = REZERVA_MESICU_VYCHOZI,
    /* CENA OCHRANY Kč/měs — odečte se z přebytku DŘÍV, než se rozdělí na cíle.
     * Viz `souhrnPojistneho` výš (tři stavy: nepřišlo / neznámá / známá). */
    pojistneMes = undefined,
    /* Je ta cena odhad? (jen pro „≈" na obrazovce, na čísla nemá vliv) */
    pojistneOdhad = null,
    /* Proč se cena neodečítá (`POJISTNE_DUVOD`) — jen pro větu na obrazovce.
     * `odmitnuto` navíc odečet vypne i u poslané ceny: „Ne“ klienta vyhrává. */
    pojistneDuvod = null,
    /* Nová cena od poradce a platba rušené staré smlouvy — jen do souhrnu
     * pro obrazovku („dnes 3 000 → nově 6 000“); počítá se s `pojistneMes`. */
    pojistneNovaMes = null,
    pojistneUvolnenoMes = null,
    /* Nová cena je ZADANÁ ČÁST (ne celá domácnost) — jen pro popisek na
     * klientském výstupu (kontrola 2. 10. 2026); na čísla nemá vliv. */
    pojistneCast = false,
    /* Výměna staré majetkové smlouvy za novou s cenou od poradce
     * (`{ staraMes, novaMes }`, `lib/nahradaMajetku.js`) — je už v rozdílu
     * `pojistneMes`; jen do souhrnu pro větu „dnes → nově“, na čísla nemá vliv. */
    pojistneMajetek = null,
    /* DOFINANCOVÁNÍ Z MAJETKU (krok 3 karty cíle, 5. 10. 2026):
     * `{ [idCile]: { zpusob: "prodej"|"zastava"|"jiny", castka, nazev, majetekId } }`.
     * Jen cíl na úvěr a jen metodika 2026-10-05; bez volby výpočet beze změny. */
    dofinancovani = {},
    /* VNITŘNÍ PRŮCHOD (splátka od čerpání, metodika 2026-10-05): `true` =
     * jen sesbírej úvěry cílů (`{ uvery }`), nic nerozděluj. Volá ho solver
     * sám, aby splátku odečetl z kapacity DŘÍV, než si ji vezme první cíl. */
    jenUvery = false,
  } = options;
  /* Nesmysl (NaN, 0, záporné, nad strop) = výchozí 4,9 %, ne úvěr zdarma. */
  const sazbaHypoPlanu = Number.isFinite(+sazbaHypoVolba) && +sazbaHypoVolba > 0 && +sazbaHypoVolba * 100 <= SAZBA_HYPO_MAX_PCT ? +sazbaHypoVolba : SAZBA_HYPO;
  /* ── NEZNÁMÝ VĚK SE U HYPOTÉKY NEVYMÝŠLÍ (2. 10. 2026) ──────────────────
   * Do téhle opravy stálo `vek = vekKlienta(analyza) ?? 35`: klient bez data
   * narození byl pro solver 35letý, hypoVarianty mu nabídla a SAMA VYBRALA
   * akontaci 10 % (výjimka do 36 let) — u bytu za 5 mil. vlastní zdroje
   * 500 000 místo 1 000 000. Lišta kalkulaček přitom ve stejné situaci říká
   * „věk nezadán“ a počítá s 20 %.
   * Teď: neznámý věk = žádné věkové pravidlo. Varianta 10 % se nenabízí,
   * bez volby poradce platí standardních 20 % a cíl nese `vekNezadan: true`,
   * aby Návrh napsal „Doplňte věk klienta“. U ZNÁMÉHO věku je výpočet
   * bajt po bajtu stejný jako dřív (klíč `vekNezadan` pak ve výstupu není).
   * ⚠️ Načasování penzijka (dostupné v 60) zůstává na dosavadním `?? 35` —
   * oprava se týká jen výběru varianty hypotéky, logika plánu jinak stojí. */
  const vekZnamy = vekVolba !== undefined ? vekVolba : vekKlienta(analyza);
  const vekNezadan = vekZnamy == null || vekZnamy === "" || !Number.isFinite(+vekZnamy);
  const vekProHypo = vekNezadan ? null : +vekZnamy;
  const vek = vekVolba !== undefined ? vekVolba : (vekKlienta(analyza) ?? 35);   // jen penzijko (beze změny)
  const rezKrok = Number.isFinite(+rezervaKrok) && +rezervaKrok > 0 ? +rezervaKrok : 10000;
  const doRenty = zdrojeDoRenty === true;
  /* Platí pro tenhle plán změny Č20 (4. 10. 2026)? Starý plán = ne. */
  const c20 = platiZmena(metodika, METODIKA_C20);
  /* Platí balík 4. 10. 2026 (metodika 2026-10-05)? Akontace podle věku v roce
   * koupě a NBS, ruční měsíční částka ve výpočtu, dospoření stupňovitě. */
  const c1005 = platiZmena(metodika, METODIKA_2026_10_05);
  /* Alokace podle tabulek Fichtner (metodika 2026-10-18, majitel 18. 10. 2026). */
  const c1018 = platiZmena(metodika, METODIKA_2026_10_18);
  /* Most do 60 (metodika 2026-10-28, majitel 28. 10. 2026): penzijko vyplacené až po
   * začátku renty se nezahodí — od výplaty zvýší rentu. */
  const c1028 = platiZmena(metodika, METODIKA_2026_10_28);
  const ktxAkontace = c1005 ? kontextAkontace(analyza, { zeme, dnes }) : null;

  const zdroje = rozdelZdroje(analyza);
  const pool = {
    sporici: zdroje.likvidni.sporici,
    bezny: zdroje.likvidni.bezny,
    hotovost: zdroje.likvidni.hotovost,
    stavebko: zdroje.stavebko.castka,
    /* Penzijko v poolu má smysl při přepisu přidělení a při `zdrojeDoRenty`.
     * Bez obojího se z poolu nečerpá (každý cíl renty dostane celou částku) —
     * a to se nemění. */
    penzijko: zdroje.penzijko.castka,
    /* Termínovaný vklad — čerpá ho jen cíl „hned“ a rezerva (`PORADI_OKAMZITE`). */
    termin: +zdroje.terminovane || 0,
    /* Fondy a životko s hodnotou (`zdroje.investice` = OPF/ETF/akcie + odkupné
     * KŽP/IŽP, jak je poradce zadal v analýze). Čerpá je jen plán s metodikou
     * 2026-10-05 — starší plán je nechává mimo, jako dřív (pool 0). */
    fondy: c1005 ? Math.max(0, cislo(zdroje.investice, 0)) : 0,
  };
  /* Kolik z poolu fondů smí jít do CHYBĚJÍCÍ REZERVY: jen podílové fondy, ETF
   * a akcie (`majetek.opf`) — investiční životko ne (viz `LABEL_FONDY_REZERVA`).
   * Mimo `pool`, aby se výstup `stavPo`/`zbyva` neměnil. Starší plán 0. */
  let fondyLikvidni = c1005 ? Math.max(0, Math.min(pool.fondy, sum(asObj(asObj(analyza).majetek).opf))) : 0;
  /** Kolik fondů už rezerva dostala (renta pak nese „zbytek po doplnění rezervy“). */
  let fondyDoRezervy = 0;

  const prideleni = normalizujPrideleni(prideleniZdroje);
  const vylouceno = normalizujBezZdroju(bezZdroju);
  /**
   * Strop čerpání zdroje `klic` pro cíl `cilId`.
   * undefined = poradce nic nepřepsal → chovej se PŘESNĚ jako dřív
   * null      = cíl smí čerpat bez stropu (vodofád beze změny)
   * číslo     = maximum v Kč (0 = z tohohle zdroje nedostane nic)
   */
  const stropZdroje = (klic, cilId) => {
    /* Zdroj odebraný u cíle (`bezZdroju`) má přednost — 0 Kč. */
    if (vylouceno.get(String(cilId))?.has(klic)) return 0;
    const p = prideleni.get(klic);
    if (!p) return undefined;
    const id = String(cilId);
    if (!p.cile.has(id)) return 0;
    return p.rezim === "castky" ? p.castky.get(id) ?? 0 : null;
  };
  const NAZVY_ZDROJU = { sporici: "spořicí účet", bezny: "běžný účet", hotovost: "hotovost", stavebko: "stavební spoření", termin: "termínovaný vklad" };

  // kapacita pravidelné investice po měsících (přebytek + budoucí přítoky mzdy)
  const maxMes = horizontKapacityLet * 12;
  const pritoky = sber.pole("pritoky", analyza?.pritoky);
  /* Cena ochrany jde z přebytku PRVNÍ — z každého měsíce kapacity, tedy i z
   * měsíců po růstu mzdy. Neznámá cena = 0 (neodečítá se, jen se to řekne). */
  const pojistneDano = pojistneMes !== undefined || jeDuvodPojistneho(pojistneDuvod);
  /* Rozdíl pojistného (nová − rušená) smí být záporný: pak kapacitu ZVĚTŠÍ —
   * ale jen u známého přebytku (neznámý se uvolněním nevyrábí). */
  /* … a nejdřív zaplatí schodek: rodina v minusu nedostane uvolněné peníze na
   * cíle, dokud jimi nepokryje vlastní schodek (`rozdilPojistnehoEfektivni`). */
  const pojistneKap = pojistneDuvod === POJISTNE_DUVOD.odmitnuto ? 0 : rozdilPojistnehoEfektivni(zdroje, pojistneMes);
  /* RŮST MZDY NEJDŘÍV KRYJE DNEŠNÍ SCHODEK (metodika 2026-10-05, bod 8 dráhy
   * splátky 5. 10. 2026): přebytek se znaménkem + růst mzdy − cena ochrany,
   * ořez na nulu až potom — `volnePredCili`, TÁŽ funkce jako graf. Dřív se
   * záporný přebytek ořízl dřív, než se přičetl růst mzdy (rodina v minusu
   * dostala celý růst na cíle). `capSurovy` = volné před ořezem (null =
   * postaru: starý plán nebo neznámý přebytek) — čte ho úvěr, jehož nájem
   * je vyšší než splátka. Bez růstu mzdy vyjde kapacita na korunu jako dřív. */
  const pojistneProVolne = pojistneDuvod === POJISTNE_DUVOD.odmitnuto ? 0 : pojistneMes;
  const capSurovy = new Array(maxMes);
  const cap = new Array(maxMes);
  for (let mIdx = 0; mIdx < maxMes; mIdx++) {
    let c = zdroje.prebytekMes;
    let mzda = 0;
    for (const p of pritoky) if (p.typ === "mzda" && mIdx >= (+p.odRoku || 0) * 12) { c += +p.castkaMes || 0; mzda += +p.castkaMes || 0; }
    const volne = c1005 ? volnePredCili(zdroje, mzda, pojistneProVolne) : null;
    capSurovy[mIdx] = volne;
    cap[mIdx] = volne == null ? Math.max(0, c - pojistneKap) : Math.max(0, volne);
  }
  const minCap = (doMes) => { let m = Infinity; for (let i = 0; i < Math.min(doMes, maxMes); i++) m = Math.min(m, cap[i]); return m === Infinity ? 0 : m; };
  const uberCap = (doMes, M) => { for (let i = 0; i < Math.min(doMes, maxMes); i++) cap[i] = Math.max(0, cap[i] - M); };

  /* DENÍK UVOLNĚNÍ KAPACITY — PROČ v daném měsíci stoupne volný přebytek.
   * Dvě příčiny, které se v kartě renty dosud tvářily stejně:
   *   1. dorazí budoucí přítok mzdy (`pritoky`, typ „mzda"),
   *   2. DOSPOŘÍ SE DŘÍVĚJŠÍ CÍL — `uberCap` ubírá kapacitu jen do horizontu
   *      cíle, takže od jeho konce se peníze vracejí do volné kapacity.
   * Renta se řeší poslední, takže vidí obojí. Bez téhle atribuce psala karta
   * u obou „+ navýšení z budoucích přítoků" — u druhého případu to nebyla pravda
   * a majitel o kaskádě po splnění cíle vůbec nevěděl.
   *
   * Deník nese jen DŮVOD a měsíc, ne částku: skutečný skok si `pravidelneKroky`
   * bere z `cap`, a u cíle placeného nad kapacitu domácnosti (`nadKapacitu`)
   * by se částka z deníku se skokem rozešla. Lhát nesmí ani vysvětlivka. */
  const uvolneni = [];
  for (const p of pritoky) {
    if (p.typ !== "mzda") continue;
    const odMes = Math.round(cislo(p.odRoku, 0) * 12);
    // přítok „od dneška" je součást plochého přebytku, žádný skok v čase nedělá
    if (odMes <= 0 || !(cislo(p.castkaMes, 0) > 0)) continue;
    uvolneni.push({ odMes, duvod: "mzda", popis: asText(p.poznamka), cilId: null });
  }
  /** Zapiš, že od `odMes` se vrací kapacita, kterou do té doby bral cíl `g`. */
  const zapisUvolneni = (odMes, castka, g) => {
    if (!(castka > 0) || !(odMes > 0) || odMes >= maxMes) return;
    uvolneni.push({ odMes: Math.round(odMes), duvod: "cil", popis: asText(g?.popis), cilId: g?.id ?? null });
  };
  /** Důvody skoku kapacity v daném měsíci (může být prázdné — pak jen částka). */
  const duvodyKroku = (odMes) => uvolneni
    .filter((u) => u.odMes === odMes)
    .map(({ duvod, popis, cilId }) => ({ duvod, popis, cilId }));

  /* ── DOSPOŘENÍ REZERVY STUPŇOVITĚ (bod 11 balíku 4. 10. 2026, metodika
   * 2026-10-05) ──────────────────────────────────────────────────────────────
   * Po kliku „dospořit rezervu za N měsíců“ bere dospoření kapacitu jen
   * N měsíců. Dřív dostal každý další cíl (mimo rentu) MINIMUM kapacity přes
   * celý svůj horizont — tedy i po N měsících jen to, co zbylo během
   * dospořování (auto 4 166 Kč/měs na 5 let, i když od 13. měsíce bylo volných
   * 15 000). Teď dostane od N-tého měsíce navíc uvolněné peníze (krok
   * `pravidelneKroky`), jen tolik, kolik cíl potřebuje. Jen po tomhle kliku:
   * měsíce uvolnění zapisuje větev `dosporitHned` (cíl „hned“). */
  const uvolneniDosporeni = new Set();
  /** Kroky z uvolněného dospoření pro cíl s horizontem `doMes` a základem `zaklad`. */
  const krokyZDosporeni = ({ zaklad, doMes, cilova, H, alokace, vklady }) => {
    const kroky = [];
    const mesice = [...uvolneniDosporeni].filter((m) => m > 0 && m < doMes).sort((a, b) => a - b);
    for (const M of mesice) {
      /* `cap` má základ cíle odečtený už z `uberCap` (proběhne dřív) — tady
       * se odečítají jen dřívější kroky. Dřív `zaklad + …` = základ dvakrát:
       * auto 800 000 dostalo krok 6 668 místo 10 834 a skončilo na 74 %. */
      const uz = kroky.reduce((a, k) => a + k.castkaMes, 0);
      let volno = Infinity;
      for (let i = M; i < Math.min(doMes, maxMes); i++) volno = Math.min(volno, cap[i]);
      volno = volno === Infinity ? 0 : Math.max(0, volno - uz);
      if (!(volno > 0.5)) continue;
      const konec = (x) => projekceCile({ horizontRoky: H, alokace, varianta, vklady, pravidelne: zaklad, kroky: [...kroky, { odMes: M, castkaMes: x }] }).konec;
      if (konec(0) >= cilova) break;                  // cíl je splněný už bez kroku
      let x = volno;
      if (konec(volno) >= cilova) {
        let lo = 0, hi = volno;
        for (let i = 0; i < 50; i++) { const mid = (lo + hi) / 2; if (konec(mid) < cilova) lo = mid; else hi = mid; }
        x = Math.min(volno, Math.ceil(hi));
      }
      x = Math.round(x);
      if (x > 0) kroky.push({ odMes: M, castkaMes: x, duvody: duvodyKroku(M) });
    }
    return kroky;
  };

  // cíle: rezerva první, pak dle priority/roků, renta poslední; bez rezervy ji doplníme
  const vstupni = sber.pole("cile", analyza?.cile).filter((g) => +g.hodnota > 0);
  const maRezervu = vstupni.some(jeRezerva);
  const splatky = sum(sber.pole("zavazkyB", analyza?.zavazkyB), "splatka")
    + sum(sber.pole("zavazkyO", analyza?.zavazkyO), "splatka");
  /* Nezbytné výdaje: zadané minimum, jinak TÝŽ součet, jaký zná analýza i plán
   * (max(obvyklé, rozpis) — `zdroje.vydajeMes`). Dřív tu byl fallback jen na
   * `obvykle`, takže klient s rozepsanými výdaji dostal cíl rezervy počítaný
   * POUZE ze splátek — na jedné obrazovce 90 000 Kč, na druhé 250 000 Kč. */
  const vydajeMin = Math.max(0, +analyza?.vydaje2?.minimalni || 0) || zdroje.vydajeMes;
  /* CÍL REZERVY BEZ VÝDAJŮ NEEXISTUJE. Do 8/2026 tu stálo `Math.max(1, …)`:
   * prázdná analýza dostala „Povinnou likvidní rezervu 1 Kč", jednu korunu ze
   * spořáku ji „naplnila" — a tím se odemkl běžný účet pro ostatní cíle
   * (`rezervaOK`). Tentýž fallback už z planEngine 8/2026 zmizel. */
  const rezervaZaklad = vydajeMin + splatky;
  /* Vzorec z `analyzaV2/doporuceniCile` — TÝŽ, jaký analýza ukazuje poradci.
   * `rezervaMesicu = 6` dá bajt po bajtu dosavadní `zaklad * 6`. */
  const rezervaSurova = rezervaCastka({ nezbytneVydaje: vydajeMin, splatky, mesicu: rezervaMesicu });
  const rezervaCil = cilRezervyPlanu({ surova: rezervaSurova, zaklad: rezervaZaklad, krok: rezKrok, metodika });
  const autoRezerva = { id: "auto-rezerva", popis: "Povinná likvidní rezerva", typ: "Rezerva", roky: 0, hodnota: rezervaCil, mesicne: false, auto: true };
  const serazene = [...(maRezervu ? vstupni : [autoRezerva, ...vstupni])].sort((a, b) => {
    const rank = (g) => (jeRezerva(g) ? -1e9 : jeRenta(g) ? 1e9 + (+g.roky || 0) : +(g.priorita ?? g.roky ?? 0));
    return rank(a) - rank(b);
  });
  /* KROK 3 — KAŽDÁ POLOŽKA MAJETKU JEN JEDNOU (dočištění 5. 10. 2026): auto
   * „prodané“ u Bydlení i u cíle Auto by snížilo oba cíle. Položka patří
   * prvnímu cíli na úvěr v tomto pořadí (= pořadí karet), další ji nepočítají.
   * Jen metodika 2026-10-05 — starší plán dofinancování nepočítá vůbec. */
  const vlastniciDof = c1005 && Object.keys(asObj(dofinancovani)).length > 0
    ? vlastniciMajetku(dofinancovani, serazene
      .filter((g) => !!g.uverem && !jeRezerva(g) && !jeRenta(g))
      .map((g) => ({ id: g.id, spotr: !jeHypotecniTypu(g.typ) })), analyza || {})
    : null;

  /* KAM MÍŘÍ DLOUHÉ ZDROJE (jen při `zdrojeDoRenty`). Přednost má RENTA — to
   * jsou majitelova slova („většinou se to investuje do renty na dlouhý cíl").
   * Když klient rentu zadanou nemá, vezme se cíl s NEJDELŠÍM horizontem:
   * nechat peníze ležet by znamenalo ukázat je jako nezapojené, a přesně to
   * majitel odmítl. Rezerva se do výběru nepočítá — ta je z definice krátká
   * a cash, stavebko ani penzijko do ní nepatří.
   * Při shodě horizontů vyhrává PRVNÍ v pořadí — aby výsledek nezáležel na
   * tom, jak seřadí `sort` dva cíle se stejným číslem. */
  const maRentu = serazene.some(jeRenta);
  const cilDlouhy = (() => {
    if (!doRenty || maRentu) return null;
    let nej = null;
    for (const g of serazene) {
      if (jeRezerva(g)) continue;
      if (!nej || (+g.roky || 0) > (+nej.roky || 0)) nej = g;
    }
    return nej;
  })();
  /** Smí tenhle cíl čerpat dlouhé zdroje (stavebko, penzijko)? */
  const smiDlouheZdroje = (g) => {
    if (!doRenty) return true;              // vodopád jako dosud
    if (maRentu) return jeRenta(g);
    return cilDlouhy != null && g.id === cilDlouhy.id;
  };

  /* ── SPLÁTKA ÚVĚRU OD ČERPÁNÍ (metodika 2026-10-05) ─────────────────────
   * Majitel 5. 10. 2026: „to nedává smysl … kolik bude dávat, kolik kam“
   * a u Bydlení „a pak zbytek kolik bude splátka“. Do téhle změny se po
   * naspoření akontace vrátila do kapacity celá částka a renta od roku koupě
   * počítala i s penězi, které ve skutečnosti spolkne splátka (Čung: renta
   * +18 000 Kč/měs od 2031, splátka 29 721 Kč/měs).
   * Teď: od měsíce čerpání se kapacita pro VŠECHNY cíle zmenší o splátku
   * (a zvětší o nájem, který koupě bydlení ukončí) — TÝMIŽ funkcemi jako graf
   * „Cashflow v čase“ (`zatizeniUvery`). Úvěry se sesbírají vnitřním průchodem
   * (`jenUvery`) z téhož výpočtu financování, který dává kartě splátku — a
   * odečtou se dřív, než si kapacitu vezme první cíl: splátka je závazek,
   * cíle se přizpůsobí jí, ne naopak. Na co splátka nestačí, to se neschová
   * do nuly: `schodekUveru` řekne, od kdy a kolik chybí. Starší plán beze změny. */
  const uveryNalezene = [];
  const uveryPlanu = c1005 && !jenUvery && vstupni.some((g) => g && g.uverem)
    ? priradNajemKUverum(asArr(solveCile(analyza, { ...options, jenUvery: true })?.uvery), najemPredKoupi(analyza))
    : [];
  const mapaUveru = new Map(uveryPlanu.map((u) => [String(u.cilId), u]));
  const schodekMes = [];
  if (uveryPlanu.length) {
    /* Nájem vyšší než splátka peníze uvolní — ale rodina v minusu jimi nejdřív
     * kryje vlastní schodek (stejně jako levnější pojištění, `rozdilPojistnehoEfektivni`). */
    const zakladSeZnamenkem = Number.isFinite(+zdroje.prebytekSeZnamenkemMes) ? +zdroje.prebytekSeZnamenkemMes : Math.max(0, cislo(zdroje.prebytekMes, 0));
    const uvolnitLze = (u) => (zdroje.prebytekNeznamy ? 0 : Math.max(0, zakladSeZnamenkem + u) - Math.max(0, zakladSeZnamenkem));
    for (let mIdx = 0; mIdx < maxMes; mIdx++) {
      const z = zatizeniUvery(uveryPlanu, mIdx);
      if (z > 0) {
        if (cap[mIdx] >= z) cap[mIdx] -= z;
        else { schodekMes[mIdx] = z - cap[mIdx]; cap[mIdx] = 0; }
      } else if (z < 0) {
        /* Volné peníze toho měsíce (i s růstem mzdy a cenou ochrany) — týž
         * vzorec jako graf (`cashflowTimeline` se `seZnamenkem`). */
        cap[mIdx] = capSurovy[mIdx] != null ? Math.max(0, capSurovy[mIdx] - z) : cap[mIdx] + uvolnitLze(-z);
      }
    }
    /* Splacený úvěr vrátí splátku — v kartě renty „po splnění cíle X“. */
    for (const u of uveryPlanu) {
      const g = vstupni.find((x) => String(x.id) === String(u.cilId));
      if (u.splatkaMes > 0 && u.doMes > u.odMes) zapisUvolneni(u.doMes, u.splatkaMes, g);
    }
  }
  /* Neznámý přebytek není schodek — nevíme, kolik rodina má (obrazovka to řekne jinde). */
  const schodekUveru = uveryPlanu.length && !zdroje.prebytekNeznamy ? souhrnSchodku(schodekMes, maxMes) : null;
  /** Kapacita pro stupňovitá navýšení renty: s úvěrem v plánu po čerpání
   * KLESNE, a navýšení umí jen přidávat. V každém měsíci proto jen to, co
   * vydrží až do konce horizontu (minimum od toho měsíce dál) — jinak by renta
   * dál dostávala peníze, které od čerpání platí splátka. Bez úvěru je
   * kapacita neklesající a výsledek je bajt po bajtu jako dřív. */
  const kapacitaProKroky = (doMes) => {
    if (!uveryPlanu.length) return cap;
    const out = cap.slice();
    let m = Infinity;
    for (let i = Math.min(doMes, maxMes) - 1; i >= 0; i--) { m = Math.min(m, cap[i]); out[i] = m; }
    return out;
  };

  let rezervaOK = false;
  const vysledky = [];

  const cerpej = (klic, potreba, mesic, jednorazove, cilId) => {
    const strop = stropZdroje(klic, cilId);
    // strop se aplikuje jen NAD potřebu — cíl si nikdy nevezme víc, než potřebuje
    const chci = strop == null ? Math.max(0, potreba) : Math.min(Math.max(0, potreba), strop);
    const kolik = Math.min(pool[klic], chci);
    if (kolik <= 0) return 0;
    pool[klic] -= kolik;
    jednorazove.push({ zdroj: NAZVY_ZDROJU[klic], castka: Math.round(kolik), mesic });
    return kolik;
  };

  /* Čerpání pro cíl „hned“ a pro rezervu — pořadí `PORADI_OKAMZITE` (Č20).
   *  · běžný účet si cíl mimo rezervu bere až po naplnění rezervy (pravidlo
   *    spec 3.1 zůstává; rezerva jde v pořadí první, takže ho dostane),
   *  · stavební spoření dorazí za ~4 měsíce (štítek u řádku); při volbě
   *    metodiky `zdrojeDoRenty` patří dlouhému cíli, pokud ho poradce výslovně
   *    nepřidělil jinak (`prideleniZdroje.stavebko`),
   *  · přepis přidělení poradcem (`stropZdroje`) platí pro každý zdroj. */
  const cerpejOkamzite = (g, potreba, jednorazove, jeRezervaCile) => {
    let p = potreba;
    for (const klic of PORADI_OKAMZITE) {
      if (klic === "bezny" && !jeRezervaCile && !rezervaOK) continue;
      if (klic === "stavebko") {
        if (doRenty && !prideleni.has("stavebko") && !smiDlouheZdroje(g)) continue;
        const pred = pool.stavebko;
        p -= cerpej("stavebko", p, STAVEBKO_DOSTUPNE_MES, jednorazove, g.id);
        if (pool.stavebko < pred) {
          const z = jednorazove[jednorazove.length - 1];
          z.zdroj += ` (zrušení trvá ~${STAVEBKO_DOSTUPNE_MES} měs.)`;
        }
        continue;
      }
      p -= cerpej(klic, p, 0, jednorazove, g.id);
    }
    return p;
  };

  /* FONDY NEJDŘÍV DO CHYBĚJÍCÍ REZERVY (metodika 2026-10-05, viz
   * `LABEL_FONDY_REZERVA`): až po dosavadních zdrojích rezervy, jen to, co
   * pořád chybí, a jen z likvidní části fondů. Starší plán `fondyLikvidni` 0. */
  const cerpejFondyDoRezervy = (g, potreba, jednorazove) => {
    if (!(potreba > 0.5) || !(fondyLikvidni > 0.5) || !(pool.fondy > 0.5)) return 0;
    const strop = stropZdroje("fondy", g.id);
    const chci = strop == null ? potreba : Math.min(potreba, Math.max(0, strop));
    const kolik = Math.min(chci, pool.fondy, fondyLikvidni);
    if (!(kolik > 0.5)) return 0;
    pool.fondy -= kolik;
    fondyLikvidni -= kolik;
    fondyDoRezervy += kolik;
    jednorazove.push({ zdroj: LABEL_FONDY_REZERVA, castka: Math.round(kolik), mesic: 0, fondy: true, doRezervy: true });
    return kolik;
  };

  /* Jednorázové přítoky BEZ určeného cíle, které už dostala renta (metodika
   * 2026-10-05, velká kontrola M1-02): jen PRVNÍ renta v pořadí, ne každá. */
  const pritokyDoRenty = new Set();
  for (const g of serazene) {
    const rezerva = jeRezerva(g);
    const renta = jeRenta(g);
    const hypoCil = !!g.uverem && !rezerva && !renta;

    // === přepisy poradce: horizont (musí jít do H PŘED alokací — glide path na něm stojí) ===
    const ov = cilOverride?.[g.id] || null;
    const ovRokyRaw = ov ? zadanyHorizont(ov.roky) : null;
    /* Strop `ROKY_STROP` (80 let) drží hlavičku a graf na jednom čísle: bez něj
     * by karta psala „za 1 000 000 000 let", zatímco projekce běží na stropu.
     * Od 25. 8. 2026 je ten strop SPOLEČNÝ s planEngine i se zobrazovací
     * vrstvou (dřív měl solver 1000 a engine 80) a platí i pro `g.roky`
     * z analýzy — jinak by se rozešly zase, jen o patro níž.
     *
     * ⚠️ NULA JE PLATNÝ PŘEPIS („hned"), ne „poradce nic nezadal" (audit R13).
     * Do 25. 8. 2026 tu stálo `ovRokyRaw > 0`, takže poradce, který napsal do
     * horizontu 0, dostal zpátky tiše hodnotu z analýzy — a karta mu tvrdila
     * jiný rok, než jaký zadal. `zadanyHorizont` vrací pro prázdno `null`,
     * takže se „nezadáno" a „nula" rozliší tvarem, ne velikostí. */
    const ovRoky = ovRokyRaw != null && ovRokyRaw >= 0 ? Math.min(ROKY_STROP, ovRokyRaw) : null;
    const ovCilovaRaw = ov ? zadanaCastka(ov.cilova) : null;
    const ovCilova = ovCilovaRaw != null && ovCilovaRaw > 0 ? ovCilovaRaw : null;

    const H = Math.max(1, Math.min(ROKY_STROP, Math.round(ovRoky != null ? ovRoky : +g.roky || 0)));
    /* Horizont, který poradce NEVYPLNIL (prázdno / text / záporno). `0` je
     * legitimní údaj („hned"), proto se testuje tvar, ne pravdivost. Rozvrh
     * zdrojů se tím NEMĚNÍ (dál se počítá s H = 1), ale procento plnění se
     * takovému cíli nepřiděluje — projekce na jeden rok, který nikdo nezadal,
     * není údaj o klientovi. Stejné pravidlo drží planEngine (`bezHorizontu`). */
    const rokyZadane = ovRoky != null ? ovRoky : (g.roky === null || g.roky === undefined || g.roky === "" || !Number.isFinite(+g.roky) || +g.roky < 0 ? null : Math.min(ROKY_STROP, +g.roky));
    const bezHorizontu = rokyZadane === null;
    /* CÍL „HNED“ = poradce (nebo analýza) zadal horizont 0. Prázdný horizont
     * „hned“ není — to je nezadaný údaj (`bezHorizontu`) a řeší se jako dřív.
     * Plán s metodikou před 4. 10. 2026 „hned“ nezná: cíl s nulou je cíl na
     * rok (H = 1) a rezerva se dorovnává z přebytku do roka — jako dřív. */
    const hned = c20 && rokyZadane === 0;
    /* CÍL BEZ TERMÍNU SE VYŘAZUJE Z ROZDĚLENÍ (dráha F3, metodika 2026-10-05).
     * Do té doby dostal cíl bez horizontu projekci na JEDEN rok: svatba
     * 250 000 bez termínu si brala 15 000 Kč/měs (celý přebytek na 12 měsíců)
     * i peníze z účtů, a cíle s termínem po ní měly 0 % — za cíl, u kterého
     * nikdo neřekl „kdy“. Teď nedostane ani zdroje, ani měsíční částku; Návrh,
     * tisk i Schod po schodu napíšou „doplňte termín“. Rezerva se nevyřazuje
     * (je vždy „teď“). Starší plán počítá postaru (razítko metodiky). */
    const vyrazenBezTerminu = c1005 && bezHorizontu && !rezerva;
    const horizontMes = H * 12;

    // cílová částka
    let cilova = +g.hodnota || 0;
    let hypo = null, hypoVybrana = null, hypoParametry = null, pravidloCile = null;
    /* Cíl na úvěr MIMO bydlení (metodika 2026-10-05) — spotřebitelský úvěr. */
    const spotrCil = hypoCil && c1005 && !jeHypotecniTypu(g.typ);
    let uverDruh = null;
    let dofinancovaniVysledek = null;
    /* Vlastní profil cíle (metodika 2026-10-05) — jen platný klíč `PROFILY`.
     * Spočítaný už tady: kapitál renty „po letech“ na profilu závisí. */
    const profilZadany = c1005 && !rezerva ? asObj(profilCil)[g.id] : undefined;
    const profilCile = typeof profilZadany === "string" && PROFILY[profilZadany] ? profilZadany : profil;
    if (renta) {
      cilova = c1018 && kapitalRenty === "poLetech"
        ? kapitalRentyPoLetech((+g.hodnota || 0) * 12, profilCile, varianta) // Nastavení: kapitál po letech čerpání (s. 26)
        : ((+g.hodnota || 0) * 12) / SAZBA_RENTA; // měsíční renta → kapitál @ 4 %
    }
    if (hypoCil) {
      /* Úrok a splatnost si nese CÍL (nabídka banky, kterou má poradce na stole);
       * bez nich platí výchozí hodnoty plánu. `hypoParametry` jdou ven i s tím,
       * jestli je poradce ZADAL — karta jinak nemá jak odlišit jeho číslo od
       * výchozího a tvrdila by, že 4,9 % zadal on. */
      const sazbaCile = sazbaHypoCile(g, sazbaHypoPlanu);
      const splatnostCile = splatnostHypoCile(g, splatnostHypo);
      /* Dvě desetinná místa, ne jedno: banky kótují 4,89 %, a zaokrouhlení na
       * 4,9 by kartě dalo jiné číslo, než s jakým engine počítá splátku. */
      const naProcenta = (x) => Math.round(x * 10000) / 100;
      hypoParametry = {
        sazbaPct: naProcenta(sazbaCile),
        splatnostLet: splatnostCile,
        sazbaZadana: sazbaHypoZadana(g),
        splatnostZadana: splatnostHypoZadana(g),
        vychoziSazbaPct: naProcenta(sazbaHypoPlanu),
        vychoziSplatnostLet: Number.isFinite(+splatnostHypo) && +splatnostHypo > 0 ? +splatnostHypo : SPLATNOST_HYPO_VYCHOZI,
      };
      /* METODIKA 2026-10-05: věk V ROCE KOUPĚ (horizont, jak ho poradce zadal
       * nebo přepsal v Návrhu; nezadaný = letos) a pravidlo země (NBS). */
      const ktxCile = ktxAkontace ? { ...ktxAkontace, roky: rokyZadane ?? 0 } : null;
      pravidloCile = ktxCile ? pravidloAkontace(vekProHypo, ktxCile) : null;
      if (spotrCil) {
        /* ÚVĚR MIMO BYDLENÍ (metodika 2026-10-05): spotřebitelský úvěr nebo úvěr
         * ze stavebka, splatnost ≤ 12 let, žádná ČNB ani parametry hypotéky. */
        const produkt = produktUveruCile(g);
        const sazbaU = sazbaUveruCile(g);
        const splatnostU = splatnostUveruCile(g);
        uverDruh = produkt;
        hypoParametry = {
          sazbaPct: naProcenta(sazbaU),
          splatnostLet: splatnostU,
          sazbaZadana: sazbaHypoZadana(g),
          splatnostZadana: splatnostHypoZadana(g),
          vychoziSazbaPct: naProcenta(produkt === "stavebko" ? SAZBA_STAVEBKO_UVER : SAZBA_SPOTREBITELSKY_UVER),
          vychoziSplatnostLet: SPLATNOST_SPOTREBITELSKY_VYCHOZI,
          maxSplatnostLet: SPLATNOST_SPOTREBITELSKY_MAX,
          produkt,
        };
        pravidloCile = null;
        hypo = uverVarianty(g, { sazba: sazbaU, splatnost: splatnostU, produkt });
        const pctH = akontacePctCile(g, vekProHypo, ktxCile);
        hypoVybrana = hypo.find((v) => v.akontacePct === pctH) || hypo.find((v) => v.akontacePct === AKONTACE_VYCHOZI_PCT) || hypo[0];
      } else {
        hypo = hypoVarianty(g, vekProHypo, {
          splatnostHypo: splatnostCile, sazbaHypo: sazbaCile,
          ...(pravidloCile ? { mladi: pravidloCile.mladi, zeme: pravidloCile.zeme } : {}),
        });
        // akontacePct === 0 znamená 100% financování — nesmí spadnout do defaultu
        const pct = akontacePctCile(g, vekProHypo, ktxCile);
        hypoVybrana = hypo.find((v) => v.akontacePct === pct) || hypo.find((v) => v.akontacePct === AKONTACE_VYCHOZI_PCT) || hypo[0];
      }
      /* KROK 3 — DOFINANCOVÁNÍ Z MAJETKU (jen metodika 2026-10-05). */
      const dof = c1005 ? volbaDofinancovani(dofinancovani, g.id, analyza || {}, { spotr: spotrCil, vlastnici: vlastniciDof }) : null;
      if (dof && dof.zpusob === "zastava" && !spotrCil) {
        /* LTV k OBĚMA nemovitostem: úvěr nejvýš limit × (cena + zástava). */
        const cena = +g.hodnota || 0;
        const limitPct = pravidloCile ? 100 - pravidloCile.pct : 100 - akontacePctVychozi(vekProHypo);
        const maxUver = Math.round((limitPct / 100) * (cena + dof.castka));
        const uverZ = Math.min(cena, maxUver);
        const vlastniZ = Math.max(0, cena - uverZ);
        const splatkaZ = Math.round(anuitniSplatka(uverZ, sazbaCile, splatnostCile));
        const varZ = {
          id: "zastava", nazev: "Se zástavou", akontacePct: cena > 0 ? Math.round((vlastniZ / cena) * 1000) / 10 : 0,
          vlastniZdroje: vlastniZ, hypoteka: uverZ, splatkaHypo: splatkaZ, uverStavebko: null, celkemMesicne: splatkaZ,
          pozn: "Modelace se zástavou další nemovitosti — ověřit u banky.",
        };
        hypo = [...hypo, varZ];
        hypoVybrana = varZ;
        dofinancovaniVysledek = { zpusob: dof.zpusob, castka: dof.castka, nazev: dof.nazev, majetekId: dof.majetekId,
          limitPct, maxUver, vlastniPred: null, snizeni: null };
      }
      cilova = hypoVybrana.vlastniZdroje; // investujeme na akontaci, zbytek řeší úvěr
      if (dof && (dof.zpusob === "prodej" || dof.zpusob === "jiny")) {
        /* Výnos z prodeje sníží vlastní zdroje, které se musí naspořit. */
        const pred = cilova;
        const snizeni = Math.min(pred, dof.castka);
        cilova = Math.max(0, pred - snizeni);
        dofinancovaniVysledek = { zpusob: dof.zpusob, castka: dof.castka, nazev: dof.nazev, majetekId: dof.majetekId,
          vlastniPred: pred, snizeni, zbyvaZMajetku: Math.max(0, dof.castka - snizeni) };
      }
    }
    // přepis cílové částky až nakonec — přebíjí i rentu (kapitál) i akontaci hypotéky
    if (ovCilova != null) cilova = ovCilova;

    /* Vnitřní průchod (`jenUvery`): úvěr cíle jako událost — čerpání na konci
     * horizontu (cíl „hned“ teď). Cíl bez termínu úvěr nečerpá. Dál se nepočítá. */
    if (jenUvery) {
      if (hypoCil && hypoVybrana && c1005 && !vyrazenBezTerminu) {
        const u = uverCileVCase({ id: g.id, typ: g.typ, hypoVybrana, hypoParametry }, { odMes: hned ? 0 : horizontMes });
        if (u) uveryNalezene.push(u);
      }
      continue;
    }

    const alokace = rezerva
      ? [{ rok: 1, akcie: 0, dluhopisy: 0, cash: 100 }]
      : alokaceProCil(renta && c1018 && hned ? 0 : H, profilCile, { renta, tabulky: c1018, posun: c1018 ? posunLet : 0 });

    const jednorazove = [];
    /* U cíle „hned“ se nic nezhodnocuje — potřeba je celá cílová částka. */
    let potreba = rezerva || hned ? cilova : potrebnaJednorazovka(cilova, alokace, varianta);

    // jednorázové čerpání likvidních zdrojů
    if (vyrazenBezTerminu) {
      /* Cíl bez termínu (metodika 2026-10-05): ze zdrojů nic — peníze zůstanou
       * cílům, u kterých se ví, kdy mají být hotové. */
    } else if (hned) {
      /* Č20: běžný → spořicí → hotovost → termínovaný vklad → stavební spoření.
       * Do 4. 10. 2026 rezerva brala jen spořicí → hotovost → běžný a stavebko
       * ani termíňák nesměla — chybějící část se pak dorovnávala z přebytku. */
      potreba = cerpejOkamzite(g, potreba, jednorazove, rezerva);
      /* Co rezervě pořád chybí, doplní fondy (metodika 2026-10-05). */
      if (rezerva) potreba -= cerpejFondyDoRezervy(g, potreba, jednorazove);
      /* `cilova > 0` je podmínka, ne kosmetika: při neznámém cíli rezervy byla
       * `potreba` nula od začátku a rezerva se prohlásila za naplněnou, aniž by
       * na ni šla jediná koruna — a tím se odemkl běžný účet pro ostatní cíle. */
      if (rezerva) rezervaOK = cilova > 0 && potreba <= 0.5;
    } else if (rezerva) {
      /* REZERVA S HORIZONTEM (1 rok a víc, nebo nezadaný) a rezerva každého
       * plánu s metodikou před 4. 10. 2026: JEN ÚČTY, spořicí → hotovost →
       * běžný — přesně jako dřív. Stavebko ani termínovaný vklad si nebere
       * (zúžení Č20, 4. 10. 2026); chybějící část se dospoří z přebytku. */
      potreba -= cerpej("sporici", potreba, 0, jednorazove, g.id);
      potreba -= cerpej("hotovost", potreba, 0, jednorazove, g.id);
      potreba -= cerpej("bezny", potreba, 0, jednorazove, g.id);
      /* Co po účtech chybí, doplní nejdřív fondy (metodika 2026-10-05), až
       * zbytek se dospoří z přebytku. */
      potreba -= cerpejFondyDoRezervy(g, potreba, jednorazove);
      rezervaOK = cilova > 0 && potreba <= 0.5;
    } else {
      potreba -= cerpej("sporici", potreba, 0, jednorazove, g.id);
      potreba -= cerpej("hotovost", potreba, 0, jednorazove, g.id);
      if (rezervaOK) potreba -= cerpej("bezny", potreba, 0, jednorazove, g.id); // běžný až po naplnění rezervy
      /* STAVEBKO: při `zdrojeDoRenty` ho smí čerpat jen dlouhý cíl. Jinak by
       * padlo na první cíl v pořadí (typicky auto za 4 roky) — a majitel řekl,
       * že se dlouhé zdroje investují do renty na dlouhý cíl.
       * ⚠️ PŘEPIS PORADCE MÁ POSLEDNÍ SLOVO: jakmile pro stavebko určil cíle
       * sám (`prideleniZdroje.stavebko`), rozhoduje jeho mapa přes `stropZdroje`
       * a metodická volba se jí nesmí plést do cesty. */
      if (horizontMes > STAVEBKO_DOSTUPNE_MES && (prideleni.has("stavebko") || smiDlouheZdroje(g))) {
        const pred = pool.stavebko;
        potreba -= cerpej("stavebko", potreba, STAVEBKO_DOSTUPNE_MES, jednorazove, g.id);
        if (pool.stavebko < pred) {
          const z = jednorazove[jednorazove.length - 1];
          z.zdroj += ` (zrušení trvá ~${STAVEBKO_DOSTUPNE_MES} měs.)`;
        }
      }
    }

    /* Penzijko (DPS/DIP) → jednorázový vklad do RENTY v roce, kdy klientovi bude
     * VEK_PENZIJKO. ⚠️ Měsíc se NESMÍ posunout na nulu: kdyby se penzijko počítalo
     * jako peníze dostupné dnes a nechalo se růst celý horizont, cíl renty by se
     * nafoukl o statisíce, které tam nejsou (a plán by tiše předpokládal odbytné
     * se ztrátou státních příspěvků). Příznak `penzijko` je pro rozpočet: peníze
     * z poolu odcházejí HNED (je to majetek klienta), jen dorazí do cíle až tady. */
    /* PŘEPIS PŘIDĚLENÍ (`prideleniZdroje.penzijko`) mění dvě věci a nic víc:
     *  1. penzijko smí jít i na jiný cíl než rentu (poradce rozhoduje),
     *  2. čerpá se z POOLU → dva cíle renty už si tutéž korunu nerozdají dvakrát.
     * Časování zůstává: peníze dorazí ve VEK_PENZIJKO, ať je přidělí kdokoli komu. */
    /* `zdrojeDoRenty` dělá totéž jako přepis, jen bez ručního zadání: penzijko
     * míří na dlouhý cíl a ČERPÁ SE Z POOLU — takže dva cíle renty už si tutéž
     * korunu nerozdají dvakrát (dřív dostal celé penzijko každý z nich a plán
     * slíbil 1 600 000 z 800 000, což rozpočet hlásil jako přečerpání). */
    const stropPenzijko = stropZdroje("penzijko", g.id);
    /* METODIKA 2026-10-05 (velká kontrola M1-02): penzijko se čerpá z POOLU
     * i bez přepisu a bez `zdrojeDoRenty` — dostane ho PRVNÍ renta v pořadí,
     * další už jen zbytek (0). Dřív dostal celé penzijko každý cíl renty:
     * dvě renty a penzijko 500 000 = slíbený 1 000 000, rozpočet −500 000. */
    const zPoolu = stropPenzijko !== undefined || doRenty || c1005;
    /* Cíl bez termínu (metodika 2026-10-05) penzijko nedostane — `0` hned
     * na začátku, ať se z poolu nic neubere. */
    const penzijkoProCil = vyrazenBezTerminu
      ? 0
      : stropPenzijko !== undefined
        ? Math.max(0, Math.min(pool.penzijko, stropPenzijko == null ? pool.penzijko : stropPenzijko))
        : doRenty
          ? (smiDlouheZdroje(g) ? Math.max(0, pool.penzijko) : 0)
          : renta
            ? (c1005 ? Math.max(0, pool.penzijko) : zdroje.penzijko.castka) // starší plán: celé penzijko do každého cíle renty
            : 0;
    if (penzijkoProCil > 0) {
      if (zPoolu) pool.penzijko -= penzijkoProCil;
      /* SK 62 let (AP-05) jen v metodice 2026-10-18 a novější: plán s dřívějším
       * razítkem počítá na korunu postaru (pravidlo majitele 3. 10.). */
      const vekPenz = c1018 ? vekPenzijka(zeme) : VEK_PENZIJKO;
      const mesicDo60 = Math.max(0, (vekPenz - vek) * 12);
      /* PENÍZE DORAZÍ AŽ PO CÍLOVÉM DATU → aby je klient na tenhle cíl vůbec
       * použil, musí smlouvu zrušit DŘÍV. To JDE (majitel: „penze nebo DIP lze
       * zrušit"), ale stojí to státní příspěvek (DPS) nebo daňovou výhodu (DIP).
       * ⚠️ ŽÁDNÉ ČÍSLO — engine neví, kolik příspěvků klient dostal ani jakou
       * úlevu uplatnil. Vrací se jen kód ztráty, věta patří UI. */
      const predcasne = mesicDo60 > horizontMes;
      const ztrata = predcasne ? ztrataZeZruseniPenzijka(zdroje.penzijko.druhy) : null;
      /* MOST DO 60 (metodika 2026-10-28, majitel 28. 10. 2026 klikem: „Most do 60,
       * penzijko nechat — Renta do 60 z ostatních peněz, od 60 penzijko bez ztráty
       * státních příspěvků“): u renty se penzijko NERUŠÍ a kapitál renty na začátku
       * nenafukuje (měsíc výplaty zůstává za horizontem), ale od výplaty zvýší rentu
       * o castka × 4 % ÷ 12 (`SAZBA_RENTA`, věčná renta jako kapitál renty). Kód
       * ztráty u renty odpadá — nic se předčasně neruší. Neznámý věk = postaru. */
      const most = c1028 && renta && predcasne && !vekNezadan;
      jednorazove.push({
        zdroj: `penzijko (DPS — dostupné v ${vekPenz})`,
        castka: Math.round(penzijkoProCil),
        mesic: mesicDo60,
        penzijko: true,
        vekDostupnosti: vekPenz,
        ...(most ? { doRentyOdVeku: true, rentaNavicMes: Math.round((penzijkoProCil * SAZBA_RENTA) / 12) } : {}),
        pozn: most
          ? "do renty od věku výplaty — renta se zvýší"
          : predcasne
            ? (renta ? "vklad až po začátku čerpání renty" : "vklad dorazí až po cílovém datu")
            : undefined,
        /* `undefined` se z JSON.stringify vytratí → výstup plánu, kde se nic
         * předčasně neruší, zůstává bajt po bajtu stejný jako dřív. */
        ztrata: (!most && ztrata) || undefined,
      });
    }

    /* ── FONDY A INVESTIČNÍ ŽIVOTKO → RENTA (metodika 2026-10-05) ───────────
     * Majitel 5. 10. 2026 („Ano, do renty“): podílové fondy, ETF, akcie
     * a životko s hodnotou (odkupné, jak ho poradce zadal v analýze), které
     * klient UŽ MÁ, plán dřív nepřiřadil k žádnému cíli — v rozpočtu stály pod
     * „nezapojeno“, jako by nebyly. Teď jdou ve výchozím stavu do renty stejně
     * jako penzijko, jen jsou k dispozici HNED (měsíc 0) a do startu renty
     * rostou podle profilu renty (glide path cíle).
     *  · JEN JEDNOU: čerpá se z poolu, takže je dostane PRVNÍ renta v pořadí
     *    (nejkratší horizont) a další renta už nic — tatáž koruna se neslíbí
     *    dvakrát (vada M1-02 u penzijka),
     *  · přepis poradce má přednost: `prideleniZdroje.fondy` (jiný cíl, částky)
     *    i `bezZdroju[cíl]` (u cíle odebrané → dostane je další renta),
     *  · se `zdrojeDoRenty` a bez renty míří na nejdelší cíl (jako penzijko),
     *  · cíl „hned“ a cíl bez termínu je nedostanou nikdy (nejsou na účtu —
     *    prodej trvá a cena kolísá); REZERVA jen chybějící částku z podílových
     *    fondů/ETF/akcií ještě PŘED rentou (`cerpejFondyDoRezervy`, majitel
     *    5. 10. 2026 „Nejdřív doplnit rezervu“) — sem dorazí už jen zbytek
     *    a řádek pak nese `poRezerve: true` („zbytek po doplnění rezervy“),
     *  · plán se starším razítkem je nečerpá (pool 0) — čísla beze změny. */
    const stropFondy = stropZdroje("fondy", g.id);
    const smiFondy = pool.fondy > 0.5 && !rezerva && !hned && !vyrazenBezTerminu;
    const fondyProCil = !smiFondy
      ? 0
      : stropFondy !== undefined
        ? Math.max(0, Math.min(pool.fondy, stropFondy == null ? pool.fondy : stropFondy))
        : (doRenty ? smiDlouheZdroje(g) : renta)
          ? pool.fondy
          : 0;
    if (fondyProCil > 0.5) {
      pool.fondy -= fondyProCil;
      jednorazove.push({ zdroj: LABEL_FONDY, castka: Math.round(fondyProCil), mesic: 0, fondy: true, ...(fondyDoRezervy > 0.5 ? { poRezerve: true } : {}) });
    }

    // jednorázové budoucí přítoky přiřazené tomuto cíli (nebo rentě, když cíl neurčen)
    for (const p of pritoky) {
      if (p.typ !== "jednorazovy") continue;
      /* Přítok bez cíle: metodika 2026-10-05 jen do PRVNÍ renty (M1-02), starší plán do každé. */
      const patriSem = !vyrazenBezTerminu && (p.cilId ? p.cilId === g.id : (renta && !(c1005 && pritokyDoRenty.has(p))));
      if (patriSem && !p.cilId && c1005) pritokyDoRenty.add(p);
      if (patriSem) jednorazove.push({ zdroj: `přítok: ${p.poznamka || "mimořádný příjem"}`, castka: Math.round(+p.castka || 0), mesic: (+p.odRoku || 0) * 12 });
    }

    // vklad přesně v cílovém datu (mesic === horizont) počítej jako kapitál u cíle
    const vkladyProSim = jednorazove
      .filter((v) => v.mesic <= horizontMes)
      .map((v) => (v.mesic >= horizontMes ? { ...v, mesic: horizontMes - 1 } : v));

    // === režim řešení zvolený poradcem (jen mimo povinnou rezervu) ===
    const rezimCile = REZIM_SET.has(rezim?.[g.id]) ? rezim[g.id] : null;
    const rucniJ = zadanaCastka(jednorazoveRucni?.[g.id]);
    const rucniP = zadanaCastka(pravidelneRucni?.[g.id]);
    /* Cíl „hned“ ruční režim ignoruje stejně jako rezerva: měsíční investice
     * k němu nepatří (Č20). Dospoření je samostatná volba `dosporitHned`. */
    const rucniRezim = !rezerva && !hned && !vyrazenBezTerminu && (rezimCile != null || rucniJ != null || rucniP != null);
    /* BEZ MĚSÍČNÍ INVESTICE (Č20 oprava): nula od poradce u cíle bez režimu.
     * Cíl „hned“ měsíční investici nemá vůbec, ruční režim má vlastní piny. */
    /* Plán s metodikou před 4. 10. 2026: nula zůstává měkkým přepisem karty
     * a rozpočtu (`efektivniPravidelne`), solver přiděluje svůj návrh — jako dřív. */
    const bezMes = c20 && !hned && !vyrazenBezTerminu && !rucniRezim && asObj(bezMesicni)[g.id] === true;
    /* RUČNÍ MĚSÍČNÍ ČÁSTKA (bod 10, metodika 2026-10-05): kladné číslo od
     * poradce u cíle bez režimu. Cíl dostane PŘESNĚ tuhle částku (i nad volnou
     * kapacitu — pak `nadKapacitu`, jako u ručního režimu) a kapacitu bere jen
     * tolik; ve výstupu je i `pravidelneNavrh` (tlačítko „návrh X“ v kartě). */
    const mekkaP = c1005 && !hned && !vyrazenBezTerminu && !rucniRezim && !bezMes ? zadanaCastka(asObj(pravidelneZadane)[g.id]) : null;
    const mekky = mekkaP != null && mekkaP > 0;

    // pravidelná investice z přebytku (konstantní část)
    let pravidelne = 0;
    let pravidelneKroky = [];
    let dopocet = null;
    let kapacitaMes = 0;
    let dosporit = null;
    let dosporitNavrh = null;
    let pravidelneNavrh = 0;
    /* „Hned“ = jen peníze dostupné do pár měsíců (účty hned, stavebko ~4 měs.,
     * přítok přidělený cíli letos) — přítok nebo penzijko za rok a déle ne. */
    /* REZERVA DOPLNĚNÁ Z FONDŮ (metodika 2026-10-05): `zFondu` je samostatný
     * krok „doplní se z fondů X“ — do „hned k dispozici“ (= co klient na
     * rezervu MÁ dnes, `plneniCilu.stavRezervy`) se nepočítá, do výsledku po
     * realizaci ano. Bez fondů 0 → výpočet bajt po bajtu jako dřív. */
    const zFondu = rezerva ? jednorazove.filter(jeFondyDoRezervy).reduce((a, v) => a + (+v.castka || 0), 0) : 0;
    const vkladyHned = hned ? jednorazove.filter((v) => v.mesic < 12 && !jeFondyDoRezervy(v)) : [];
    const hnedKDispozici = vkladyHned.reduce((a, v) => a + (+v.castka || 0), 0);
    /* Kdy dorazí POSLEDNÍ z těch peněz (stavebko ~4 měs.). Slovo „hned“ ve
     * větě pro klienta platí jen u nuly — jinak „do ~4 měs.“ (Č20 oprava). */
    const hnedDoMes = vkladyHned.reduce((m, v) => ((+v.castka || 0) > 0 ? Math.max(m, Math.round(+v.mesic || 0)) : m), 0);
    if (vyrazenBezTerminu) {
      /* Cíl bez termínu (metodika 2026-10-05): měsíčně nic, kapacita zůstává
       * dalším cílům. Ruční částka ani režim u něj neplatí — nejdřív termín. */
    } else if (hned) {
      /* CÍL „HNED“ — MĚSÍČNÍ ČÁSTKU SOLVER SÁM NEPŘIDĚLÍ (Č20).
       * `dosporitNavrh` je jen nabídka pro poradce: kolik měsíčně a jak dlouho,
       * aby se chybějící část vešla do volného přebytku (min. 12 měsíců, jako
       * dřív „do roka“; víc, když se 1/12 do přebytku nevejde). Kapacitu bere
       * nabídka PŘED odečtem, tedy tak, jak by ji cíl po kliknutí dostal. */
      const gap = Math.max(0, cilova - hnedKDispozici - zFondu);
      if (gap > 0.5) {
        const kap = minCap(12);
        if (kap > 0.5) {
          const m0 = Math.max(12, Math.ceil(gap / kap));
          if (m0 <= DOSPORIT_MAX_MES) {
            dosporitNavrh = { mesicu: m0, castkaMes: Math.min(Math.round(minCap(m0)), Math.ceil(gap / m0)) };
          }
        }
        const M = dosporitMesicu(dosporitHned?.[g.id]);
        if (M != null) {
          pravidelne = Math.min(minCap(M), Math.ceil(gap / M));
          uberCap(M, pravidelne);
          zapisUvolneni(M, pravidelne, g);
          /* Od M-tého měsíce se peníze vracejí dalším cílům stupňovitě (bod 11). */
          if (c1005 && pravidelne > 0.5) uvolneniDosporeni.add(M);
          dosporit = { mesicu: M, castkaMes: Math.round(pravidelne) };
        }
      }
    } else if (rucniRezim) {
      // Poradce si hraje s čísly → jedna bisekce nad projekceCile dá OBĚ čísla i graf.
      // Kapacitu domácnosti tu NEstropujeme (poradce chce vidět skutečnou potřebu),
      // jen ji poctivě označíme přes nadKapacitu a odečteme pro další cíle.
      kapacitaMes = minCap(horizontMes);
      dopocet = dopocitejCil({
        cilova,
        horizontRoky: H,
        alokace,
        varianta,
        vklady: vkladyProSim,
        kroky: [],
        rezim: rezimCile || "oboji",
        zadano: { jednorazove: rucniJ, pravidelne: rucniP },
      });
      pravidelne = dopocet.pravidelne;
      if (dopocet.jednorazove > 0) {
        jednorazove.push({
          zdroj: LABEL_DOPOCET,
          castka: dopocet.jednorazove,
          mesic: 0,
          rucni: true,
        });
      }
      uberCap(horizontMes, pravidelne);
      if (!renta) zapisUvolneni(horizontMes, pravidelne, g);
    } else if (bezMes) {
      /* Poradce měsíční investici u cíle nechce. Solver si jen spočítá, co by
       * navrhl (pro tlačítko „návrh X“), ale kapacitu NEBERE — zůstane volná
       * pro další cíle. Cíl se pak kryje jen zdroji (projekce s 0 Kč/měs). */
      if (rezerva) {
        const gap = Math.max(0, potreba);
        const doMes = Math.max(12, horizontMes);
        pravidelneNavrh = gap > 0 ? Math.min(minCap(doMes), Math.ceil(gap / doMes)) : 0;
      } else {
        const potrebne = potrebnaPravidelna({ cilova, horizontRoky: H, alokace, varianta, vklady: vkladyProSim });
        pravidelneNavrh = Math.min(potrebne, minCap(horizontMes));
      }
    } else if (mekky) {
      /* Ruční částka platí ve výpočtu. Návrh se spočítá stejně jako bez ní —
       * jen pro tlačítko „návrh X“, kapacitu bere ruční číslo. */
      const doMes = rezerva ? Math.max(12, horizontMes) : horizontMes;
      kapacitaMes = minCap(doMes);
      if (rezerva) {
        const gap = Math.max(0, potreba);
        pravidelneNavrh = gap > 0 ? Math.min(kapacitaMes, Math.ceil(gap / doMes)) : 0;
      } else {
        const potrebne = potrebnaPravidelna({ cilova, horizontRoky: H, alokace, varianta, vklady: vkladyProSim });
        pravidelneNavrh = Math.min(potrebne, kapacitaMes);
      }
      pravidelne = mekkaP;
      uberCap(doMes, pravidelne);
      if (!renta) zapisUvolneni(doMes, pravidelne, g);
      /* Renta: uvolněné peníze z dřívějších cílů a růstu mzdy dostane dál jako
       * kroky — ale jen NAVÝŠENÍ po prvním měsíci. Co zbylo hned od začátku,
       * renta nebere: poradce zadal, kolik do ní jde. */
      if (renta) {
        const kap = kapacitaProKroky(horizontMes);
        let posledni = kap[0] || 0;
        for (let mIdx = 1; mIdx < Math.min(horizontMes, maxMes); mIdx++) {
          if (kap[mIdx] !== posledni) {
            if (kap[mIdx] > posledni) pravidelneKroky.push({ odMes: mIdx, castkaMes: kap[mIdx] - posledni, duvody: duvodyKroku(mIdx) });
            posledni = kap[mIdx];
          }
        }
        for (const k of pravidelneKroky) uberCap2(cap, k, horizontMes);
      }
    } else if (rezerva) {
      const gap = Math.max(0, potreba);
      if (gap > 0) {
        const doMes = Math.max(12, horizontMes); // doplnit do roka (nebo dle zadaného horizontu)
        pravidelne = Math.min(minCap(doMes), Math.ceil(gap / doMes));
        uberCap(doMes, pravidelne);
        zapisUvolneni(doMes, pravidelne, g);
      }
    } else {
      const potrebne = potrebnaPravidelna({ cilova, horizontRoky: H, alokace, varianta, vklady: vkladyProSim });
      pravidelne = Math.min(potrebne, minCap(horizontMes));
      uberCap(horizontMes, pravidelne);
      if (!renta) zapisUvolneni(horizontMes, pravidelne, g);
      /* Bod 11 (metodika 2026-10-05): po dospoření rezervy dostane cíl od
       * uvolněného měsíce víc — stupňovitě, ne minimum na celý horizont. */
      if (!renta && c1005 && uvolneniDosporeni.size > 0 && potrebne > pravidelne) {
        pravidelneKroky = krokyZDosporeni({ zaklad: pravidelne, doMes: horizontMes, cilova, H, alokace, vklady: vkladyProSim });
        for (const k of pravidelneKroky) uberCap2(cap, k, horizontMes);
      }
      // renta: zbylá (stupňovitá) kapacita z růstu mzdy jde do renty
      if (renta && potrebne > pravidelne) {
        const kap = kapacitaProKroky(horizontMes);
        let posledni = 0;
        for (let mIdx = 0; mIdx < Math.min(horizontMes, maxMes); mIdx++) {
          if (kap[mIdx] !== posledni) {
            if (kap[mIdx] > posledni) pravidelneKroky.push({ odMes: mIdx, castkaMes: kap[mIdx] - posledni, duvody: duvodyKroku(mIdx) });
            posledni = kap[mIdx];
          }
        }
        for (const k of pravidelneKroky) uberCap2(cap, k, horizontMes);
      }
    }

    /* „Hned“ = hotovost, kterou klient má k dispozici teď (vč. stavebka za ~4
     * měsíce), bez výnosu — žádná projekce na rok s „101 %“. */

    // v ručním režimu bereme projekci z dopočtu — číslo i graf tak vzniknou ze STEJNÉHO běhu
    const projekce = hned
      ? projekceHotovosti(hnedKDispozici + zFondu, pravidelne, dosporit ? dosporit.mesicu : 0)
      : dopocet
        ? dopocet.projekce
        : rezerva
          ? projekceCile({ horizontRoky: Math.max(1, H), alokace, varianta, vklady: vkladyProSim, pravidelne })
          : projekceCile({ horizontRoky: H, alokace, varianta, vklady: vkladyProSim, pravidelne, kroky: pravidelneKroky });

    /* STAV DNES = táž projekce, ale BEZ nových vkladů: jen peníze, které klient
     * už má (spořák, hotovost, běžný, stavebko, penzijko v 60), nechané růst do
     * cílového data. Ručně přibitý vklad poradce se sem nepočítá — ten teprve
     * musí někde vzniknout; `vkladyProSim` ho neobsahuje (přidává se až do
     * `jednorazove` níž). Díky tomu má „dnes → po realizaci" obě čísla z JEDNÉ
     * metodiky; dřív se „dnes" bralo z planEngine (ploché 4 %) a „po" odsud
     * (glide path), takže šipka srovnávala dvě různě spočítané veličiny. */
    /* REZERVA „DNES“ BEZ ÚROKU (dráha F3, metodika 2026-10-05): rezerva
     * s termínem (1 rok a víc) měla „dnes“ = účty zúročené 0,8 % do termínu —
     * na 1 straně pak stálo „naplněno 60 % (220 000 Kč)“, přitom 220 000 je
     * 59 % z 370 000. Rezerva se neinvestuje, „dnes“ je to, co na účtech JE
     * (`plneniCilu.stavRezervy`). Starší plán počítá postaru. */
    const vkladyDnesRezervy = rezerva && !hned && c1005
      ? jednorazove.filter(jeVkladDnes).reduce((a, v) => a + (+v.castka || 0), 0)
      : null;
    const projekceDnes = hned
      ? projekceHotovosti(hnedKDispozici, 0, 0)
      : vkladyDnesRezervy != null
        ? projekceHotovosti(vkladyDnesRezervy, 0, 0)
        : projekceCile({ horizontRoky: Math.max(1, H), alokace, varianta, vklady: vkladyProSim, pravidelne: 0 });
    // rezerva se počítá prostě: jednorázově + pravidelně × měsíce (cash výnos zanedbatelný)
    const dosazeno = projekce.konec;
    const dosazenoDnes = projekceDnes.konec;
    /* `cilova > 0` — cíl bez zadané částky NENÍ splněný cíl. Dřív `0 >= 0` dalo
     * `splneno: true`, a věta „tvoje cíle se podaří naplnit" pak stála na cíli,
     * o kterém appka nevěděla nic. Totéž pro cíl bez horizontu. */
    const splneno = cilova > 0 && !bezHorizontu && dosazeno >= cilova * 0.995;
    const chybi = splneno ? 0 : Math.max(0, Math.round(cilova - dosazeno));

    vysledky.push({
      id: g.id, popis: g.popis, typ: g.typ,
      roky: rokyZadane === null ? 0 : rokyZadane,
      renta, rezerva, auto: !!g.auto,
      cilova: Math.round(cilova),
      rentaMes: renta ? Math.round(ovCilova != null ? (cilova * SAZBA_RENTA) / 12 : +g.hodnota || 0) : undefined,
      hypo, hypoVybrana, hypoParametry,
      /* Věk klienta neznámý → varianta akontace se nevybrala podle věku.
       * Klíč jen v tom případě — u známého věku je výstup beze změny. */
      ...(hypoCil && vekNezadan ? { vekNezadan: true } : {}),
      /* Metodika 2026-10-05: podle čeho se akontace rozhodla (věk a rok koupě,
       * země). Klíč jen u cíle na úvěr v nové metodice — jinak výstup beze změny. */
      /* Úvěr mimo bydlení (metodika 2026-10-05) a dofinancování z majetku —
       * klíče jen když platí, jinak výstup beze změny. */
      ...(uverDruh ? { uverDruh } : {}),
      ...(dofinancovaniVysledek ? { dofinancovani: dofinancovaniVysledek } : {}),
      ...(hypoCil && pravidloCile ? {
        akontacePravidlo: {
          vekPriKoupi: pravidloCile.vekPriKoupi, rokKoupe: pravidloCile.rokKoupe,
          zeme: pravidloCile.zeme, mladi: pravidloCile.mladi,
          ...(pravidloCile.rezim ? { rezim: pravidloCile.rezim } : {}),
          duvod: pravidloCile.duvod,
          ...(pravidloCile.vekPriKoupiK2 != null ? { vekPriKoupiK2: pravidloCile.vekPriKoupiK2 } : {}),
        },
      } : {}),
      /* ── JEDEN CÍL = JEDNO ČÍSLO (audit R1/R4, 25. 8. 2026) ───────────────
       * `cilova` je to, co se MÁ NASPOŘIT (u hypotéky akontace). Klientův cíl
       * je ale celá cena — „chci byt za 3 000 000", ne „chci 300 000". Obojí
       * proto jde ven zvlášť a hlavička klientských výstupů bere `hodnotaCile`;
       * akontace, vlastní zdroje a splátka jsou „jak na to", ne místo toho.
       * Dřív si každá vrstva jedno z těch čísel dopočítávala sama (× 0.2,
       * vlastní sazba hypotéky), takže na jedné A4 stálo 3 000 000, 600 000
       * i 300 000 a dvě různé splátky. */
      uverem: !!g.uverem,
      hodnotaCile: hypoCil ? +g.hodnota || 0 : Math.round(cilova),
      akontacePct: hypoVybrana ? hypoVybrana.akontacePct : undefined,
      splatkaMes: hypoVybrana ? Math.round(hypoVybrana.celkemMesicne) : undefined,
      /* Horizont, jak ho poradce ZADAL: `0` = „hned", `null` = nevyplnil.
       * `roky` výš je dopočet pro projekci a nulu od prázdna nerozliší. */
      rokyZadane,
      /* CÍL „HNED“ (Č20) — klíče jen u něj, ostatní cíle mají výstup beze změny.
       *  `hnedKDispozici` = kolik ze zdrojů má klient na cíl teď (bez výnosu),
       *  `dosporit`       = dospoření zvolené poradcem `{mesicu, castkaMes}` | null,
       *  `dosporitNavrh`  = nabídka dospoření, když zdroje nestačí (nic nepřiděluje). */
      ...(hned ? { hned: true, hnedKDispozici: Math.round(hnedKDispozici), dosporit, dosporitNavrh } : {}),
      /* `hnedDoMes` = za kolik měsíců dorazí poslední peníze „hned“ (stavebko).
       * Klíč jen když je > 0 — u peněz z účtů je výstup beze změny. */
      ...(hned && hnedDoMes > 0 ? { hnedDoMes } : {}),
      /* `zFondu` = kolik chybějící rezervy doplní fondy (metodika 2026-10-05,
       * krok „doplní se z fondů X“). Klíč jen když > 0 — jinak výstup beze změny. */
      ...(zFondu > 0.5 ? { zFondu: Math.round(zFondu) } : {}),
      /* Bez měsíční investice (rozhodnutí poradce) + co by solver navrhl. */
      ...(bezMes ? { bezMesicni: true, pravidelneNavrh: Math.round(pravidelneNavrh) } : {}),
      /* Ruční měsíční částka ve výpočtu (bod 10) + co by solver navrhl. */
      ...(mekky ? { pravidelneZadane: true, pravidelneNavrh: Math.round(pravidelneNavrh) } : {}),
      jednorazove, pravidelne: Math.round(pravidelne), pravidelneKroky,
      alokace, projekce, splneno, chybi,
      /* Podklady pro JEDINÝ výpočet procenta plnění (`lib/plneniCilu.js`).
       * `bezHorizontu` = poradce nezadal, kdy to má být hotové. */
      dosazenoDnes, bezHorizontu,
      /* Dráha F3 (metodika 2026-10-05) — klíče jen když platí, jinak výstup
       * beze změny:
       *  `vyrazenBezTerminu` = cíl bez termínu dostal 0 (zdroje i měsíčně),
       *  `profilCile`        = cíl počítaný vlastním profilem (jiným než plán),
       *  `krokyVTisku`       = navýšení renty z uvolněných peněz se tiskne
       *                        (Plný plán, 1 strana) jako u běžných cílů. */
      ...(vyrazenBezTerminu ? { vyrazenBezTerminu: true } : {}),
      ...(profilCile !== profil ? { profilCile } : {}),
      /* Metodika 2026-10-18 (Fichtner): portfolio renty V ČERPÁNÍ (s. 26, sloupec 0 s. 27) —
       * jiné než poslední rok akumulace; klíč jen pod novým razítkem. */
      ...(renta && c1018 ? { alokaceCerpani: alokaceRenty(0, profilCile) } : {}),
      ...(renta && c1005 ? { krokyVTisku: true } : {}),
      /* Úvěr cíle v čase (metodika 2026-10-05): od kdy a kolik splátka krátí
       * kapacitu ostatních cílů (graf a karta čtou TUTÉŽ událost). */
      ...(mapaUveru.has(String(g.id)) ? { uverVCase: mapaUveru.get(String(g.id)) } : {}),
      // ručně přepsaný cíl: co engine dopočítal a jestli se to vejde do rozpočtu
      rezim: dopocet ? dopocet.rezim : undefined,
      dopocitano: dopocet ? dopocet.dopocitano : undefined,
      nedosazitelne: dopocet ? dopocet.nedosazitelne : undefined,
      kapacitaMes: dopocet || mekky ? Math.round(kapacitaMes) : undefined,
      nadKapacitu: dopocet || mekky ? pravidelne > kapacitaMes + 0.5 : undefined,
      // průběžný přehled: co po tomto cíli zbývá
      stavPo: {
        sporici: Math.round(pool.sporici), bezny: Math.round(pool.bezny),
        hotovost: Math.round(pool.hotovost), stavebko: Math.round(pool.stavebko),
        likvidniCelkem: Math.round(pool.sporici + pool.bezny + pool.hotovost),
        prebytekMes: Math.round(cap[0]),
      },
      /* Věta jen když se z přebytku OPRAVDU doplňuje (pravidelná > 0). U rezervy
       * „hned“ bez zvoleného dospoření i u rezervy bez volné kapacity by lhala;
       * co chybí a co s tím, říká karta (Č20). */
      pozn: rezerva && !rezervaOK && (pravidelne > 0 || !c20) ? "rezerva se doplňuje z přebytku — běžný účet zatím neinvestujeme" : undefined,
    });
  }

  /* Vnitřní průchod: jen úvěry (viz „SPLÁTKA ÚVĚRU OD ČERPÁNÍ“). */
  if (jenUvery) return { uvery: uveryNalezene };

  /* `celkemJednorazove` = co jde do plánu HNED. Penzijko ani budoucí přítok se
   * do něj nepočítají — dorazí až za roky, a věta „jednorázově vložíme X" by
   * jinak klientovi slíbila peníze, které dnes nikam nepošle.
   * Penzijko přesto NENÍ ztracené: je to finanční majetek, běžící rozpočet
   * (lib/rozpocetPlanu) ho zapojuje do poolu a odečítá u řádku renty. Aby si
   * obě čísla šlo srovnat, vrací se zvlášť jako `celkemPenzijko`. */
  const celkemJednorazove = vysledky.reduce((a, c) => a + c.jednorazove.filter(jeVkladDnes).reduce((s, j) => s + j.castka, 0), 0);
  const celkemPenzijko = vysledky.reduce((a, c) => a + c.jednorazove.filter(jePenzijko).reduce((s, j) => s + j.castka, 0), 0);
  /* Fondy a životko s hodnotou přiřazené cílům (metodika 2026-10-05) — mimo
   * `celkemJednorazove` (už pracují, nic se nevkládá), rozpočet je bere z
   * „nezapojeno“ do poolu (`rozpocetPlanu`, `fondyCerpanoZeSolveru`). */
  const celkemFondy = vysledky.reduce((a, c) => a + c.jednorazove.filter(jeFondy).reduce((s, j) => s + j.castka, 0), 0);
  /* Kolik z `celkemJednorazove` NEPOCHÁZÍ z dnešních zdrojů klienta, ale přibil to
   * poradce (nebo to enginu vyšlo jako potřebný vklad navíc). Pool likvidních zdrojů
   * se o tuhle částku NEsnižuje — je to peníze, které klient teprve musí najít.
   * Kdo tvrdí klientovi „rozložíme vaše současné úspory", musí ji odečíst,
   * jinak plán slíbí úspory, které klient nemá. `undefined` bez přepisu poradce
   * (JSON.stringify ji zahodí) → výstup zůstává bajt po bajtu zpětně kompatibilní. */
  const rucniJednorazove = vysledky.reduce((a, c) => a + c.jednorazove.filter((j) => j.rucni).reduce((s, j) => s + j.castka, 0), 0);
  const celkemPravidelne = vysledky.reduce((a, c) => a + c.pravidelne, 0);

  return {
    cile: vysledky,
    zdroje,
    rezervaOK,
    /* Cesty vstupních polí, která dorazila v jiném tvaru. Prázdné = data OK
     * (i prázdná data jsou OK). Neprázdné = obrazovka to MUSÍ přiznat. */
    dataProblemy: sber.problemy,
    zbyva: {
      sporici: Math.round(pool.sporici), bezny: Math.round(pool.bezny), hotovost: Math.round(pool.hotovost),
      stavebko: Math.round(pool.stavebko),
      likvidniCelkem: Math.round(pool.sporici + pool.bezny + pool.hotovost),
      prebytekMes: Math.round(cap[0]),
      /* Nerozdělené penzijko. Dává smysl jen tehdy, když se z poolu opravdu
       * čerpá — tedy při přepisu přidělení nebo při `zdrojeDoRenty`. Jinak by
       * číslo lhalo („zbývá 800 000", zatímco solver je celé poslal do renty),
       * a proto je `undefined` → JSON.stringify ho zahodí a výstup zůstává
       * bajt po bajtu zpětně kompatibilní. */
      penzijko: prideleni.has("penzijko") || doRenty ? Math.round(pool.penzijko) : undefined,
      /* Nevyčerpaný termínovaný vklad — jen když ho klient má (Č20). Plán
       * s metodikou před 4. 10. 2026 termíňák nečerpá, klíč nemá (jako dřív). */
      ...(c20 && zdroje.terminovane > 0 ? { termin: Math.round(pool.termin) } : {}),
      /* Fondy, které žádný cíl nedostal (klient bez renty, přepis poradce).
       * Klíč jen v metodice 2026-10-05 a jen když klient fondy má — starší
       * plán i klient bez fondů mají výstup beze změny. */
      ...(c1005 && cislo(zdroje.investice, 0) > 0 ? { fondy: Math.round(pool.fondy) } : {}),
    },
    celkemJednorazove: Math.round(celkemJednorazove),
    celkemJednorazoveRucni: rucniJednorazove > 0 ? Math.round(rucniJednorazove) : undefined,
    // penzijko přidělené do renty (undefined, když klient žádné nemá — výstup
    // pro klienta bez DPS zůstává bajt po bajtu stejný jako dřív)
    celkemPenzijko: celkemPenzijko > 0 ? Math.round(celkemPenzijko) : undefined,
    /* Fondy a životko s hodnotou přiřazené cílům (undefined, když žádné —
     * starší plán i klient bez fondů mají výstup bajt po bajtu stejný). */
    ...(celkemFondy > 0 ? { celkemFondy: Math.round(celkemFondy) } : {}),
    celkemPravidelne: Math.round(celkemPravidelne),
    /* Splátka úvěru vyšší, než dovoluje rozpočet (metodika 2026-10-05):
     * `{ odMes, chybiMes, maxChybiMes, maxOdMes, kroky: [{odMes, chybiMes}] }`.
     * Klíč jen když chybí — jinak výstup beze změny. */
    ...(schodekUveru ? { schodekUveru } : {}),
    /* Přebytek → pojištění → cíle. `undefined`, když volající cenu vůbec
     * neposlal (JSON.stringify klíč zahodí → výstup bajt po bajtu jako dřív). */
    pojistne: pojistneDano
      ? souhrnPojistneho(zdroje, pojistneMes, pojistneOdhad, pojistneDuvod, { novaMes: pojistneNovaMes, uvolnenoMes: pojistneUvolnenoMes, cast: pojistneCast, majetek: pojistneMajetek, metodika })
      : undefined,
  };
}

// odečti stupňovitý krok z kapacity (pomocná — mimo hot path)
function uberCap2(cap, krok, doMes) {
  for (let i = krok.odMes; i < Math.min(doMes, cap.length); i++) cap[i] = Math.max(0, cap[i] - krok.castkaMes);
}

/* Souhrn měsíců, kdy splátky úvěrů přesáhnou rozpočet (`schodekMes[m]` = kolik
 * chybí). `null` = nikdy. `kroky` = každá změna částky (0 = zase vychází). */
function souhrnSchodku(schodekMes, maxMes) {
  const kroky = [];
  let posledni = 0;
  for (let m = 0; m < maxMes; m++) {
    const v = Math.round(schodekMes[m] || 0);
    if (v !== posledni) { kroky.push({ odMes: m, chybiMes: v }); posledni = v; }
  }
  const prvni = kroky.find((k) => k.chybiMes > 0);
  if (!prvni) return null;
  const nejvic = kroky.reduce((a, k) => (k.chybiMes > a.chybiMes ? k : a), prvni);
  return { odMes: prvni.odMes, chybiMes: prvni.chybiMes, maxChybiMes: nejvic.chybiMes, maxOdMes: nejvic.odMes, kroky };
}
